import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Archive,
  Building2,
  Plus,
  ClipboardList,
  Users,
  Clock,
  CheckCircle,
  Wrench,
  Edit,
  Trash2,
  Loader2,
  AlertTriangle,
  Eye,
  Search,
  Filter,
  Calendar,
  History,
  Bell,
  FileText,
  FileSpreadsheet,
  Download,
  Info,
  PoundSterling,
  Send,
  Receipt,
  Heart,
  MapPin,
  ArrowRight,
  MailPlus,
  Copy,
  BarChart2,
  X,
  EyeOff,
  Sparkles,
  RefreshCw,
  MoreHorizontal,
  XCircle,
  ClipboardCheck,
  PenLine,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { scrollToElementWithOffset } from '../lib/scroll';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { ComplianceBadge } from '../components/ComplianceBadge';
import { ComplianceDisclaimer } from '../components/ComplianceDisclaimer';
import { UpgradeHintCard } from '../components/UpgradeHintCard';
import { FinancePanel } from '../components/FinancePanel';
import { PdfPreviewDialog } from '../components/PdfPreviewDialog';
import { StatementVersionsDialog } from '../components/StatementVersionsDialog';
import { MessageCenterContent } from './Messages/MessageCenterContent';
import { AddressInput } from '../components/AddressInput';
import { CountdownButton } from '../components/CountdownButton';
import { DEFAULT_ISSUE_CATEGORY, ISSUE_CATEGORIES, ISSUE_CATEGORY_OPTIONS } from '../lib/issueCategories';
import { CATEGORY_ICONS } from '../lib/issueCategoryIcons';
import { getIssueDescriptionTemplate } from '../lib/issueFormHelpers';
import { formatDateForDisplay } from '../lib/utils';
import { dismissUpgradePrompt, getUpgradePromptsForPlacement } from '../lib/upgradeHints';
import { fetchReceiptPreviewPdfBytes } from '../lib/fetchReceiptPreviewPdf';
import { validatePersonFullName } from '../lib/personNames';
import { LandlordDocumentDeliveryTab } from '../components/LandlordDocumentDeliveryTab';

// SA105 expense categories (individual landlords — Self Assessment)
const SA105_EXPENSE_CATEGORIES = [
  { value: 'mortgage_interest',          label: 'Mortgage Interest (Box 26)' },
  { value: 'management_fees',            label: 'Management Fees (Box 27)' },
  { value: 'insurance',                  label: 'Insurance (Box 28)' },
  { value: 'ground_rent_service_charge', label: 'Ground Rent / Service Charge (Box 28)' },
  { value: 'repairs_maintenance',        label: 'Repairs & Maintenance (Box 29)' },
  { value: 'legal_professional',         label: 'Legal & Professional (Box 30)' },
  { value: 'utilities',                  label: 'Utilities (Box 30)' },
  { value: 'travel',                     label: 'Travel (Box 30)' },
  { value: 'other_expenses',             label: 'Other Expenses (Box 30)' },
];

// CT600 expense categories (company landlords — Corporation Tax)
const CT600_EXPENSE_CATEGORIES = [
  { value: 'mortgage_interest',          label: 'Loan Interest' },
  { value: 'management_fees',            label: 'Management Fees' },
  { value: 'insurance',                  label: 'Insurance' },
  { value: 'ground_rent_service_charge', label: 'Ground Rent / Service Charge' },
  { value: 'repairs_maintenance',        label: 'Repairs & Maintenance' },
  { value: 'legal_professional',         label: 'Legal & Professional' },
  { value: 'accountancy_fees',           label: 'Accountancy Fees' },
  { value: 'directors_remuneration',     label: "Directors' Remuneration" },
  { value: 'company_pension',            label: 'Company Pension Contributions' },
  { value: 'capital_allowances',         label: 'Capital Allowances — AIA' },
  { value: 'utilities',                  label: 'Utilities' },
  { value: 'travel',                     label: 'Travel' },
  { value: 'other_expenses',             label: 'Other Expenses' },
];

const SA105_CATEGORY_LABEL_MAP = Object.fromEntries(SA105_EXPENSE_CATEGORIES.map(c => [c.value, c.label]));
const CT600_CATEGORY_LABEL_MAP = Object.fromEntries(CT600_EXPENSE_CATEGORIES.map(c => [c.value, c.label]));

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Option D — White Text Panel
// Photo fills the card background; all text content is wrapped in a
// white/95 rounded panel that floats over the image.
// Hides itself gracefully if the image 404s.

function StreetViewStrip({ propId, filename, photoHeight = 'h-40', children, fallbackBg = 'bg-gray-100' }) {
  const [hidden, setHidden] = useState(false);

  if (!propId || !filename || hidden) {
    return <div className={`${fallbackBg} p-4 rounded-xl`}>{children}</div>;
  }

  return (
    /* Outer wrapper must NOT have overflow-hidden so the front-card shadow is visible */
    <div className="relative w-full pb-2">

      {/* 1. Back card — image strip, styled as its own card */}
      <div className={`relative overflow-hidden rounded-2xl shadow-md border border-gray-200 ${photoHeight}`}>
        <img
          src={`${API_URL}/uploads/listing-images/${propId}/${filename}`}
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover scale-110"
          onError={() => setHidden(true)}
        />
      </div>

      {/* 2. Front card — overlaps the bottom of the image */}
      <div className="relative -mt-6 mx-3 z-10">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100">
          {children}
        </div>
      </div>

    </div>
  );
}


const MARK_PAID_CONFIRM_PREF_KEY = 'landlord_mark_paid_skip_confirmation';
const MARKET_RATE_AUTO_FETCH_KEY = 'landlord_market_rate_auto_fetch_ts';
const MARKET_RATE_AUTO_FETCH_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const _marketRateAutoFetchDue = (propertyId) => {
  try {
    const stored = window.localStorage.getItem(MARKET_RATE_AUTO_FETCH_KEY);
    const map = stored ? JSON.parse(stored) : {};
    const last = map[propertyId];
    return !last || Date.now() - last > MARKET_RATE_AUTO_FETCH_TTL_MS;
  } catch {
    return true;
  }
};

const _marketRateAutoFetchStamp = (propertyId) => {
  try {
    const stored = window.localStorage.getItem(MARKET_RATE_AUTO_FETCH_KEY);
    const map = stored ? JSON.parse(stored) : {};
    map[propertyId] = Date.now();
    window.localStorage.setItem(MARKET_RATE_AUTO_FETCH_KEY, JSON.stringify(map));
  } catch {
    // ignore storage errors
  }
};
const MANUAL_TENANT_PREFIX = 'manual:';

const createEmptyTenancyParticipant = () => ({ name: '', email: '' });

const createTenancyFormState = (overrides = {}) => ({
  property_id: '',
  tenants: [createEmptyTenancyParticipant()],
  start_date: '',
  deposit_taken: false,
  deposit_amount: '',
  notes: '',
  ...overrides,
});

const normalizeTenancyParticipantsForPayload = (tenants, label = 'Tenancy participant') => (
  tenants.map((tenant, index) => {
    const fullName = validatePersonFullName(tenant.name, `${label} ${index + 1} Full Name`);
    if (!fullName.valid) {
      throw new Error(fullName.error);
    }
    return {
      name: fullName.value,
      email: String(tenant.email || '').trim(),
    };
  })
);

const buildTenancyDetailsPayload = (form, label = 'Tenancy participant') => ({
  property_id: form.property_id,
  tenants: normalizeTenancyParticipantsForPayload(form.tenants, label),
  start_date: form.start_date || null,
  deposit_amount: form.deposit_taken && form.deposit_amount !== ''
    ? parseFloat(form.deposit_amount)
    : 0,
  notes: form.notes || null,
});

const getListingStatusMeta = (property) => {
  if (property?.is_occupied) {
    return {
      label: 'No listing',
      badgeClassName: 'bg-slate-100 text-slate-700 border-slate-200',
      actionLabel: property?.listing_started ? 'Edit draft listing' : 'Create listing',
      showAction: false,
      isPublished: false,
    };
  }
  if (property?.marketing_status === 'listed') {
    return {
      label: 'Published',
      badgeClassName: 'bg-green-100 text-green-700 border-green-200',
      actionLabel: 'Edit live listing',
      showAction: true,
      isPublished: true,
    };
  }
  if (property?.listing_started) {
    return {
      label: 'No listing',
      badgeClassName: 'bg-slate-100 text-slate-700 border-slate-200',
      actionLabel: 'Edit draft listing',
      showAction: true,
      isPublished: false,
    };
  }
  return {
    label: 'No listing',
    badgeClassName: 'bg-slate-100 text-slate-700 border-slate-200',
    actionLabel: 'Create listing',
    showAction: true,
    isPublished: false,
  };
};

const getOccupancyBadgeMeta = (property) => {
  if (property?.is_occupied) {
    return {
      label: 'occupied',
      className: 'bg-slate-100 text-slate-700 border-slate-200',
    };
  }

  if (property?.status === 'maintenance') {
    return {
      label: 'maintenance',
      className: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    };
  }

  return {
    label: property?.status || 'available',
    className: 'bg-green-100 text-green-700 border-green-200',
  };
};

const getCurrentTaxYear = () => {
  const now = new Date();
  return now.getMonth() + 1 > 4 || (now.getMonth() + 1 === 4 && now.getDate() >= 6)
    ? now.getFullYear()
    : now.getFullYear() - 1;
};

const getCurrentCompanyAccountingYear = (yearEnd = '03-31') => {
  const now = new Date();
  const [monthRaw, dayRaw] = String(yearEnd || '03-31').split('-');
  const endMonth = Math.max(1, Math.min(12, parseInt(monthRaw, 10) || 3));
  const endDay = Math.max(1, Math.min(31, parseInt(dayRaw, 10) || 31));
  const yearEndThisYear = new Date(now.getFullYear(), endMonth - 1, endDay, 23, 59, 59, 999);
  return now > yearEndThisYear ? now.getFullYear() : now.getFullYear() - 1;
};

const normalizeNonNegativeIntegerInput = (value) => {
  if (value === '') return '';
  const digitsOnly = String(value).replace(/[^0-9]/g, '');
  if (!digitsOnly) return '';
  return String(parseInt(digitsOnly, 10));
};

const formatReceiptDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatReceiptCurrency = (amount) => {
  const numericAmount = Number(amount);
  if (Number.isNaN(numericAmount)) return '£0.00';
  return `£${numericAmount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const resolveAssetUrl = (path) => {
  if (typeof path !== 'string') return '';
  const normalizedPath = path.trim();
  if (!normalizedPath) return '';
  if (['none', 'null', 'undefined'].includes(normalizedPath.toLowerCase())) return '';
  if (!/^https?:\/\//i.test(normalizedPath) && !/^\/?uploads\//i.test(normalizedPath)) return '';
  if (/^(https?:\/\/|blob:|data:)/i.test(normalizedPath)) return normalizedPath;
  if (normalizedPath.startsWith('/')) return `${API_URL}${normalizedPath}`;
  return `${API_URL}/${normalizedPath.replace(/^\/+/, '')}`;
};

const getRentScheduleChainKey = (rent) => `${rent.property_id}::${rent.tenant_id || rent.manual_tenancy_id || '__unassigned__'}`;

const getLatestRentSchedulePerChain = (rents = []) => {
  const latestByChain = new Map();
  rents.forEach((rent) => {
    const key = getRentScheduleChainKey(rent);
    const existing = latestByChain.get(key);
    if (!existing || (rent.next_due_date || '') > (existing.next_due_date || '')) {
      latestByChain.set(key, rent);
    }
  });
  return Array.from(latestByChain.values());
};

const unwrapApiList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const isEmptyStatePayload = (payload) => payload?.message === 'empty_state';

const VALID_TABS = new Set([
  'overview', 'properties', 'tenants', 'applications', 'messages',
  'issues', 'rent', 'compliance', 'finances', 'export', 'document-deliveries',
]);
const DEFAULT_TAB = 'overview';

export const LandlordDashboard = () => {
  useEffect(() => { document.title = 'Landlord Dashboard - SIYAK'; }, []);
  const { user, refreshUser } = useAuth();
  const isCompany = user?.ownership_type === 'company';
  const currentFinanceYear = isCompany
    ? getCurrentCompanyAccountingYear(user?.company_year_end)
    : getCurrentTaxYear();
  const navigate = useNavigate();
  const location = useLocation();
  const dashboardErrorToastAtRef = useRef(0);
  const isMountedRef = useRef(false);
  const currentRoleRef = useRef(user?.role ?? null);
  const fetchRequestSeqRef = useRef(0);
  const tabScrollRef = useRef(null);
  const hasScrolledTabsRef = useRef(false);
  const [properties, setProperties] = useState([]);
  const [issues, setIssues] = useState([]);
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState(null);
  const [accountMetrics, setAccountMetrics] = useState(user?.metrics || {});
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [messageUnreadCount, setMessageUnreadCount] = useState(0);
  const [savedProperties, setSavedProperties] = useState([]);
  const [transferRequests, setTransferRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = VALID_TABS.has(searchParams.get('tab')) ? searchParams.get('tab') : DEFAULT_TAB;

  const handleTabChange = useCallback((tab) => {
    if (!VALID_TABS.has(tab)) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    });
  }, [setSearchParams]);
  const [selectedMessageThreadId, setSelectedMessageThreadId] = useState(null);

  const [complianceData, setComplianceData] = useState({});
  const [complianceStats, setComplianceStats] = useState({
    total: 0,
    compliant: 0,
    warning: 0,
    nonCompliant: 0,
    bypassed: 0,
  });

  const [interestPopupOpen, setInterestPopupOpen] = useState(false);
  const [selectedPropertyInterest, setSelectedPropertyInterest] = useState(null);
  const [pendingInterestRemoval, setPendingInterestRemoval] = useState(null);
  const [removingInterest, setRemovingInterest] = useState(false);

  const [pendingApplicationRemoval, setPendingApplicationRemoval] = useState(null);
  const [withdrawingApplication, setWithdrawingApplication] = useState(false);

  const [replyOpen, setReplyOpen] = useState(false);
  const [replyNotification, setReplyNotification] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [replySending, setReplySending] = useState(false);

  const [screeningDialogOpen, setScreeningDialogOpen] = useState(false);
  const [screeningApplication, setScreeningApplication] = useState(null);
  const [screeningQuestions, setScreeningQuestions] = useState([]);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState(new Set());
  const [screeningQuestionsLoading, setScreeningQuestionsLoading] = useState(false);
  const [screeningSending, setScreeningSending] = useState(false);
  const [screeningAnswersDialogOpen, setScreeningAnswersDialogOpen] = useState(false);
  const [screeningAnswersApplication, setScreeningAnswersApplication] = useState(null);

  const [propertyDialogOpen, setPropertyDialogOpen] = useState(false);
  const [postPropertyCreateCueVisible, setPostPropertyCreateCueVisible] = useState(false);
  const [dismissedHintVersion, setDismissedHintVersion] = useState(0);
  const [editingProperty, setEditingProperty] = useState(null);
  const [propertyForm, setPropertyForm] = useState({
    title: '',
    address: '',
    postcode: '',
    description: '',
    property_type: 'flat',
    bedrooms: '1',
    bathrooms: '1',
    rent: '',
    features: [],
    has_gas_supply: false,
    floors: '',
    furnishing: 'none',
    furnished_appliances: false,
    water_system_type: 'none',
    is_occupied: false,
    licensing_status: 'yes',
  });

  const [issueSearch, setIssueSearch] = useState('');
  const [issueStatusFilter, setIssueStatusFilter] = useState('all');
  const [issuePriorityFilter, setIssuePriorityFilter] = useState('all');
  const [issuePropertyFilter, setIssuePropertyFilter] = useState('all');

  const [selectedIssue, setSelectedIssue] = useState(null);
  const [issueNotes, setIssueNotes] = useState('');
  const [updatingIssue, setUpdatingIssue] = useState(false);

  const [selectedPropertyTimeline, setSelectedPropertyTimeline] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loadingTimeline, setLoadingTimeline] = useState(false);

  const [rentSchedules, setRentSchedules] = useState([]);
  const [payingIds, setPayingIds] = useState([]);
  const [rentOverview, setRentOverview] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [receiptPreviewLoading, setReceiptPreviewLoading] = useState(false);
  const receiptPreviewRequestRef = useRef(0);
  const receiptPreviewAbortRef = useRef(null);
  const [marketRateByProperty, setMarketRateByProperty] = useState({});
  const [marketRateLoadingByProperty, setMarketRateLoadingByProperty] = useState({});
  const [dashboardEmptyState, setDashboardEmptyState] = useState(false);
  const fetchedMarketRateProps = useRef(new Set());
  const [rentDialogOpen, setRentDialogOpen] = useState(false);
  const [rentForm, setRentForm] = useState({
    property_id: '',
    tenancy_id: '',
    tenant_id: '',
    manual_tenancy_id: '',
    rent_amount: '',
    due_day_of_month: '1',
    payment_method: 'bank_transfer',
    notes: ''
  });
  const [editRentDialogOpen, setEditRentDialogOpen] = useState(false);
  const [editingRent, setEditingRent] = useState(null);
  const [editRentForm, setEditRentForm] = useState({
    rent_amount: '',
    due_day_of_month: '',
    paid_date_preset: '',
    payment_method: 'bank_transfer',
    notes: ''
  });
  const [editRentSubmitting, setEditRentSubmitting] = useState(false);
  // Per-rent payment date presets set from the edit dialog — used as the default date on Mark Paid
  const [rentPaymentDatePresets, setRentPaymentDatePresets] = useState({});
  const [markPaidDialogOpen, setMarkPaidDialogOpen] = useState(false);
  const [selectedRentForPayment, setSelectedRentForPayment] = useState(null);
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  // Only used to record additional installments on an already partially-paid schedule
  // via an explicit amount typed at Mark Paid time (in addition to the Edit dialog's
  // planned_payment_amount preset, which paymentAmount takes priority over when set).
  const [paymentAmount, setPaymentAmount] = useState('');
  const [markPaidTimer, setMarkPaidTimer] = useState(0);
  const [markPaidTimerActive, setMarkPaidTimerActive] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [tenantAssignments, setTenantAssignments] = useState([]);
  const [manualTenancies, setManualTenancies] = useState([]);
  const [tenancyStatusFilter, setTenancyStatusFilter] = useState('active');
  const [propertyStatusFilter, setPropertyStatusFilter] = useState('active');
  const [tenantDirectory, setTenantDirectory] = useState([]);
  const [manualTenancyForm, setManualTenancyForm] = useState(() => createTenancyFormState());
  const [addTenancyDialogOpen, setAddTenancyDialogOpen] = useState(false);
  const [tenantManagementSubmitting, setTenantManagementSubmitting] = useState(false);
  const [editTenancyDialogOpen, setEditTenancyDialogOpen] = useState(false);
  const [editTenancyType, setEditTenancyType] = useState(null);
  const [editingTenancyRecord, setEditingTenancyRecord] = useState(null);
  const [editTenancySubmitting, setEditTenancySubmitting] = useState(false);
  const [editTenancyForm, setEditTenancyForm] = useState({
    property_id: '',
    tenant_id: '',
    tenants: [createEmptyTenancyParticipant()],
    start_date: '',
    deposit_taken: false,
    deposit_amount: '',
    notes: '',
  });
  const [sendingReminders, setSendingReminders] = useState(false);
  const [sendingReminderIds, setSendingReminderIds] = useState([]);
  const [reminderRulesOpen, setReminderRulesOpen] = useState(false);

  const getTenancyTenantNames = (tenancy) =>
    Array.isArray(tenancy?.tenants)
      ? tenancy.tenants.map((tenant) => tenant?.name).filter(Boolean)
      : [];

  const getTenancyTenantEmails = (tenancy) =>
    Array.isArray(tenancy?.tenants)
      ? tenancy.tenants.map((tenant) => tenant?.email).filter(Boolean)
      : [];

  const getTenancyLabel = (tenancy) => {
    const names = getTenancyTenantNames(tenancy);
    if (names.length) {
      return names.join(', ');
    }
    const emails = getTenancyTenantEmails(tenancy);
    return emails.join(', ');
  };

  const [submitting, setSubmitting] = useState(false);

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteForm, setInviteForm] = useState(() => createTenancyFormState());
  const [createdInvite, setCreatedInvite] = useState(null);
  const [tenancyMode, setTenancyMode] = useState('invite'); // 'invite' | 'manual'
  const [pendingConfirmAction, setPendingConfirmAction] = useState(null);
  const [confirmActionLoading, setConfirmActionLoading] = useState(false);
  const [markPaidConfirmOpen, setMarkPaidConfirmOpen] = useState(false);
  const [pendingMarkPaidRentId, setPendingMarkPaidRentId] = useState(null);
  const [markPaidDontShowAgainChecked, setMarkPaidDontShowAgainChecked] = useState(false);
  const [markPaidCountdownArmedRentId, setMarkPaidCountdownArmedRentId] = useState(null);
  const [skipMarkPaidConfirmation, setSkipMarkPaidConfirmation] = useState(() => {
    if (typeof window === 'undefined') return false;
    // localStorage = permanent "Don't show again"; sessionStorage = already seen this session
    return (
      window.localStorage.getItem(MARK_PAID_CONFIRM_PREF_KEY) === 'true' ||
      window.sessionStorage.getItem(MARK_PAID_CONFIRM_PREF_KEY) === 'true'
    );
  });
  // Save-to-finance: track which paid rent IDs have already been logged
  const [financeLoggedRentIds, setFinanceLoggedRentIds] = useState(new Set());
  const [loggingFinanceRentId, setLoggingFinanceRentId] = useState(null);

  const [issueCost, setIssueCost] = useState('');
  const [issueCostCategory, setIssueCostCategory] = useState('');
  const [savingIssueCost, setSavingIssueCost] = useState(false);

  const [landlordIssueDialogOpen, setLandlordIssueDialogOpen] = useState(false);
  const [landlordIssueSubmitting, setLandlordIssueSubmitting] = useState(false);
  const [landlordIssueTenants, setLandlordIssueTenants] = useState([]);
  const [landlordIssueForm, setLandlordIssueForm] = useState({
    property_id: '',
    tenant_id: '',
    description: '',
    category: DEFAULT_ISSUE_CATEGORY,
    sub_category: '',
    custom_title: '',
    priority: 'medium'
  });

  const landlordIssueSubCategoryOptions = ISSUE_CATEGORIES[landlordIssueForm.category] || [];
  const SelectedLandlordCategoryIcon = CATEGORY_ICONS[landlordIssueForm.category];

  const updateLandlordIssueDetails = (updates) => {
    setLandlordIssueForm((current) => {
      const currentTemplate = getIssueDescriptionTemplate(current);
      const next = { ...current, ...updates };
      const nextTemplate = getIssueDescriptionTemplate(next);

      if (!current.description.trim() || current.description === currentTemplate) {
        return {
          ...next,
          description: nextTemplate,
        };
      }

      return next;
    });
  };

  const isExpectedAuthError = (error) => {
    const status = error?.response?.status;
    return status === 401 || status === 403;
  };

  const shouldShowDashboardErrorToast = () => {
    const now = Date.now();
    if (now - dashboardErrorToastAtRef.current < 10000) {
      return false;
    }
    dashboardErrorToastAtRef.current = now;
    return true;
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    currentRoleRef.current = user?.role ?? null;
  }, [user?.role]);

  useEffect(() => {
    setAccountMetrics(user?.metrics || {});
  }, [user?.metrics]);

  useEffect(() => {
    const hashTab = location.hash?.replace('#', '');
    if (!hashTab) return;
    if (VALID_TABS.has(hashTab)) {
      handleTabChange(hashTab);
    }
  }, [location.hash, handleTabChange]);

  useEffect(() => {
    const threadId = location.state?.notificationThreadId;
    if (!threadId) return;
    setSelectedMessageThreadId(threadId);
    handleTabChange('messages');
  }, [location.state, handleTabChange]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const issueId = params.get('issueId');
    if (!issueId) return;
    handleTabChange('issues');
    const issue = issues.find((item) => item.id === issueId);
    if (issue) {
      setSelectedIssue(issue);
      setIssueNotes(issue.landlord_notes || issue.admin_notes || '');
    }
  }, [issues, location.search, handleTabChange]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    if (params.get('applicationId')) {
      handleTabChange('applications');
    }
  }, [location.search, handleTabChange]);

  useEffect(() => {
    if (!hasScrolledTabsRef.current) {
      hasScrolledTabsRef.current = true;
      return undefined;
    }

    if (!tabScrollRef.current) {
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      scrollToElementWithOffset(tabScrollRef.current);
    }, 100);

    return () => window.clearTimeout(timerId);
  }, [activeTab]);

  const getPropertyComplianceStatus = (propertyId) => {
    const propCompliance = (complianceData.properties || []).find(
      p => p.property_id === propertyId
    );
    if (!propCompliance) return null;

    const expiredCount = (propCompliance.expired_documents?.length || propCompliance.expired_compliance_documents?.length || 0);
    const missingCount = (propCompliance.missing_documents?.length || propCompliance.missing_required_documents?.length || 0);
    const motStatus = (propCompliance.mot_status || '').toLowerCase();

    if (motStatus === 'pass') {
      return { status: 'compliant', label: 'MOT Pass', color: 'text-green-600' };
    }
    if (motStatus === 'bypassed') {
      return { status: 'disabled', label: 'MOT Disabled', color: 'text-slate-600' };
    }
    if (motStatus === 'advisory') {
      return { status: 'warning', label: 'Advisory', color: 'text-amber-600' };
    }
    if (motStatus === 'fail') {
      return { status: 'non-compliant', label: 'MOT Fail', color: 'text-red-600' };
    }
    if (motStatus === 'due') {
      return { status: 'non-compliant', label: 'MOT Due', color: 'text-red-600' };
    }

    if (propCompliance.regulatory_compliant) {
      return { status: 'compliant', label: 'MOT Pass', color: 'text-green-600' };
    } else if (expiredCount > 0 && missingCount === 0) {
      return { status: 'non-compliant', label: 'MOT Due', color: 'text-red-600' };
    } else if (missingCount > 0) {
      return { status: 'non-compliant', label: 'MOT Due', color: 'text-red-600' };
    }
    return null;
  };

  const getMissingBreakdownText = (propertyCompliance, fallbackMissingCount = 0) => {
    const failedRules = Array.isArray(propertyCompliance?.failed_rules)
      ? propertyCompliance.failed_rules
      : [];
    const docCount = failedRules.filter((rule) => rule?.requires_document_upload).length;
    const checklistCount = failedRules.filter((rule) => rule?.requires_checkbox_declaration).length;

    if (docCount === 0 && checklistCount === 0) {
      return `${fallbackMissingCount} missing`;
    }

    const totalActions = docCount + checklistCount;
    return `${totalActions} missing (${docCount} docs, ${checklistCount} checklist)`;
  };

  const handleMotTenancyStartDate = async (tenancyId, value) => {
    try {
      await axios.patch(
        `${API_URL}/api/tenancies/${tenancyId}/start-date`,
        { start_date: value },
        { withCredentials: true }
      );
      await fetchData();
    } catch {
      toast.error('Could not save start date.');
    }
  };

  const fetchData = useCallback(async () => {
    if (!isMountedRef.current) {
      return;
    }
    if (currentRoleRef.current !== 'landlord') {
      setLoading(false);
      return;
    }

    const requestSeq = ++fetchRequestSeqRef.current;

    try {
      const requests = [
        axios.get(`${API_URL}/api/properties?lifecycle_status=all`),
        axios.get(`${API_URL}/api/issues`),
        axios.get(`${API_URL}/api/applications`),
        axios.get(`${API_URL}/api/landlord/stats`),
        axios.get(`${API_URL}/api/notifications`),
        axios.get(`${API_URL}/api/notifications/count`),
        axios.get(`${API_URL}/api/rent`),
        axios.get(`${API_URL}/api/rent/overview`),
        axios.get(`${API_URL}/api/saved-properties/landlord`),
        axios.get(`${API_URL}/api/transfer-request/landlord-tenants`),
        axios.get(`${API_URL}/api/documents/compliance/dashboard`, { withCredentials: true }),
        axios.get(`${API_URL}/api/messages/threads`, { withCredentials: true }),
        axios.get(`${API_URL}/api/tenant-assignments`, { withCredentials: true }),
        axios.get(`${API_URL}/api/tenancies/manual`, { params: { status: 'all' }, withCredentials: true }),
        axios.get(`${API_URL}/api/tenancies/tenant-directory`, { withCredentials: true }),
        axios.get(`${API_URL}/api/written-statements`, { withCredentials: true }),
      ];

      const results = await Promise.allSettled(requests);
      if (!isMountedRef.current || requestSeq !== fetchRequestSeqRef.current || currentRoleRef.current !== 'landlord') {
        return;
      }
      const [propsRes, issuesRes, applicationsRes, statsRes, notifsRes, unreadRes, rentRes, rentOverviewRes, savedRes, transferRes, complianceRes, messageThreadsRes, tenantAssignmentsRes, manualTenanciesRes, tenantDirectoryRes, writtenStatementsRes] = results;

      if (propsRes.status === 'fulfilled') setProperties(unwrapApiList(propsRes.value.data));
      if (issuesRes.status === 'fulfilled') {
        setIssues(unwrapApiList(issuesRes.value.data));
        setDashboardEmptyState(isEmptyStatePayload(issuesRes.value.data));
      }
      if (applicationsRes.status === 'fulfilled') setApplications(applicationsRes.value.data || []);
      if (statsRes.status === 'fulfilled') {
        setStats(statsRes.value.data);
        setAccountMetrics(statsRes.value.data?.metrics || user?.metrics || {});
      }
      if (notifsRes.status === 'fulfilled') setNotifications(notifsRes.value.data);
      if (unreadRes.status === 'fulfilled') setUnreadCount(unreadRes.value.data.unread_count);
      if (rentRes.status === 'fulfilled') setRentSchedules(unwrapApiList(rentRes.value.data));
      if (rentOverviewRes.status === 'fulfilled') setRentOverview(rentOverviewRes.value.data);
      if (savedRes.status === 'fulfilled') setSavedProperties(savedRes.value.data || []);
      if (transferRes.status === 'fulfilled') setTransferRequests(transferRes.value.data || []);
      if (tenantAssignmentsRes.status === 'fulfilled') setTenantAssignments(tenantAssignmentsRes.value.data || []);
      if (manualTenanciesRes.status === 'fulfilled') setManualTenancies(manualTenanciesRes.value.data || []);
      if (tenantDirectoryRes.status === 'fulfilled') setTenantDirectory(tenantDirectoryRes.value.data || []);
      if (writtenStatementsRes.status === 'fulfilled') setWrittenStatements(Array.isArray(writtenStatementsRes.value.data) ? writtenStatementsRes.value.data : []);
      if (messageThreadsRes.status === 'fulfilled') {
        const unreadTotal = (messageThreadsRes.value.data || []).reduce(
          (sum, thread) => sum + (thread?.unread_count || 0),
          0,
        );
        setMessageUnreadCount(unreadTotal);
      }

      if (complianceRes.status === 'fulfilled') {
        const compliancePayload = complianceRes.value.data;
        setComplianceData(compliancePayload);
        const complianceProperties = compliancePayload.properties || [];
        const compliant = complianceProperties.filter(
          p => (p.mot_status || '').toLowerCase() === 'pass'
        ).length;
        const warning = complianceProperties.filter(
          p => (p.mot_status || '').toLowerCase() === 'advisory'
        ).length;
        const bypassed = complianceProperties.filter(
          p => (p.mot_status || '').toLowerCase() === 'bypassed'
        ).length;
        const nonCompliant = complianceProperties.filter(
          p => {
            const status = (p.mot_status || '').toLowerCase();
            return status === 'fail' || status === 'due';
          }
        ).length;
        setComplianceStats({
          total: complianceProperties.length,
          compliant,
          warning,
          nonCompliant,
          bypassed,
        });
      }

      const rejected = results.filter((result) => result.status === 'rejected').map((result) => result.reason);
      const unexpectedFailures = rejected.filter((error) => !isExpectedAuthError(error));
      if (unexpectedFailures.length > 0 && shouldShowDashboardErrorToast()) {
        toast.error('Some dashboard data failed to load');
      }
    } catch (error) {
      if (!isMountedRef.current || requestSeq !== fetchRequestSeqRef.current || currentRoleRef.current !== 'landlord') {
        return;
      }
      console.error('Failed to fetch data:', error);
      if (!isExpectedAuthError(error) && shouldShowDashboardErrorToast()) {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      if (isMountedRef.current && requestSeq === fetchRequestSeqRef.current) {
        setLoading(false);
      }
    }
  }, [user?.metrics]);

  useEffect(() => {
    if (user?.role !== 'landlord') {
      setLoading(false);
      return;
    }
    fetchData();
  }, [user?.role, fetchData]);

  useEffect(() => {
    if (activeTab !== 'tenants') return undefined;
    const refreshTenancyStatements = () => {
      if (document.visibilityState === 'hidden') return;
      fetchData();
    };
    window.addEventListener('focus', refreshTenancyStatements);
    document.addEventListener('visibilitychange', refreshTenancyStatements);
    return () => {
      window.removeEventListener('focus', refreshTenancyStatements);
      document.removeEventListener('visibilitychange', refreshTenancyStatements);
    };
  }, [activeTab, fetchData]);

  const handleSaveIssueCost = async () => {
    if (!selectedIssue) return;
    setSavingIssueCost(true);
    try {
      const payload = {};
      if (issueCost !== '') {
        const val = parseFloat(issueCost);
        if (isNaN(val) || val < 0) { toast.error('Invalid cost amount'); return; }
        payload.cost = val;
      }
      if (issueCostCategory) payload.cost_category = issueCostCategory;
      const resp = await axios.put(`${API_URL}/api/issues/${selectedIssue.id}`, payload);
      setSelectedIssue(prev => ({ ...prev, cost: resp.data.cost, cost_category: resp.data.cost_category }));
      toast.success('Cost saved');
      await fetchData();
    } catch (error) {
      toast.error('Failed to save cost');
    } finally {
      setSavingIssueCost(false);
    }
  };

  const handleLandlordCreateIssue = async (e) => {
    e.preventDefault();
    setLandlordIssueSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/issues`, {
        ...landlordIssueForm,
        sub_category: landlordIssueForm.sub_category || null,
        custom_title: landlordIssueForm.category === 'Other' ? (landlordIssueForm.custom_title.trim() || null) : null,
        tenant_id: isManualTenantValue(landlordIssueForm.tenant_id) ? null : landlordIssueForm.tenant_id || null,
      });
      toast.success('Issue reported successfully');
      setLandlordIssueDialogOpen(false);
      setLandlordIssueForm({
        property_id: '',
        tenant_id: '',
        description: '',
        category: DEFAULT_ISSUE_CATEGORY,
        sub_category: '',
        custom_title: '',
        priority: 'medium'
      });
      setLandlordIssueTenants([]);
      handleSearchIssues();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to report issue');
    } finally {
      setLandlordIssueSubmitting(false);
    }
  };

  const fetchTimeline = async (propertyId) => {
    setLoadingTimeline(true);
    try {
      const response = await axios.get(`${API_URL}/api/properties/${propertyId}/timeline`);
      setTimeline(response.data);
    } catch (error) {
      console.error('Failed to fetch timeline:', error);
      toast.error('Failed to load timeline');
    } finally {
      setLoadingTimeline(false);
    }
  };

  const handleSearchIssues = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (issueSearch) params.append('search', issueSearch);
      if (issueStatusFilter !== 'all') params.append('status', issueStatusFilter);
      if (issuePriorityFilter !== 'all') params.append('priority', issuePriorityFilter);
      if (issuePropertyFilter !== 'all') params.append('property_id', issuePropertyFilter);

      const response = await axios.get(`${API_URL}/api/issues?${params.toString()}`);
      setIssues(unwrapApiList(response.data));
      setDashboardEmptyState(isEmptyStatePayload(response.data));
    } catch (error) {
      const status = error?.response?.status;
      const isInitialEmptySearch = !issueSearch
        && issueStatusFilter === 'all'
        && issuePriorityFilter === 'all'
        && issuePropertyFilter === 'all'
        && issues.length === 0
        && properties.length === 0;
      if (status === 401 || (status === 403 && isInitialEmptySearch)) {
        setIssues([]);
        setDashboardEmptyState(true);
        return;
      }
      toast.error('Failed to search issues');
    }
  }, [issueSearch, issueStatusFilter, issuePriorityFilter, issuePropertyFilter, issues.length, properties.length]);

  useEffect(() => {
    if (!loading) {
      handleSearchIssues();
    }
  }, [loading, handleSearchIssues]);

  const resetPropertyForm = () => {
    setPropertyForm({
      title: '',
      address: '',
      postcode: '',
      description: '',
      property_type: 'flat',
      bedrooms: '1',
      bathrooms: '1',
      rent: '',
      features: [],
      has_gas_supply: false,
      floors: '',
      furnishing: 'none',
      furnished_appliances: false,
      water_system_type: 'none',
      is_occupied: false,
      licensing_status: 'yes',
    });
    setEditingProperty(null);
  };

  const handleOpenPropertyDialog = (property = null) => {
    if (property) {
      setEditingProperty(property);
      setPropertyForm({
        title: property.title,
        address: property.address,
        postcode: property.postcode,
        description: property.description || '',
        property_type: property.property_type,
        bedrooms: property.bedrooms.toString(),
        bathrooms: property.bathrooms.toString(),
        rent: property.rent?.toString() || '',
        features: property.features || [],
        has_gas_supply: property.has_gas_supply || false,
        floors: property.floors?.toString() || '',
        furnishing: property.furnishing || 'none',
        furnished_appliances: property.furnished_appliances || false,
        water_system_type: property.water_system_type || 'none',
        is_occupied: Boolean(property.is_occupied),
        licensing_status: property.licensing_status || '',
      });
    } else {
      resetPropertyForm();
    }
    setPropertyDialogOpen(true);
  };

  const handleSubmitProperty = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const isCreating = !editingProperty;

    const data = {
      ...propertyForm,
      bedrooms: parseInt(propertyForm.bedrooms),
      bathrooms: parseInt(propertyForm.bathrooms),
      rent: propertyForm.rent ? parseFloat(propertyForm.rent) : 0,
      features: propertyForm.features || [],
      // Compliance fields
      floors: propertyForm.floors === '' ? 0 : Math.max(0, parseInt(propertyForm.floors, 10) || 0),
      has_gas_supply: Boolean(propertyForm.has_gas_supply),
      furnished_appliances: Boolean(propertyForm.furnished_appliances),
      furnishing: propertyForm.furnishing === 'none' ? null : propertyForm.furnishing,
      water_system_type: propertyForm.water_system_type === 'none' ? null : propertyForm.water_system_type,
      is_occupied: Boolean(propertyForm.is_occupied),
      licensing_status: propertyForm.licensing_status || null,
    };

    try {
      if (editingProperty) {
        await axios.put(`${API_URL}/api/properties/${editingProperty.id}`, data, { withCredentials: true });
        toast.success('Property updated successfully');
      } else {
        await axios.post(`${API_URL}/api/properties`, data, { withCredentials: true });
        toast.success('Property created successfully');
      }
      setPropertyDialogOpen(false);
      resetPropertyForm();
      const refreshedUser = await refreshUser?.(true);
      await fetchData();
      if (isCreating && (refreshedUser?.metrics?.properties_count || 0) >= 2) {
        setPostPropertyCreateCueVisible(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save property');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteProperty = async (propertyId) => {
    try {
      await axios.delete(`${API_URL}/api/properties/${propertyId}`);
      toast.success('Property deleted');
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'object' && detail?.error === 'property_has_records') {
        const reasons = detail.blocking_reasons || [];
        const reasonText = formatBlockingReasons(reasons);
        toast.error(`Cannot delete: property has ${reasonText}. Archive it instead to preserve records.`);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to delete property');
      }
    }
  };

  const handleArchiveProperty = async (propertyId) => {
    try {
      await axios.post(`${API_URL}/api/properties/${propertyId}/archive`, {}, { withCredentials: true });
      toast.success('Property archived. It is now read-only and preserved for audit.');
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'object' && detail?.error === 'active_tenancy_exists') {
        toast.error(detail.message || 'End all active tenancies before archiving this property.');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to archive property');
      }
    }
  };

  const propertyHasRecords = (propertyId) => {
    const hasPortal = tenantAssignments.some(a => a.property_id === propertyId);
    const hasManual = manualTenancies.some(mt => mt.property_id === propertyId);
    const hasStatements = writtenStatements.some(ws => ws.property_id === propertyId);
    const hasRent = rentSchedules.some(r => r.property_id === propertyId);
    return hasPortal || hasManual || hasStatements || hasRent;
  };

  const handleOpenInviteDialog = (property = null, application = null) => {
    setCreatedInvite(null);
    setInviteForm(createTenancyFormState({
      property_id: property?.id || '',
      tenants: [{
        name: application?.tenant_name || '',
        email: application?.tenant_email || '',
      }],
      notes: application?.id ? `Application approved: ${application.id}` : '',
    }));
    setInviteDialogOpen(true);
  };

  const copyToClipboard = async (value, successMessage) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(successMessage);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const getPublishedPropertyUrl = (propertyId) => `${window.location.origin}/properties/${propertyId}`;

  const handleCopyPublishedPropertyLink = async (propertyId) => {
    await copyToClipboard(getPublishedPropertyUrl(propertyId), 'Published listing link copied');
  };

  const handleOpenPublishedPropertyLink = (propertyId) => {
    window.open(getPublishedPropertyUrl(propertyId), '_blank', 'noopener,noreferrer');
  };

  const sendInviteEmail = async () => {
    const inviteRows = createdInvite?.invites?.length ? createdInvite.invites : (createdInvite?.id ? [createdInvite] : []);
    if (inviteRows.length === 0) {
      toast.error('Invite details are missing');
      return;
    }

    try {
      await Promise.all(inviteRows.map((invite) => (
        axios.post(`${API_URL}/api/landlord/invites/${invite.id}/send-email`)
      )));
      setCreatedInvite((prev) => prev ? {
        ...prev,
        email_status: 'sent',
        email_sent_at: new Date().toISOString(),
        invites: (prev.invites || []).map((invite) => ({
          ...invite,
          email_status: 'sent',
          email_sent_at: new Date().toISOString(),
        })),
      } : prev);
      toast.success(inviteRows.length === 1 ? 'Tenancy invite email sent' : 'Tenancy invite emails sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite email');
    }
  };

  const handleCreateInvite = async (e) => {
    e.preventDefault();
    setInviteSubmitting(true);

    try {
      const response = await axios.post(`${API_URL}/api/landlord/invites`, buildTenancyDetailsPayload(inviteForm));

      const invite = response.data;
      const inviteRows = invite.invites || [invite];
      const selectedProperty = properties.find((p) => p.id === (invite.property_id || inviteRows[0]?.property_id));
      const withInviteUrls = inviteRows.map((row) => {
        const targetPath = row.invite_type === 'existing' ? '/login' : '/register';
        return {
          ...row,
          invite_url: `${window.location.origin}${targetPath}?invite_token=${encodeURIComponent(row.token)}&invite_email=${encodeURIComponent(row.email)}`,
          email_status: 'not_sent',
        };
      });

      setCreatedInvite({
        ...invite,
        ...(withInviteUrls[0] || {}),
        invites: withInviteUrls,
        property_title: selectedProperty?.title || 'Selected property',
      });

      toast.success(withInviteUrls.some((row) => row.reused) ? 'Tenancy invite links loaded. Email not sent yet.' : 'Tenancy invite created. Email not sent yet.');
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to create invite');
    } finally {
      setInviteSubmitting(false);
    }
  };

  const handleUpdateIssue = async (issueId, status, notes = null) => {
    setUpdatingIssue(true);
    try {
      const data = { status };
      if (notes !== null) data.admin_notes = notes;
      
      await axios.put(`${API_URL}/api/issues/${issueId}`, data);
      toast.success('Issue updated');
      setSelectedIssue(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update issue');
    } finally {
      setUpdatingIssue(false);
    }
  };

  const fetchMarketRateForProperty = useCallback(async (propertyId, options = {}) => {
    const {
      force = false,
      showErrorToast = false,
      recordTimeline = false,
      recordHistory = false,
      recordInitial = false,
    } = options;
    if (!propertyId) return;
    // Non-forced (auto) fetches: skip within-session dedupe only.
    // The localStorage TTL is intentionally NOT used as an early-return gate here because
    // non-forced calls (no force=true param) simply read cached DB data — they do not
    // trigger a fresh scrape. Skipping the fetch entirely would leave marketRateByProperty
    // unpopulated whenever the TTL is still active, hiding perfectly valid cached results.
    if (!force) {
      if (fetchedMarketRateProps.current.has(propertyId)) return;
      // Still check the TTL — but only to decide whether to stamp after success (below).
    }
    fetchedMarketRateProps.current.add(propertyId);
    setMarketRateLoadingByProperty((prev) => ({ ...prev, [propertyId]: true }));
    try {
      const params = { property_id: propertyId };
      if (force) params.force = true;
      if (recordTimeline) params.record_timeline = true;
      if (recordHistory) params.record_history = true;
      if (recordInitial) params.record_initial = true;
      const res = await axios.get(`${API_URL}/api/landlord/market-rate-summary`, {
        params,
        withCredentials: true,
      });
      setMarketRateByProperty((prev) => ({ ...prev, [propertyId]: res.data }));
      // Stamp the auto-fetch time only when a fresh scrape was due (avoids redundant stamps
      // on plain cache-read calls, though stamping on every successful load is harmless).
      _marketRateAutoFetchStamp(propertyId);
      return res.data;
    } catch (err) {
      // Always re-throw rate-limit errors so callers can show a specific message
      if (err?.response?.status === 429) throw err;
      setMarketRateByProperty((prev) => {
        // Preserve a known result (e.g. "no data found" sentinel) so the UI
        // doesn't go blank on a transient network error.  Only write a
        // no-data sentinel if we have no prior value at all.
        const existing = prev[propertyId];
        const fallback = existing !== undefined && existing !== null
          ? existing
          : { min_rent: null, median_rent: null, max_rent: null, listings_count: 0, bedrooms: null, last_scraped_at: null };
        return { ...prev, [propertyId]: fallback };
      });
      if (showErrorToast) {
        toast.error('Failed to fetch market data');
      }
      return null;
    } finally {
      setMarketRateLoadingByProperty((prev) => ({ ...prev, [propertyId]: false }));
    }
  }, []);

  // Market rate: fetch area median per unique property whenever rentSchedules updates.
  // recordInitial=true so the very first load is registered on the property timeline.
  useEffect(() => {
    const uniqueIds = [...new Set(rentSchedules.map((r) => r.property_id).filter(Boolean))];
    uniqueIds.forEach((propertyId) => {
      fetchMarketRateForProperty(propertyId, { recordInitial: true });
    });
  }, [rentSchedules, fetchMarketRateForProperty]);

  const handleScrapeMarketData = async (propertyId) => {
    try {
      const data = await fetchMarketRateForProperty(propertyId, {
        force: true,
        showErrorToast: false,   // we handle errors here
        recordTimeline: true,
        recordHistory: true,
      });
      const selectedTimelinePropertyId = typeof selectedPropertyTimeline === 'string'
        ? selectedPropertyTimeline
        : selectedPropertyTimeline?.id;
      if (selectedTimelinePropertyId === propertyId) {
        await fetchTimeline(propertyId);
      }
      if (data?.median_rent != null) {
        toast.success('Market data updated');
        return;
      }
      const property = properties.find((item) => item.id === propertyId);
      toast.info(`No market data found for this location and property type yet (${data?.bedrooms ?? property?.bedrooms ?? 'unknown'} bedrooms).`);
    } catch (err) {
      // 429 = daily rate limit hit
      if (err?.response?.status === 429) {
        const msg = err.response?.data?.detail || 'Market rate already refreshed today. Try again tomorrow.';
        toast.warning(msg);
        return;
      }
      toast.error('Failed to fetch market data');
    }
  };

  const handleToggleMarketRateShared = async (propertyId, currentValue) => {
    try {
      const res = await axios.patch(`${API_URL}/api/landlord/properties/${propertyId}/market-rate-shared`, {}, { withCredentials: true });
      const nextValue = Boolean(res.data?.market_rate_shared ?? !currentValue);
      const sharedTenantCount = res.data?.shared_tenant_count ?? 0;

      setRentSchedules((prev) =>
        prev.map((rent) => rent.property_id === propertyId ? { ...rent, tenant_market_rate_shared: nextValue } : rent)
      );
      setMarketRateByProperty((prev) => ({
        ...prev,
        [propertyId]: prev[propertyId]
          ? {
              ...prev[propertyId],
              market_rate_shared: nextValue,
              shared_with_all_tenants: nextValue,
              shared_tenant_count: sharedTenantCount,
            }
          : prev[propertyId],
      }));

      toast.success(`Market rate ${nextValue ? 'shared with all tenants' : 'hidden from all tenants'}`);
    } catch {
      toast.error('Failed to update market rate visibility');
    }
  };

  const isManualTenantValue = (value) => typeof value === 'string' && value.startsWith(MANUAL_TENANT_PREFIX);

  const getManualTenancyIdFromValue = (value) => (isManualTenantValue(value) ? value.slice(MANUAL_TENANT_PREFIX.length) : '');

  const getTenantOptionValue = (tenant) => (tenant?.is_manual ? `${MANUAL_TENANT_PREFIX}${tenant.manual_tenancy_id || tenant.id}` : tenant.id);

  // Rent management functions
  const fetchTenants = async (propertyId) => {
    try {
      const res = await axios.get(`${API_URL}/api/properties/${propertyId}/tenants`);
      setTenants(res.data);
    } catch (error) {
      setTenants([]);
    }
  };

  const handleCreateRent = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/rent`, {
        property_id: rentForm.property_id,
        tenancy_id: rentForm.tenancy_id,
        tenant_id: rentForm.tenant_id || null,
        manual_tenancy_id: rentForm.manual_tenancy_id || null,
        rent_amount: parseFloat(rentForm.rent_amount),
        due_day_of_month: parseInt(rentForm.due_day_of_month),
        payment_method: rentForm.payment_method || 'bank_transfer',
        notes: rentForm.notes || null
      });
      toast.success('Rent schedule created');
      setRentDialogOpen(false);
      setRentForm({ property_id: '', tenancy_id: '', tenant_id: '', manual_tenancy_id: '', rent_amount: '', due_day_of_month: '1', payment_method: 'bank_transfer', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create rent schedule');
    } finally {
      setSubmitting(false);
    }
  };

  const createNextRentScheduleFromExisting = async (rent) => {
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/rent`, {
        property_id: rent.property_id,
        tenancy_id: rent.tenancy_id,
        tenant_id: rent.tenant_id || null,
        manual_tenancy_id: rent.manual_tenancy_id || null,
        rent_amount: parseFloat(rent.rent_amount),
        due_day_of_month: parseInt(rent.due_day_of_month, 10),
        payment_method: rent.payment_method || 'bank_transfer',
        notes: rent.notes || null,
      });
      toast.success('Next rent schedule created');
      handleTabChange('rent');
      fetchData();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create rent schedule');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const getDefaultRentForProperty = (propertyId) => {
    const selectedProperty = properties.find((property) => property.id === propertyId);
    if (!selectedProperty) return '';
    const propertyRent = selectedProperty.rent;
    if (propertyRent === null || propertyRent === undefined || Number.isNaN(Number(propertyRent))) {
      return '';
    }
    return String(propertyRent);
  };

  const getExistingScheduleCandidate = (propertyId = null) => {
    const scopedSchedules = propertyId
      ? rentSchedules.filter((rent) => rent.property_id === propertyId)
      : rentSchedules;

    const latestSchedules = getLatestRentSchedulePerChain(scopedSchedules);
    if (latestSchedules.length !== 1) {
      return null;
    }

    return latestSchedules[0];
  };

  const openRentScheduleForm = (property = null, tenancy = null) => {
    handleTabChange('rent');
    setRentDialogOpen(true);
    if (property && tenancy) {
      const manual = Boolean(tenancy.id && !tenancy.tenant_id);
      setRentForm({
        property_id: property.id,
        tenancy_id: tenancy.tenancy_id || tenancy.id,
        tenant_id: tenancy.tenant_id || '',
        manual_tenancy_id: manual ? tenancy.id : '',
        rent_amount: getDefaultRentForProperty(property.id),
        due_day_of_month: '1',
        payment_method: 'bank_transfer',
        notes: '',
      });
      fetchTenants(property.id);
      return;
    }
    if (property) {
      setRentForm({
        property_id: property.id,
        tenancy_id: '',
        tenant_id: '',
        manual_tenancy_id: '',
        rent_amount: getDefaultRentForProperty(property.id),
        due_day_of_month: '1',
        payment_method: 'bank_transfer',
        notes: '',
      });
      fetchTenants(property.id);
      return;
    }

    setRentForm({ property_id: '', tenancy_id: '', tenant_id: '', manual_tenancy_id: '', rent_amount: '', due_day_of_month: '1', payment_method: 'bank_transfer', notes: '' });
  };



  const handleFinalizeAssignmentTenancy = async (assignmentId) => {
    setTenantManagementSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/tenant-assignments/${assignmentId}/finalize-tenancy`, {}, { withCredentials: true });
      toast.success('Tenancy finalized');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to finalize tenancy');
    } finally {
      setTenantManagementSubmitting(false);
    }
  };

  const handleCreateManualTenancy = async (e) => {
    e.preventDefault();
    setTenantManagementSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/tenancies/manual`, buildTenancyDetailsPayload(manualTenancyForm), { withCredentials: true });
      toast.success('Manual tenancy added');
      setManualTenancyForm(createTenancyFormState());
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to add manual tenancy');
    } finally {
      setTenantManagementSubmitting(false);
    }
  };

  const resetEditTenancyState = () => {
    setEditTenancyDialogOpen(false);
    setEditTenancyType(null);
    setEditingTenancyRecord(null);
    setEditTenancyForm({
      property_id: '',
      tenant_id: '',
      tenants: [createEmptyTenancyParticipant()],
      start_date: '',
      deposit_taken: false,
      deposit_amount: '',
      notes: '',
    });
  };

  const openEditAssignmentDialog = (assignment) => {
    setEditTenancyType('assignment');
    setEditingTenancyRecord(assignment);
    setEditTenancyForm({
      property_id: assignment.property_id || '',
      tenant_id: assignment.tenant_id || '',
      tenants: [createEmptyTenancyParticipant()],
      start_date: assignment.start_date || '',
      deposit_taken: Boolean(assignment.deposit_taken || assignment.deposit_amount),
      deposit_amount: assignment.deposit_amount != null ? String(assignment.deposit_amount) : '',
      notes: assignment.notes || '',
    });
    setEditTenancyDialogOpen(true);
  };

  const openEditManualTenancyDialog = (manualTenancy) => {
    setEditTenancyType('manual');
    setEditingTenancyRecord(manualTenancy);
    setEditTenancyForm({
      property_id: manualTenancy.property_id || '',
      tenant_id: '',
      tenants: Array.isArray(manualTenancy.tenants) && manualTenancy.tenants.length
        ? manualTenancy.tenants.map((tenant) => ({ name: tenant?.name || '', email: tenant?.email || '' }))
        : [{ name: '', email: '' }],
      start_date: manualTenancy.start_date || '',
      deposit_taken: Boolean(manualTenancy.deposit_taken || manualTenancy.deposit_amount),
      deposit_amount: manualTenancy.deposit_amount != null ? String(manualTenancy.deposit_amount) : '',
      notes: manualTenancy.notes || '',
    });
    setEditTenancyDialogOpen(true);
  };

  const getStatementForTenancy = (tenancyId) => {
    const candidates = writtenStatements
      .filter((s) => s.tenancy_id === tenancyId && s.status !== 'superseded')
      .sort((a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0));
    return candidates[0] || null;
  };

  const statementHasPdf = (statement) => Boolean(
    statement?.document?.has_versions ||
    statement?.document?.download_available ||
    statement?.document?.final_pdf_path ||
    statement?.document?.pdf_path
  );

  const getStatementActionLabel = (statement, hasPdf = false) => {
    if (!statement) return 'Write Written Statement';
    if (statement.status === 'draft' && !hasPdf) return 'Edit Statement';
    return 'View Statement';
  };

  const [statementVersionsDialogStmt, setStatementVersionsDialogStmt] = useState(null);
  const [statementVersionsDialogEnded, setStatementVersionsDialogEnded] = useState(false);

  const handleOpenWrittenStatement = (propertyId, tenancyId) => {
    if (!propertyId || !tenancyId) return;
    const statement = getStatementForTenancy(tenancyId);
    if (statement && statement.status !== 'draft') {
      navigate(`/tenancy-agreement?propertyId=${propertyId}&tenancyId=${tenancyId}&id=${statement.id}`);
      return;
    }
    if (statement?.status === 'draft' && statementHasPdf(statement)) {
      setEditStatementConfirm({ propertyId, tenancyId, statementId: statement.id });
      return;
    }
    if (statement) {
      navigate(`/tenancy-agreement?propertyId=${propertyId}&tenancyId=${tenancyId}&id=${statement.id}`);
    } else {
      navigate(`/tenancy-agreement?propertyId=${propertyId}&tenancyId=${tenancyId}`);
    }
  };

  const handleEditTenancyRecord = async (e) => {
    e.preventDefault();
    if (!editingTenancyRecord || !editTenancyType) return;

    setEditTenancySubmitting(true);
    try {
      if (editTenancyType === 'assignment') {
        await axios.put(`${API_URL}/api/tenant-assignments/${editingTenancyRecord.id}`, {
          property_id: editTenancyForm.property_id,
          tenant_id: editTenancyForm.tenant_id,
          start_date: editTenancyForm.start_date || null,
          deposit_amount: editTenancyForm.deposit_taken && editTenancyForm.deposit_amount !== ''
            ? parseFloat(editTenancyForm.deposit_amount)
            : 0,
          notes: editTenancyForm.notes || null,
        }, { withCredentials: true });
        toast.success('Tenant assignment updated');
      } else {
        const payload = buildTenancyDetailsPayload(editTenancyForm);
        await axios.put(`${API_URL}/api/tenancies/manual/${editingTenancyRecord.id}`, {
          tenants: payload.tenants,
          start_date: editTenancyForm.start_date || null,
          deposit_amount: payload.deposit_amount,
          notes: payload.notes,
        }, { withCredentials: true });
        toast.success('Manual tenancy updated');
      }

      resetEditTenancyState();
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to update tenancy record');
    } finally {
      setEditTenancySubmitting(false);
    }
  };

  /**
   * Returns true when the tenancy identified by tenancyId has any linked
   * records (written statements or rent schedules) that are already loaded
   * in the dashboard state.  This drives whether the UI offers "End tenancy"
   * (preserve records) vs "Delete tenancy" (hard remove, only for empty tenancies).
   * The backend is the authoritative source — the frontend check is used to
   * surface the most likely correct action up-front; the backend will block
   * a hard-delete even if the frontend misses a record type.
   */
  const tenancyHasRecords = (tenancyId) => {
    if (!tenancyId) return false;
    const hasStatement = writtenStatements.some(
      (s) => s.tenancy_id === tenancyId && s.status !== 'superseded'
    );
    const hasRent = rentSchedules.some(
      (r) => r.tenancy_id === tenancyId || r.manual_tenancy_id === tenancyId
    );
    return hasStatement || hasRent;
  };

  /**
   * Parse the blocking_reasons array from a structured tenancy_has_records error
   * into a natural-language sentence.
   */
  const formatBlockingReasons = (reasons = []) => {
    const labels = {
      written_statements: 'written statements',
      rent: 'rent records',
      issues: 'issues',
      documents: 'documents',
      messages: 'message threads',
    };
    const human = reasons.map((r) => labels[r] || r);
    if (!human.length) return '';
    if (human.length === 1) return human[0];
    return `${human.slice(0, -1).join(', ')} and ${human[human.length - 1]}`;
  };

  // --- Portal tenancy (assignment) actions ---

  /** Hard-delete a portal tenancy assignment. Only succeeds if the tenancy has no records. */
  const handleDeletePortalTenancy = async (assignmentId) => {
    try {
      await axios.delete(`${API_URL}/api/tenant-assignments/${assignmentId}`, { withCredentials: true });
      toast.success('Tenancy deleted');
      await fetchData();
    } catch (error) {
      const data = error.response?.data;
      if (data?.error === 'tenancy_has_records') {
        const reasons = formatBlockingReasons(data.blocking_reasons);
        toast.error(
          reasons
            ? `This tenancy has ${reasons} and cannot be deleted. Use "End tenancy" to close it while preserving records.`
            : data.message || 'This tenancy has records and cannot be deleted.'
        );
      } else {
        toast.error(data?.detail || error.message || 'Failed to delete tenancy');
      }
    }
  };

  /** Soft-end a portal tenancy assignment. Records are preserved. */
  const handleEndPortalTenancy = async (assignmentId) => {
    try {
      await axios.post(`${API_URL}/api/tenant-assignments/${assignmentId}/end`, {}, { withCredentials: true });
      toast.success('Tenancy ended');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to end tenancy');
    }
  };

  // --- Manual tenancy actions ---

  /** Hard-delete a manual tenancy. Only succeeds if the tenancy has no records. */
  const handleDeleteManualTenancy = async (manualTenancyId) => {
    try {
      await axios.delete(`${API_URL}/api/tenancies/manual/${manualTenancyId}`, { withCredentials: true });
      toast.success('Tenancy deleted');
      await fetchData();
    } catch (error) {
      const data = error.response?.data;
      if (data?.error === 'tenancy_has_records') {
        const reasons = formatBlockingReasons(data.blocking_reasons);
        toast.error(
          reasons
            ? `This tenancy has ${reasons} and cannot be deleted. Use "End tenancy" to close it while preserving records.`
            : data.message || 'This tenancy has records and cannot be deleted.'
        );
      } else {
        toast.error(data?.detail || error.message || 'Failed to delete tenancy');
      }
    }
  };

  /** Soft-end a manual tenancy. Records are preserved. */
  const handleEndManualTenancy = async (manualTenancyId) => {
    try {
      await axios.post(`${API_URL}/api/tenancies/manual/${manualTenancyId}/end`, {}, { withCredentials: true });
      toast.success('Tenancy ended');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to end tenancy');
    }
  };

  const handleAddRentSchedule = async (property = null) => {
    // Only auto-create the next month's schedule when we are scoped to a
    // specific property (or the landlord has exactly one property) — this
    // prevents silently picking the wrong property when multiple properties /
    // tenancies exist.
    const multipleProperties = properties.length > 1;
    const candidate = getExistingScheduleCandidate(property?.id);
    if (candidate && (!multipleProperties || property?.id)) {
      await createNextRentScheduleFromExisting(candidate);
      return;
    }

    openRentScheduleForm(property);
  };

  const handleOpenCreateRentForProperty = (property) => {
    handleAddRentSchedule(property);
  };

  const openEditRentDialog = (rent) => {
    setEditingRent(rent);
    setEditRentForm({
      // Show the effective amount (an existing planned partial payment preset, if any,
      // otherwise the full rent amount) so re-saving unchanged doesn't clear a preset.
      rent_amount: rent.planned_payment_amount != null && rent.planned_payment_amount > 0
        ? rent.planned_payment_amount
        : rent.rent_amount,
      due_day_of_month: rent.due_day_of_month,
      paid_date_preset: rentPaymentDatePresets[rent.id] || '',
      payment_method: rent.payment_method || 'bank_transfer',
      notes: rent.notes || ''
    });
    setEditRentDialogOpen(true);
  };

  const handleEditRent = async () => {
    if (!editingRent) return;
    setEditRentSubmitting(true);
    try {
      const originalRentAmount = parseFloat(editingRent.rent_amount);
      const enteredRentAmount = parseFloat(editRentForm.rent_amount);
      // Lowering the Rent Amount below the schedule's actual rent amount is treated as a
      // planned partial payment for this cycle: the true rent amount is preserved, and the
      // lower value becomes the amount used the next time Mark Paid is clicked.
      const isPlannedPartial = !Number.isNaN(enteredRentAmount)
        && !Number.isNaN(originalRentAmount)
        && enteredRentAmount > 0
        && enteredRentAmount < originalRentAmount;

      const payload = {
        rent_amount: isPlannedPartial ? originalRentAmount : editRentForm.rent_amount,
        due_day_of_month: editRentForm.due_day_of_month,
        payment_method: editRentForm.payment_method,
        notes: editRentForm.notes,
        planned_payment_amount: isPlannedPartial ? enteredRentAmount : null,
      };
      await axios.put(`${API_URL}/api/rent/${editingRent.id}`, payload);
      // Save payment date preset locally (no backend storage needed — it drives the UI)
      if (editRentForm.paid_date_preset) {
        setRentPaymentDatePresets((prev) => ({ ...prev, [editingRent.id]: editRentForm.paid_date_preset }));
      } else {
        setRentPaymentDatePresets((prev) => { const next = { ...prev }; delete next[editingRent.id]; return next; });
      }
      toast.success(isPlannedPartial
        ? `Rent schedule updated — £${enteredRentAmount.toFixed(2)} will be recorded as a partial payment when marked paid`
        : 'Rent schedule updated successfully');
      setEditRentDialogOpen(false);
      setEditingRent(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update rent schedule');
    } finally {
      setEditRentSubmitting(false);
    }
  };

  const openMarkPaidDialog = (rentId) => {
    setSelectedRentForPayment(rentId);
    setPaymentDate(new Date().toISOString().slice(0, 10));
    // Don't start timer automatically - wait for user to click "Start Payment Timer"
    setMarkPaidTimer(0);
    setMarkPaidTimerActive(false);
    setMarkPaidDialogOpen(true);
  };

  const startMarkPaidTimer = () => {
    setMarkPaidTimer(20); // 20 second timer
    setMarkPaidTimerActive(true);
  };

  const handleMarkRentPaid = async () => {
    if (!selectedRentForPayment || payingIds.includes(selectedRentForPayment)) return;
    
    const rentId = selectedRentForPayment;
    const rentSchedule = rentSchedules.find(r => r.id === rentId);
    setPayingIds(prev => [...prev, rentId]);
    setMarkPaidDialogOpen(false);
    setMarkPaidTimer(0);
    setMarkPaidTimerActive(false);

    const previousStatus = rentSchedule?.status || 'due';

    // Build payment payload: explicit paymentAmount (used only for additional
    // installments on already partially-paid schedules) takes priority, then the
    // preset planned amount (from Edit Rent Schedule), else the full remaining balance.
    const payload = { paid_date: paymentDate };
    if (paymentAmount && parseFloat(paymentAmount) > 0) {
      payload.amount = parseFloat(paymentAmount);
    } else if (rentSchedule?.planned_payment_amount > 0) {
      payload.amount = rentSchedule.planned_payment_amount;
    }

    // Optimistic UI update
    setRentSchedules(prev => prev.map(r => r.id === rentId ? { ...r, status: 'paid', paid_date: paymentDate } : r));

    try {
      const response = await axios.post(`${API_URL}/api/rent/${rentId}/pay`, payload);
      // Store receipt info and updated status from response
      if (response.data) {
        setRentSchedules(prev => prev.map(r => 
          r.id === rentId 
            ? { ...r, ...response.data }
            : r
        ));
      }
      const successMsg = response.data?.status === 'partially_paid'
        ? `Payment of £${(response.data.amount_paid - (rentSchedule?.amount_paid || 0)).toFixed(2)} recorded — £${response.data.balance_remaining?.toFixed(2)} remaining`
        : 'Rent marked as paid — receipt sent to tenant';
      toast.success(successMsg);
      // Refresh to pick up newly created next-month schedule
      fetchData();
    } catch (error) {
      // Revert optimistic update on failure
      setRentSchedules(prev => prev.map(r => r.id === rentId ? { ...r, status: previousStatus, paid_date: null, receipt_url: null, receipt_reference: null, receipts: [], amount_paid: rentSchedule?.amount_paid || 0, balance_remaining: rentSchedule?.balance_remaining || rentSchedule?.rent_amount || 0 } : r));
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setPayingIds(prev => prev.filter(id => id !== rentId));
      setSelectedRentForPayment(null);
      setPaymentAmount('');
    }
  };

  const handleLogToFinance = async (rent) => {
    if (!rent?.id || financeLoggedRentIds.has(rent.id)) return;
    setLoggingFinanceRentId(rent.id);
    try {
      const paidDate = rent.paid_date || new Date().toISOString().slice(0, 10);
      const period = rent.period_label || rent.month_label
        || (rent.due_date ? new Date(rent.due_date).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) : '');
      await axios.post(
        `${API_URL}/api/cashflow/entries`,
        {
          type: 'income',
          category: 'rent_income',
          amount: rent.amount_paid > 0 ? rent.amount_paid : rent.rent_amount,
          date: paidDate,
          property_id: rent.property_id,
          description: `Rent from ${rent.tenant_name || 'tenant'}${period ? ` — ${period}` : ''}`,
          notes: `Logged from Rent tab (rent schedule ID: ${rent.id})`,
        },
        { withCredentials: true }
      );
      setFinanceLoggedRentIds((prev) => new Set([...prev, rent.id]));
      toast.success('Rent logged to Finance');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string' && detail.toLowerCase().includes('duplicate')) {
        // Already exists — treat as success
        setFinanceLoggedRentIds((prev) => new Set([...prev, rent.id]));
        toast.info('Already logged to Finance');
      } else {
        toast.error(detail || 'Failed to log to Finance');
      }
    } finally {
      setLoggingFinanceRentId(null);
    }
  };

  const handleUnmarkPaid = async (rentId) => {
    try {
      await axios.delete(`${API_URL}/api/rent/${rentId}/payment`);
      toast.success('Payment unmarked successfully');
      // Refresh to update status
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unmark payment');
    }
  };

  const getReceiptUrl = (receiptUrl) => {
    if (!receiptUrl) return '';
    if (receiptUrl.startsWith('blob:')) return receiptUrl;
    return receiptUrl.startsWith('http') ? receiptUrl : `${API_URL}${receiptUrl}`;
  };

  const getReceiptPeriodForPreview = (rent) => {
    if (!rent?.next_due_date) {
      return 'Based on the schedule due date';
    }

    const startDate = new Date(rent.next_due_date);
    if (Number.isNaN(startDate.getTime())) {
      return rent.next_due_date;
    }

    const endDate = new Date(startDate);
    endDate.setMonth(endDate.getMonth() + 1);
    endDate.setDate(endDate.getDate() - 1);

    return `${formatReceiptDate(startDate.toISOString())} – ${formatReceiptDate(endDate.toISOString())}`;
  };

  const getReceiptIssuerDetails = () => {
    const displayName = user?.ownership_type === 'company'
      ? (user?.company_name?.trim() || user?.name || 'SIYAK')
      : (user?.name || 'SIYAK');

    return {
      displayName,
      address: user?.business_address?.trim() || '',
      registration: user?.company_registration?.trim() || '',
      vatNumber: user?.vat_number?.trim() || '',
      email: user?.email || 'info@siyak.co.uk',
      phone: user?.phone?.trim() || '',
      website: user?.website?.trim() || '',
      logoUrl: resolveAssetUrl(user?.logo_url),
    };
  };

  const getReceiptPreviewFilename = (receipt) => {
    const reference = String(receipt?.reference || 'draft').replace(/[^a-z0-9_-]+/gi, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    return `rent-receipt-${reference || 'draft'}.pdf`;
  };

  const downloadReceiptFile = (url, filename) => {
    if (!url) {
      toast.error('Receipt not available');
      return;
    }

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const closeReceiptPreview = () => {
    receiptPreviewRequestRef.current += 1;
    receiptPreviewAbortRef.current?.abort();
    receiptPreviewAbortRef.current = null;
    setReceiptPreviewLoading(false);
    setReceiptPreview(null);
  };

  useEffect(() => {
    return () => {
      receiptPreviewAbortRef.current?.abort();
    };
  }, []);

  const handleOpenReceiptPreview = async (rent) => {
    if (!rent) return;

    receiptPreviewRequestRef.current += 1;
    const requestId = receiptPreviewRequestRef.current;
    receiptPreviewAbortRef.current?.abort();
    const controller = new AbortController();
    receiptPreviewAbortRef.current = controller;

    const paidDate = rent.paid_date || new Date().toISOString().slice(0, 10);

    setReceiptPreview({
      pdfData: null,
      sourceKey: `${rent.id}-${Date.now()}`,
      fallbackUrl: getReceiptUrl(rent.receipt_url),
      reference: rent.receipt_reference || 'Generated on send',
      propertyTitle: rent.property_title || rent.property_id || 'Property',
      propertyAddress: rent.property_address || 'Property address will appear here',
      tenantName: rent.tenant_name || 'Tenant',
      paidDate,
      amount: formatReceiptCurrency(rent.rent_amount),
      paymentMethod: getPaymentMethodLabel(rent.payment_method || 'bank_transfer'),
      periodCovered: getReceiptPeriodForPreview(rent),
      isDraft: !rent.receipt_url,
      generatedOn: new Date().toISOString().slice(0, 10),
      issuer: getReceiptIssuerDetails(),
      errorMessage: '',
    });
    setReceiptPreviewLoading(true);

    try {
      const { bytes } = await fetchReceiptPreviewPdfBytes({
        apiUrl: API_URL,
        rentId: rent.id,
        signal: controller.signal,
      });

      if (requestId !== receiptPreviewRequestRef.current) {
        return;
      }

      setReceiptPreview((current) => current ? {
        ...current,
        pdfData: bytes,
        sourceKey: `${rent.id}-${bytes.byteLength}`,
        errorMessage: '',
      } : current);
    } catch (error) {
      if (error?.name === 'AbortError') {
        return;
      }
      if (requestId !== receiptPreviewRequestRef.current) {
        return;
      }
      setReceiptPreview((current) => current ? {
        ...current,
        pdfData: null,
        errorMessage: error?.message || 'Fetch failed: receipt preview could not be loaded.',
      } : current);
      toast.error(error?.message || 'Failed to load receipt preview');
    } finally {
      if (requestId === receiptPreviewRequestRef.current) {
        receiptPreviewAbortRef.current = null;
        setReceiptPreviewLoading(false);
      }
    }
  };

  const handleViewReceipt = (receiptUrl) => {
    const url = getReceiptUrl(receiptUrl);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      toast.error('Receipt not available');
    }
  };

  const handleDownloadReceiptPreview = () => {
    if (!receiptPreview) {
      toast.error('Receipt not available');
      return;
    }
    const targetUrl = receiptPreview.fallbackUrl;
    downloadReceiptFile(targetUrl, getReceiptPreviewFilename(receiptPreview));
  };

  const handleRequestMarkPaid = (rentId) => {
    // Use the preset date (from edit dialog) if set, otherwise default to today
    const presetDate = rentPaymentDatePresets[rentId];
    setPaymentDate(presetDate || new Date().toISOString().slice(0, 10));
    const shouldSkipConfirmation = skipMarkPaidConfirmation || (
      typeof window !== 'undefined' && (
        window.localStorage.getItem(MARK_PAID_CONFIRM_PREF_KEY) === 'true' ||
        window.sessionStorage.getItem(MARK_PAID_CONFIRM_PREF_KEY) === 'true'
      )
    );

    if (shouldSkipConfirmation) {
      if (!skipMarkPaidConfirmation) {
        setSkipMarkPaidConfirmation(true);
      }
      setMarkPaidCountdownArmedRentId(rentId);
      return;
    }

    setPendingMarkPaidRentId(rentId);
    setMarkPaidDontShowAgainChecked(false);
    setMarkPaidConfirmOpen(true);
  };

  const handleMarkPaidDialogConfirm = () => {
    if (!pendingMarkPaidRentId) {
      setMarkPaidConfirmOpen(false);
      return;
    }

    if (markPaidDontShowAgainChecked) {
      setSkipMarkPaidConfirmation(true);
      if (typeof window !== 'undefined') {
        // Permanent: "Don't show again" persists across browser sessions
        window.localStorage.setItem(MARK_PAID_CONFIRM_PREF_KEY, 'true');
      }
    } else {
      // Mark as seen this session so the dialog doesn't re-appear for subsequent marks
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(MARK_PAID_CONFIRM_PREF_KEY, 'true');
      }
      setSkipMarkPaidConfirmation(true);
    }

    setMarkPaidCountdownArmedRentId(pendingMarkPaidRentId);
    setMarkPaidConfirmOpen(false);
    setPendingMarkPaidRentId(null);
  };

  const handleOpenReceiptSettings = () => {
    setMarkPaidConfirmOpen(false);
    setPendingMarkPaidRentId(null);
    setMarkPaidDontShowAgainChecked(false);
    navigate('/profile?tab=profile&section=receipt-settings');
  };

  useEffect(() => {
    if (!skipMarkPaidConfirmation) return;
    setMarkPaidConfirmOpen(false);
    setPendingMarkPaidRentId(null);
    setMarkPaidDontShowAgainChecked(false);
  }, [skipMarkPaidConfirmation]);

  // Reset checkbox when dialog opens
  useEffect(() => {
    if (markPaidConfirmOpen) {
      setMarkPaidDontShowAgainChecked(false);
    }
  }, [markPaidConfirmOpen]);

  const getPropertyInterest = (propertyId) => {
    const saves = savedProperties.filter(s => s.property_id === propertyId);
    const transfers = transferRequests.filter(t => t.current_tenancy_id === propertyId);
    const applications = []; // Could be added if needed for rented property
    
    return {
      saves,
      transfers,
      applications,
      totalInterest: saves.length + transfers.length
    };
  };

  const handleOpenInterestPopup = (propertyId) => {
    const interest = getPropertyInterest(propertyId);
    setSelectedPropertyInterest({
      propertyId,
      property: properties.find(p => p.id === propertyId),
      ...interest
    });
    setInterestPopupOpen(true);
  };

  const getApplicationDecisionStatus = (application) => application?.decision_status || application?.status || 'pending';

  const handlePromptInterestRemoval = (interestEntry) => {
    if (!interestEntry?.property_id || !(interestEntry?.tenant_id || interestEntry?.user_id)) {
      return;
    }
    setPendingInterestRemoval(interestEntry);
  };

  const confirmInterestRemoval = async () => {
    const tenantId = pendingInterestRemoval?.tenant_id || pendingInterestRemoval?.user_id;
    const propertyId = pendingInterestRemoval?.property_id;

    if (!tenantId || !propertyId) {
      setPendingInterestRemoval(null);
      return;
    }

    try {
      setRemovingInterest(true);
      await axios.delete(`${API_URL}/api/properties/${propertyId}/interests/${tenantId}`, {
        withCredentials: true,
      });

      setSavedProperties((prev) =>
        prev.filter((entry) => !(entry.property_id === propertyId && (entry.tenant_id || entry.user_id) === tenantId))
      );
      setSelectedPropertyInterest((prev) => {
        if (!prev || prev.propertyId !== propertyId) {
          return prev;
        }

        const nextSaves = (prev.saves || []).filter(
          (entry) => !((entry.tenant_id || entry.user_id) === tenantId && entry.property_id === propertyId)
        );
        return {
          ...prev,
          saves: nextSaves,
          totalInterest: nextSaves.length + (prev.transfers || []).length,
        };
      });
      toast.success('Interest removed from your list.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove interest.');
    } finally {
      setRemovingInterest(false);
      setPendingInterestRemoval(null);
    }
  };

  const getDefaultReplyMessage = (tenantName) => {
    if (!user) return '';
    const landlordName = user?.name || 'Landlord';
    const landlordEmail = user?.email || '';
    const landlordPhone = user?.phone || '';
    const name = tenantName || 'there';
    
    let message = `Hi ${name},\n\nThank you for your application! I'm interested in learning more about you and your needs.\n\nPlease feel free to contact me at ${landlordEmail}`;
    if (landlordPhone) message += ` or ${landlordPhone}`;
    message += `\n\nto discuss further. I look forward to hearing from you.\n\nBest regards,\n${landlordName}`;
    return message;
  };

  const handleApplicationDecision = async (applicationId, decision) => {
    try {
      const res = await axios.patch(
        `${API_URL}/api/applications/${applicationId}`,
        { status: decision },
        { withCredentials: true }
      );
      setApplications((prev) => prev.map((a) => a.id === applicationId ? { ...a, ...res.data } : a));
      toast.success(decision === 'approved' ? 'Application approved. Next step: assign an existing tenant or send an invite.' : 'Application rejected.');
    } catch {
      toast.error('Failed to update application.');
    }
  };

  const handleOpenScreeningDialog = async (application) => {
    setScreeningApplication(application);
    setSelectedQuestionIds(new Set());
    setScreeningDialogOpen(true);
    setScreeningQuestionsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/screening-questions`, { withCredentials: true });
      setScreeningQuestions(res.data || []);
    } catch {
      toast.error('Failed to load screening questions.');
      setScreeningDialogOpen(false);
    } finally {
      setScreeningQuestionsLoading(false);
    }
  };

  const handleSendScreening = async () => {
    if (!screeningApplication || selectedQuestionIds.size === 0) return;
    setScreeningSending(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/applications/${screeningApplication.id}/send-screening`,
        { question_ids: Array.from(selectedQuestionIds) },
        { withCredentials: true }
      );
      setApplications((prev) =>
        prev.map((a) => a.id === screeningApplication.id ? { ...a, ...res.data } : a)
      );
      toast.success('Pre-screening questionnaire sent.');
      setScreeningDialogOpen(false);
      setScreeningApplication(null);
      setSelectedQuestionIds(new Set());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send screening questionnaire.');
    } finally {
      setScreeningSending(false);
    }
  };

  const handleOpenScreeningAnswers = (application) => {
    setScreeningAnswersApplication(application);
    setScreeningAnswersDialogOpen(true);
  };

  const getScreeningStatusBadge = (application) => {
    if (application.screening_status === 'sent') {
      return <Badge className="bg-amber-100 text-amber-700">Screening Sent</Badge>;
    }
    if (application.screening_status === 'completed') {
      return <Badge className="bg-green-100 text-green-700">Screening Complete</Badge>;
    }
    return null;
  };

  const handlePromptApplicationRemoval = (application) => {
    if (!application?.id) {
      return;
    }
    setPendingApplicationRemoval(application);
  };

  const confirmApplicationRemoval = async () => {
    if (!pendingApplicationRemoval?.id) {
      setPendingApplicationRemoval(null);
      return;
    }

    try {
      setWithdrawingApplication(true);
      await axios.patch(
        `${API_URL}/api/applications/${pendingApplicationRemoval.id}/withdraw`,
        {},
        { withCredentials: true }
      );

      setApplications((prev) => prev.filter((application) => application.id !== pendingApplicationRemoval.id));
      setNotifications((prev) => {
        const removed = prev.filter(
          (notification) =>
            notification.type === 'application_received'
            && notification.property_id === pendingApplicationRemoval.property_id
            && notification.tenant_id === pendingApplicationRemoval.tenant_id
        );
        if (removed.length > 0) {
          const unreadRemoved = removed.filter((notification) => !notification.is_read).length;
          if (unreadRemoved > 0) {
            setUnreadCount((count) => Math.max(0, count - unreadRemoved));
          }
        }
        return prev.filter(
          (notification) => !(
            notification.type === 'application_received'
            && notification.property_id === pendingApplicationRemoval.property_id
            && notification.tenant_id === pendingApplicationRemoval.tenant_id
          )
        );
      });
      toast.success('Application removed from your list.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove application.');
    } finally {
      setWithdrawingApplication(false);
      setPendingApplicationRemoval(null);
    }
  };

  const handleOpenReplyDialog = (application) => {
    if (!application?.id) {
      toast.error('Application details are not available');
      return;
    }

    setReplyNotification(application);
    setReplyMessage(getDefaultReplyMessage(application.tenant_name));
    setReplyOpen(true);
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim()) {
      toast.error('Message cannot be empty');
      return;
    }

    if (!replyNotification?.id) {
      toast.error('Cannot reply: Application not found');
      return;
    }

    setReplySending(true);
    try {
      const sentAt = new Date().toISOString();
      const sentMessage = replyMessage;
      const currentReplyNotification = replyNotification;

      const response = await axios.post(
        `${API_URL}/api/applications/${currentReplyNotification.id}/messages`,
        { body: sentMessage },
        { withCredentials: true }
      );

      const messageDoc = response.data;
      const matchingNotif = notifications.find(
        (notification) =>
          (notification.application_id && notification.application_id === currentReplyNotification.id)
          || (
            notification.type === 'application_received'
            && notification.property_id === currentReplyNotification.property_id
            && notification.tenant_id === currentReplyNotification.tenant_id
          )
      );

      setApplications((prev) => prev.map((application) => {
        if (application.id !== currentReplyNotification.id) {
          return application;
        }

        return {
          ...application,
          application_messages: [...(application.application_messages || []), messageDoc],
          landlord_reply: sentMessage,
          landlord_reply_at: sentAt,
          latest_application_message: sentMessage,
          latest_application_message_at: sentAt,
          application_unread_count: 0,
          decision_status: 'responded',
        };
      }));

      setNotifications((prev) => prev.map((notification) => {
        if (!matchingNotif || notification.id !== matchingNotif.id) {
          return notification;
        }

        return {
          ...notification,
          is_read: true,
        };
      }));

      setUnreadCount((prev) => Math.max(0, prev - (matchingNotif && !matchingNotif.is_read ? 1 : 0)));

      toast.success('Message sent to tenant');
      setReplyOpen(false);
      setReplyMessage('');
      setReplyNotification(null);
    } catch (error) {
      console.error('Reply error:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setReplySending(false);
    }
  };

  const [pendingForwardReceipt, setPendingForwardReceipt] = useState(null);
  const [forwardingReceipt, setForwardingReceipt] = useState(false);
  const [writtenStatements, setWrittenStatements] = useState([]);
  const [editStatementConfirm, setEditStatementConfirm] = useState(null); // { propertyId, tenancyId, statementId }
  const [amendStatementConfirm, setAmendStatementConfirm] = useState(null); // { propertyId, tenancyId, statementId, status }

  const handleForwardReceipt = (rentId, tenantName, tenantSource) => {
    setPendingForwardReceipt({ rentId, tenantName, tenantSource });
  };

  const confirmForwardReceipt = async () => {
    if (!pendingForwardReceipt?.rentId) {
      setPendingForwardReceipt(null);
      return;
    }
    setForwardingReceipt(true);
    try {
      const response = await axios.post(`${API_URL}/api/rent/${pendingForwardReceipt.rentId}/forward-receipt`);
      toast.success(`Receipt resent to ${response.data.tenant_email || pendingForwardReceipt.tenantName || 'tenant'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend receipt');
    } finally {
      setForwardingReceipt(false);
      setPendingForwardReceipt(null);
    }
  };

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const res = await axios.post(`${API_URL}/api/rent/send-reminders`);
      const { total, reminders_sent } = res.data;
      if (total > 0) {
        toast.success(`Sent ${total} reminders (${reminders_sent.upcoming} upcoming, ${reminders_sent.due_today} due today, ${reminders_sent.overdue} overdue)`);
      } else {
        toast.info('No reminders to send at this time');
      }
    } catch (error) {
      toast.error('Failed to send reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  const handleSendRentReminder = async (rent) => {
    if (!rent?.id) return;
    setSendingReminderIds((prev) => [...prev, rent.id]);
    try {
      const res = await axios.post(`${API_URL}/api/rent/${rent.id}/send-reminder`);
      if (res.data?.sent) {
        const label = (res.data.reminder_type || 'rent').replace('_', ' ');
        toast.success(`Sent ${label} reminder to ${rent.tenant_name || 'tenant'}`);
      } else {
        toast.info('Reminder was already sent recently or no tenant email is available');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSendingReminderIds((prev) => prev.filter((id) => id !== rent.id));
    }
  };

  const handleDeleteRent = async (rentId) => {
    try {
      await axios.delete(`${API_URL}/api/rent/${rentId}`);
      toast.success('Rent schedule deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete rent schedule');
    }
  };

  const openDeletePropertyConfirm = (propertyId) => {
    setPendingConfirmAction({ type: 'delete_property', id: propertyId });
  };

  const openArchivePropertyConfirm = (propertyId) => {
    setPendingConfirmAction({ type: 'archive_property', id: propertyId });
  };

  const openUnmarkPaidConfirm = (rentId) => {
    setPendingConfirmAction({ type: 'unmark_paid', id: rentId });
  };

  const openDeleteRentConfirm = (rentId) => {
    setPendingConfirmAction({ type: 'delete_rent', id: rentId });
  };

  const getConfirmDialogContent = () => {
    switch (pendingConfirmAction?.type) {
      case 'delete_property':
        return {
          title: 'Delete property?',
          description: 'This will permanently delete the property, all related issues, and tenant assignments. Only empty properties with no records can be deleted.',
          actionLabel: 'Delete property',
        };
      case 'archive_property':
        return {
          title: 'Archive property?',
          description: 'This will archive the property for audit purposes. It will become read-only — no new tenancies, documents, or schedules can be added. All existing records will be preserved.',
          actionLabel: 'Archive property',
        };
      case 'unmark_paid':
        return {
          title: 'Unmark payment?',
          description: 'This will remove the payment record and revert the rent status to unpaid.',
          actionLabel: 'Unmark payment',
        };
      case 'delete_rent':
        return {
          title: 'Delete rent schedule?',
          description: 'This will permanently remove this rent schedule.',
          actionLabel: 'Delete schedule',
        };
      case 'end_assignment':
        return {
          title: 'End tenancy?',
          description: `This will end the tenancy for ${pendingConfirmAction?.label || 'this tenant'}. All records (written statements, rent, documents) will be preserved.`,
          actionLabel: 'End tenancy',
        };
      case 'delete_assignment':
        return {
          title: 'Delete tenancy?',
          description: `This will permanently delete the tenancy record for ${pendingConfirmAction?.label || 'this tenant'}. Only tenancies with no records can be deleted.`,
          actionLabel: 'Delete tenancy',
        };
      case 'end_manual_tenancy':
        return {
          title: 'End tenancy?',
          description: `This will end the tenancy for ${pendingConfirmAction?.label || 'this tenancy'}. All records (written statements, rent, documents) will be preserved.`,
          actionLabel: 'End tenancy',
        };
      case 'delete_manual_tenancy':
        return {
          title: 'Delete tenancy?',
          description: `This will permanently delete the tenancy record for ${pendingConfirmAction?.label || 'this tenancy'}. Only tenancies with no records can be deleted.`,
          actionLabel: 'Delete tenancy',
        };
      default:
        return {
          title: 'Confirm action',
          description: 'Are you sure you want to continue?',
          actionLabel: 'Confirm',
        };
    }
  };

  const handleConfirmAction = async () => {
    if (!pendingConfirmAction?.id || confirmActionLoading) return;

    setConfirmActionLoading(true);
    try {
      if (pendingConfirmAction.type === 'delete_property') {
        await handleDeleteProperty(pendingConfirmAction.id);
      } else if (pendingConfirmAction.type === 'archive_property') {
        await handleArchiveProperty(pendingConfirmAction.id);
      } else if (pendingConfirmAction.type === 'unmark_paid') {
        await handleUnmarkPaid(pendingConfirmAction.id);
      } else if (pendingConfirmAction.type === 'delete_rent') {
        await handleDeleteRent(pendingConfirmAction.id);
      } else if (pendingConfirmAction.type === 'end_assignment') {
        await handleEndPortalTenancy(pendingConfirmAction.id);
      } else if (pendingConfirmAction.type === 'delete_assignment') {
        await handleDeletePortalTenancy(pendingConfirmAction.id);
      } else if (pendingConfirmAction.type === 'end_manual_tenancy') {
        await handleEndManualTenancy(pendingConfirmAction.id);
      } else if (pendingConfirmAction.type === 'delete_manual_tenancy') {
        await handleDeleteManualTenancy(pendingConfirmAction.id);
      }
    } finally {
      setConfirmActionLoading(false);
      setPendingConfirmAction(null);
    }
  };

  // Formats a currency amount for summary stat cards: £850, £2.5k, £1.2m
  const formatCurrencyStat = (val) => {
    const n = Math.ceil(val || 0);
    if (n >= 1_000_000) return `£${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}m`;
    if (n >= 1_000) return `£${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
    return `£${n}`;
  };

  const getRentStatusBadge = (status, rent) => {
    switch (status) {
      case 'due':
        return <Badge className="bg-blue-100 text-blue-700">Due</Badge>;
      case 'overdue':
        return <Badge className="bg-red-100 text-red-700">Overdue</Badge>;
      case 'paid':
        return <Badge className="bg-green-100 text-green-700">Paid</Badge>;
      case 'partially_paid':
        return (
          <Badge className="bg-amber-100 text-amber-700">
            Partial{rent?.amount_paid > 0 ? ` — £${rent.amount_paid.toFixed(2)}` : ''}
          </Badge>
        );
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      'bank_transfer': 'Bank Transfer',
      'standing_order': 'Standing Order',
      'direct_debit': 'Direct Debit',
      'cheque': 'Cheque',
      'cash': 'Cash',
      'other': 'Other'
    };
    return labels[method] || method;
  };

  const getApplicationStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>;
      case 'responded':
        return <Badge className="bg-purple-100 text-purple-700">Responded</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-700">Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700">Rejected</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'reported':
        return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="h-3 w-3 mr-1" /> Reported</Badge>;
      case 'in_progress':
        return <Badge className="bg-blue-100 text-blue-700"><Wrench className="h-3 w-3 mr-1" /> In Progress</Badge>;
      case 'resolved':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" /> Resolved</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'low':
        return <Badge variant="outline" className="text-gray-600">Low</Badge>;
      case 'medium':
        return <Badge variant="outline" className="text-blue-600">Medium</Badge>;
      case 'high':
        return <Badge variant="outline" className="text-orange-600">High</Badge>;
      case 'urgent':
        return <Badge className="bg-red-100 text-red-700"><AlertTriangle className="h-3 w-3 mr-1" /> Urgent</Badge>;
      default:
        return <Badge variant="outline">{priority}</Badge>;
    }
  };

  const getTimelineIcon = (eventType) => {
    switch (eventType) {
      case 'issue_created':
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      case 'status_change':
        return <CheckCircle className="h-4 w-4 text-blue-600" />;
      case 'note_added':
        return <Edit className="h-4 w-4 text-purple-600" />;
      case 'tenant_assigned':
        return <Users className="h-4 w-4 text-green-600" />;
      case 'tenant_removed':
        return <Users className="h-4 w-4 text-red-600" />;
      default:
        return <History className="h-4 w-4 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-royal-600" />
        </div>
      </Layout>
    );
  }

  if (!user?.is_approved) {
    return (
      <Layout>
        <div className="bg-navy-50 min-h-screen">
          <div className="max-w-2xl mx-auto px-4 py-16 text-center">
            <AlertTriangle className="h-16 w-16 text-yellow-500 mx-auto mb-6" />
            <h1 className="font-heading text-2xl font-bold text-navy-900 mb-4">
              Account Pending Approval
            </h1>
            <p className="text-muted-foreground mb-6">
              Your landlord account is pending admin approval. You'll be able to manage properties once approved.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  const pendingApplicationsCount = applications.filter((application) => getApplicationDecisionStatus(application) === 'pending').length;
  const assignedTenantIds = new Set(
    tenantAssignments
      .filter((assignment) => assignment.status !== 'ended')
      .map((assignment) => assignment.tenant_id)
      .filter(Boolean)
  );
  const availableEditTenantOptions = tenantDirectory.filter(
    (tenant) => tenant.id === editTenancyForm.tenant_id || !assignedTenantIds.has(tenant.id)
  );
  const headerUpgradeHints = getUpgradePromptsForPlacement(accountMetrics, 'property-create', {
    justCreatedProperty: postPropertyCreateCueVisible,
  });
  const dashboardUpgradeHints = getUpgradePromptsForPlacement(accountMetrics, 'dashboard').filter(
    (hint) => !(postPropertyCreateCueVisible && hint.id === 'multi-property')
  );
  const rentUpgradeHints = getUpgradePromptsForPlacement(accountMetrics, 'rent');
  const handleDismissHint = (hintId, options = {}) => {
    if (hintId) {
      dismissUpgradePrompt(hintId);
      setDismissedHintVersion((value) => value + 1);
    }
    if (options.hidePostCreateCue) {
      setPostPropertyCreateCueVisible(false);
    }
  };

  return (
    <Layout>
      <div className="bg-navy-50 min-h-screen" data-testid="landlord-dashboard">
        {/* Header */}
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h1 className="font-heading text-3xl font-bold text-navy-900 mb-2">
                  Property Dashboard
                </h1>
                <p className="text-muted-foreground">
                  Manage your properties, issues, and tenants
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Dialog open={propertyDialogOpen} onOpenChange={setPropertyDialogOpen}>
                  <DialogTrigger asChild>
                    <Button onClick={() => handleOpenPropertyDialog()} data-testid="add-property-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Property
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>{editingProperty ? 'Edit Property' : 'Add New Property'}</DialogTitle>
                      <DialogDescription>
                        {editingProperty ? 'Update property details' : 'Add a new property to manage'}
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmitProperty} className="space-y-4">
                      <div>
                        <Label>Address *</Label>
                        <div className="space-y-2">
                          <AddressInput
                            defaultValue={propertyForm.address}
                            onAddressSelect={(addressObject) => {
                              setPropertyForm((prev) => ({
                                ...prev,
                                address: Object.prototype.hasOwnProperty.call(addressObject || {}, 'formatted')
                                  ? (addressObject?.formatted ?? '')
                                  : prev.address,
                                postcode: addressObject?.postcode || prev.postcode,
                                title: !editingProperty ? (addressObject?.line1 || prev.title) : prev.title,
                              }));
                            }}
                            data-testid="property-address-input"
                          />

                        </div>
                      </div>
                      <div>
                        <Label>Property Name *</Label>
                        <Input
                          required
                          placeholder="e.g. 12 Oak Street Flat"
                          value={propertyForm.title}
                          onChange={(e) => setPropertyForm({...propertyForm, title: e.target.value})}
                          data-testid="property-title-input"
                        />
                      </div>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                          <Label>Postcode *</Label>
                          <Input
                            required
                            placeholder="e.g. SW1 1AA"
                            value={propertyForm.postcode}
                            onChange={(e) => setPropertyForm({...propertyForm, postcode: e.target.value})}
                            data-testid="property-postcode-input"
                          />
                        </div>
                        <div>
                          <Label>Property Type</Label>
                          <Select value={propertyForm.property_type} onValueChange={(val) => setPropertyForm({...propertyForm, property_type: val})}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="flat">Flat</SelectItem>
                              <SelectItem value="house">House</SelectItem>
                              <SelectItem value="studio">Studio</SelectItem>
                              <SelectItem value="bungalow">Bungalow</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                          <Label>Bedrooms</Label>
                          <Select value={propertyForm.bedrooms} onValueChange={(val) => setPropertyForm({...propertyForm, bedrooms: val})}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {[1,2,3,4,5,6].map(n => <SelectItem key={n} value={n.toString()}>{n}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Bathrooms</Label>
                          <Select value={propertyForm.bathrooms} onValueChange={(val) => setPropertyForm({...propertyForm, bathrooms: val})}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {[1,2,3,4].map(n => <SelectItem key={n} value={n.toString()}>{n}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label>Monthly Rent (£)</Label>
                        <Input
                          type="number"
                          placeholder="e.g. 1500"
                          value={propertyForm.rent}
                          onChange={(e) => setPropertyForm({...propertyForm, rent: e.target.value})}
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea
                          placeholder="Property notes..."
                          value={propertyForm.description}
                          onChange={(e) => setPropertyForm({...propertyForm, description: e.target.value})}
                          rows={3}
                        />
                      </div>

                      <div className="border-t pt-4 mt-4 space-y-3">
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            id="property_is_occupied"
                            checked={Boolean(propertyForm.is_occupied)}
                            onChange={(e) => setPropertyForm({...propertyForm, is_occupied: e.target.checked})}
                            className="w-4 h-4"
                          />
                          <Label htmlFor="property_is_occupied" className="m-0 cursor-pointer">Property Occupied</Label>
                        </div>
                        <p className="text-xs text-muted-foreground pl-7">
                          Occupied properties are treated as <strong>No listing</strong> until marked unoccupied.
                        </p>
                      </div>

                      {/* Compliance Fields */}
                      <div className="border-t pt-4 mt-4">
                        <h4 className="font-semibold mb-4 text-sm">Property MOT Information</h4>
                        
                        <div className="grid grid-cols-1 gap-4 mb-4 sm:grid-cols-2">
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              id="has_gas_supply"
                              checked={propertyForm.has_gas_supply}
                              onChange={(e) => setPropertyForm({...propertyForm, has_gas_supply: e.target.checked})}
                              className="w-4 h-4"
                            />
                            <Label htmlFor="has_gas_supply" className="m-0 cursor-pointer">Has Gas Supply</Label>
                          </div>

                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              id="furnished_appliances"
                              checked={propertyForm.furnished_appliances}
                              onChange={(e) => setPropertyForm({...propertyForm, furnished_appliances: e.target.checked})}
                              className="w-4 h-4"
                            />
                            <Label htmlFor="furnished_appliances" className="m-0 cursor-pointer">Furnished with Electric Appliances</Label>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <div>
                            <Label>Number of Floors</Label>
                            <Input
                              type="number"
                              placeholder="e.g. 3"
                              value={propertyForm.floors}
                              onChange={(e) => setPropertyForm({...propertyForm, floors: normalizeNonNegativeIntegerInput(e.target.value)})}
                              min="0"
                              step="1"
                            />
                          </div>

                          <div>
                            <Label>Furnishing Type</Label>
                            <Select value={propertyForm.furnishing} onValueChange={(val) => setPropertyForm({...propertyForm, furnishing: val})}>
                              <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Not specified</SelectItem>
                                <SelectItem value="unfurnished">Unfurnished</SelectItem>
                                <SelectItem value="part_furnished">Part Furnished</SelectItem>
                                <SelectItem value="furnished">Furnished</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div>
                          <Label>Water System Type</Label>
                          <Select value={propertyForm.water_system_type} onValueChange={(val) => setPropertyForm({...propertyForm, water_system_type: val})}>
                            <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Standard/No risk</SelectItem>
                              <SelectItem value="stored_water">Stored Water System</SelectItem>
                              <SelectItem value="complex_system">Complex Water System</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label>Council Licensing</Label>
                          <p className="text-xs text-muted-foreground mb-1">Does this property require a council licence?</p>
                          <Select value={propertyForm.licensing_status} onValueChange={(val) => setPropertyForm({...propertyForm, licensing_status: val})}>
                            <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="no">No licence required</SelectItem>
                              <SelectItem value="yes">Licence required</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground mt-1">
                            Not sure? Check your local council's website or search on{' '}
                            <a href="https://www.gov.uk/find-local-council" target="_blank" rel="noopener noreferrer" className="underline text-blue-600">GOV.UK</a>.
                          </p>
                        </div>
                      </div>

                      <div className="border-t pt-4 mt-4 rounded-lg bg-slate-50 px-4 py-3">
                        <h4 className="font-semibold mb-1 text-sm">Tenancy Management</h4>
                        <p className="text-xs text-muted-foreground">
                          Add manual tenancies and remove occupants from the dedicated Tenancy Management tab.
                        </p>
                      </div>

                      <div className="flex gap-4 pt-6">
                        <Button type="button" variant="outline" onClick={() => setPropertyDialogOpen(false)}>Cancel</Button>
                        <Button type="submit" disabled={submitting}>
                          {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                          {editingProperty ? 'Update' : 'Create'}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>

                <Dialog
                  open={inviteDialogOpen}
                  onOpenChange={(open) => {
                    setInviteDialogOpen(open);
                    if (!open) {
                      setCreatedInvite(null);
                      setInviteForm(createTenancyFormState());
                    }
                  }}
                >
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Invite tenants</DialogTitle>
                      <DialogDescription>
                        Capture the tenancy participants and create invite links for tenant onboarding.
                      </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleCreateInvite} className="space-y-4">
                      <div>
                        <Label>Property *</Label>
                        <Select
                          value={inviteForm.property_id}
                          onValueChange={(val) => setInviteForm({ ...inviteForm, property_id: val })}
                        >
                          <SelectTrigger data-testid="invite-property-select">
                            <SelectValue placeholder="Select a property" />
                          </SelectTrigger>
                          <SelectContent>
                            {properties.map((property) => (
                              <SelectItem key={property.id} value={property.id}>
                                {property.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-3">
                        <Label>Tenancy participants</Label>
                        {inviteForm.tenants.map((tenant, index) => (
                          <div key={`invite-tenant-${index}`} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3">
                            <Input
                              value={tenant.name}
                              onChange={(e) => setInviteForm((form) => ({
                                ...form,
                                tenants: form.tenants.map((item, itemIndex) => itemIndex === index ? { ...item, name: e.target.value } : item),
                              }))}
                              placeholder="Full Name (e.g. John Smith)"
                              required
                              data-testid={`invite-tenant-name-${index}`}
                            />
                            <Input
                              type="email"
                              value={tenant.email}
                              onChange={(e) => setInviteForm((form) => ({
                                ...form,
                                tenants: form.tenants.map((item, itemIndex) => itemIndex === index ? { ...item, email: e.target.value } : item),
                              }))}
                              placeholder="tenant@example.com"
                              required
                              data-testid={`invite-tenant-email-${index}`}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              disabled={inviteForm.tenants.length === 1}
                              onClick={() => setInviteForm((form) => ({ ...form, tenants: form.tenants.filter((_, itemIndex) => itemIndex !== index) }))}
                            >
                              Remove
                            </Button>
                          </div>
                        ))}
                        <Button type="button" variant="outline" onClick={() => setInviteForm((form) => ({ ...form, tenants: [...form.tenants, createEmptyTenancyParticipant()] }))}>
                          Add another participant
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Start date</Label>
                          <Input
                            type="date"
                            value={inviteForm.start_date}
                            onChange={(e) => setInviteForm((form) => ({ ...form, start_date: e.target.value }))}
                          />
                        </div>
                        <div>
                          <Label>Deposit amount</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={inviteForm.deposit_amount}
                            disabled={!inviteForm.deposit_taken}
                            onChange={(e) => setInviteForm((form) => ({ ...form, deposit_amount: e.target.value }))}
                            placeholder="0.00"
                          />
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="invite_deposit_taken"
                          checked={inviteForm.deposit_taken}
                          onCheckedChange={(checked) => setInviteForm((form) => ({ ...form, deposit_taken: Boolean(checked), deposit_amount: checked ? form.deposit_amount : '' }))}
                        />
                        <Label htmlFor="invite_deposit_taken" className="m-0 cursor-pointer">Deposit taken for this tenancy</Label>
                      </div>

                      <div>
                        <Label>Notes</Label>
                        <Input
                          value={inviteForm.notes}
                          onChange={(e) => setInviteForm((form) => ({ ...form, notes: e.target.value }))}
                          placeholder="Optional notes"
                        />
                      </div>

                      <div className="flex gap-4 pt-4">
                        <Button type="button" variant="outline" onClick={() => setInviteDialogOpen(false)}>
                          Close
                        </Button>
                        <Button type="submit" disabled={inviteSubmitting || !inviteForm.property_id || inviteForm.tenants.some((tenant) => !tenant.name.trim() || !tenant.email.trim())} data-testid="create-invite-btn">
                          {inviteSubmitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                          Create tenancy invite
                        </Button>
                      </div>
                    </form>

                    {createdInvite && (
                      <div className="mt-4 space-y-4 rounded-md border p-4 bg-navy-50" data-testid="created-invite-result">
                        <div className="space-y-1">
                          <p className="text-sm font-medium">Tenancy invite created for {createdInvite.property_title}</p>
                          <p className="text-xs text-muted-foreground">
                            Recipients: {(createdInvite.invites || [createdInvite]).map((invite) => invite.tenant_name || invite.email).join(', ')}
                          </p>
                          <Badge className={createdInvite.email_status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                            {createdInvite.email_status === 'sent' ? 'Tenancy invite email sent' : 'Tenancy invite email not sent yet'}
                          </Badge>
                          {createdInvite.email_sent_at && (
                            <p className="text-xs text-muted-foreground">
                              Sent {new Date(createdInvite.email_sent_at).toLocaleString('en-GB')}
                            </p>
                          )}
                        </div>
                        <div className="space-y-2">
                          {(createdInvite.invites || [createdInvite]).map((invite, index) => (
                            <div key={invite.id || index} className="space-y-2 rounded-md border border-navy-200 bg-white/70 p-3">
                              <div className="text-xs text-muted-foreground">
                                {invite.tenant_name ? `${invite.tenant_name} - ` : ''}
                                {invite.invite_type === 'existing'
                                  ? 'Login link for an existing tenant account'
                                  : 'Registration link for a new tenant'}
                              </div>
                              <div className="flex items-center gap-2">
                                <Input value={invite.invite_url} readOnly className="text-xs" />
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  onClick={() => copyToClipboard(invite.invite_url, 'Invite link copied')}
                                  title="Copy invite link"
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                          {(createdInvite.start_date || createdInvite.deposit_amount || createdInvite.notes) && (
                            <div className="rounded-md border border-navy-200 bg-white/70 p-3 text-xs text-muted-foreground space-y-1">
                              {createdInvite.start_date && <p>Start date: {new Date(`${createdInvite.start_date}T00:00:00`).toLocaleDateString('en-GB')}</p>}
                              {createdInvite.deposit_amount ? <p>Deposit: £{Number(createdInvite.deposit_amount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p> : null}
                              {createdInvite.notes && <p>Notes: {createdInvite.notes}</p>}
                            </div>
                          )}
                          <Button type="button" variant="outline" onClick={sendInviteEmail}>
                            <MailPlus className="h-4 w-4 mr-2" />
                            Email invite link{(createdInvite.invites || []).length > 1 ? 's' : ''}
                          </Button>
                        </div>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          {headerUpgradeHints.map((hint) => (
            <div key={`${hint.id}-${dismissedHintVersion}`} className="mb-6">
              <UpgradeHintCard
                message={hint.message}
                ctaLabel={hint.ctaLabel}
                onDismiss={() => handleDismissHint(hint.id, { hidePostCreateCue: true })}
              />
            </div>
          ))}

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <Card onClick={() => handleTabChange('properties')} className="cursor-pointer hover:shadow-md transition-shadow">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-royal-100">
                  <Building2 className="h-5 w-5 text-royal-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stats?.properties || 0}</div>
                  <div className="text-sm text-muted-foreground">Properties</div>
                </div>
              </CardContent>
            </Card>
            <Card onClick={() => handleTabChange('tenants')} className="cursor-pointer hover:shadow-md transition-shadow">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100">
                  <Users className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stats?.tenants || 0}</div>
                  <div className="text-sm text-muted-foreground">Tenants</div>
                </div>
              </CardContent>
            </Card>
            <Card onClick={() => handleTabChange('issues')} className="cursor-pointer hover:shadow-md transition-shadow">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-100">
                  <ClipboardList className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stats?.issues?.reported || 0}</div>
                  <div className="text-sm text-muted-foreground">Open Issues</div>
                </div>
              </CardContent>
            </Card>
            <Card onClick={() => handleTabChange('issues')} className="cursor-pointer hover:shadow-md transition-shadow">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <Wrench className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stats?.issues?.in_progress || 0}</div>
                  <div className="text-sm text-muted-foreground">In Progress</div>
                </div>
              </CardContent>
            </Card>
            <Card onClick={() => handleTabChange('rent')} className="cursor-pointer hover:shadow-md transition-shadow">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-100">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{rentOverview?.overdue || 0}</div>
                  <div className="text-sm text-muted-foreground">Overdue</div>
                </div>
              </CardContent>
            </Card>
            <Card onClick={() => handleTabChange('rent')} className="cursor-pointer hover:shadow-md transition-shadow">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-100 shrink-0">
                  <PoundSterling className="h-5 w-5 text-purple-600" />
                </div>
                <div className="min-w-0">
                  <div className="text-2xl font-bold">{formatCurrencyStat(rentOverview?.monthly_revenue)}</div>
                  <div className="text-sm text-muted-foreground">This Month</div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div ref={tabScrollRef}>
          <Tabs value={activeTab} onValueChange={(val) => { handleTabChange(val); if (val === 'tenants') fetchData(); }} className="space-y-8">
            <TabsList className="w-full justify-start sm:flex-wrap sm:overflow-visible">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="properties">Properties</TabsTrigger>
              <TabsTrigger value="tenants">Tenancy Management</TabsTrigger>
              <TabsTrigger value="applications">
                Applications
                {pendingApplicationsCount > 0 && (
                  <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-royal-600 text-white">
                    {pendingApplicationsCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="issues">
                Issues
                {(stats?.issues?.reported || 0) > 0 && (
                  <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-yellow-500 text-white">
                    {stats.issues.reported}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="rent">
                Rent
                {(rentOverview?.overdue || 0) > 0 && (
                  <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-red-500 text-white">
                    {rentOverview.overdue}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="messages">
                Messages
                {messageUnreadCount > 0 && (
                  <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-royal-600 text-white">
                    {messageUnreadCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="compliance">
                Property MOT
                {complianceStats.nonCompliant > 0 && (
                  <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-red-500 text-white">
                    {complianceStats.nonCompliant}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="finances">Finances</TabsTrigger>
              <TabsTrigger value="document-deliveries">Document Deliveries</TabsTrigger>
              <TabsTrigger value="export">Export Data</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview">
              {dashboardUpgradeHints.length > 0 && (
                <div className="mb-6 space-y-3">
                  {dashboardUpgradeHints.map((hint) => (
                    <UpgradeHintCard
                      key={`${hint.id}-${dismissedHintVersion}`}
                      message={hint.message}
                      ctaLabel={hint.ctaLabel}
                      compact
                      onDismiss={() => handleDismissHint(hint.id)}
                    />
                  ))}
                </div>
              )}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Issues */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span>Recent Issues</span>
                      <Button variant="ghost" size="sm" onClick={() => handleTabChange('issues')}>View All</Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {issues.filter(i => i.status !== 'resolved').slice(0, 5).length > 0 ? (
                      <div className="space-y-4">
                        {issues.filter(i => i.status !== 'resolved').slice(0, 5).map(issue => (
                          <div key={issue.id} className="flex items-start justify-between p-4 bg-navy-50 rounded-lg">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                {getStatusBadge(issue.status)}
                                {getPriorityBadge(issue.priority)}
                              </div>
                              <h4 className="font-medium text-sm">{issue.title}</h4>
                              <p className="text-xs text-muted-foreground">{issue.property_title}</p>
                            </div>
                            <Button size="sm" variant="ghost" onClick={() => {
                              setSelectedIssue(issue);
                              setIssueNotes(issue.admin_notes || '');
                            }}>
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-4">No open issues</p>
                    )}
                  </CardContent>
                </Card>

                {/* Properties Overview with Summary */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span>Property Summary</span>
                      <Button variant="ghost" size="sm" onClick={() => handleTabChange('properties')}>View All</Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {properties.slice(0, 5).length > 0 ? (
                      <div className="space-y-4">
                        {properties.slice(0, 5).map(prop => {
                          const complianceStatus = getPropertyComplianceStatus(prop.id);
                          const occupancyBadge = getOccupancyBadgeMeta(prop);
                          return (
                            <div key={prop.id} className="cursor-pointer hover:opacity-90 transition-opacity" data-testid={`property-summary-${prop.id}`} onClick={() => handleTabChange('properties')}>
                              <StreetViewStrip propId={prop.id} filename={prop.listing_street_view_image} photoHeight="h-20" fallbackBg="rounded-xl border border-gray-200 shadow-sm bg-slate-50">
                                <div className="p-3">
                                  <div className="flex items-center justify-between mb-1">
                                    <h4 className="font-medium text-sm">{prop.title}</h4>
                                    <div className="flex items-center gap-2">
                                      <Badge variant="outline" className={`capitalize text-xs ${occupancyBadge.className}`}>{occupancyBadge.label}</Badge>
                                      {getPropertyInterest(prop.id).totalInterest > 0 && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenInterestPopup(prop.id);
                                          }}
                                          className="flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200"
                                        >
                                          <Bell className="h-3 w-3" />
                                          {getPropertyInterest(prop.id).totalInterest}
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  <p className="text-xs text-muted-foreground mb-2">{prop.address}</p>
                                  <div className="flex items-center gap-3 text-xs flex-wrap">
                                    <span className="flex items-center gap-1">
                                      <Users className="h-3 w-3 text-blue-500" /> {prop.tenant_count} tenants
                                    </span>
                                    {prop.open_issues > 0 ? (
                                      <span className="flex items-center gap-1 text-amber-600 font-medium">
                                        <AlertTriangle className="h-3 w-3" /> {prop.open_issues} open issues
                                      </span>
                                    ) : (
                                      <span className="flex items-center gap-1 text-green-600">
                                        <CheckCircle className="h-3 w-3" /> No issues
                                      </span>
                                    )}
                                    {prop.rent > 0 && (
                                      <span className="flex items-center gap-1 text-muted-foreground">
                                        <PoundSterling className="h-3 w-3" /> {prop.rent.toLocaleString()}/mo
                                      </span>
                                    )}
                                    {complianceStatus && (
                                      <span className={`flex items-center gap-1 font-medium ${complianceStatus.color}`}>
                                        {complianceStatus.status === 'compliant' && <CheckCircle className="h-3 w-3" />}
                                        {complianceStatus.status === 'warning' && <Clock className="h-3 w-3" />}
                                        {complianceStatus.status === 'non-compliant' && <AlertTriangle className="h-3 w-3" />}
                                        {complianceStatus.status === 'disabled' && <EyeOff className="h-3 w-3" />}
                                        {complianceStatus.label}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </StreetViewStrip>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-4">No properties yet</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Properties Tab */}
            <TabsContent value="properties">
              {/* Active / Archived filter — mirrors tenancy status pattern */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
                <div className="flex rounded-md border overflow-hidden w-fit" role="group" aria-label="Property status filter">
                  {['active', 'archived'].map((status) => (
                    <button
                      key={status}
                      type="button"
                      className={`px-4 py-1.5 text-sm font-medium transition-colors ${
                        propertyStatusFilter === status
                          ? 'bg-royal-600 text-white'
                          : 'bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                      onClick={() => setPropertyStatusFilter(status)}
                      aria-pressed={propertyStatusFilter === status}
                    >
                      {status === 'active' ? 'Active' : 'Archived'}
                    </button>
                  ))}
                </div>
                {propertyStatusFilter === 'active' && (
                  <Button onClick={() => handleOpenPropertyDialog()}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Property
                  </Button>
                )}
              </div>
              {(() => {
                const filteredProperties = properties.filter(p =>
                  propertyStatusFilter === 'archived'
                    ? p.lifecycle_status === 'archived'
                    : p.lifecycle_status !== 'archived'
                );
                return filteredProperties.length > 0 ? (
                <div className="space-y-4">
                  {filteredProperties.map(property => {
                    const isArchived = property.lifecycle_status === 'archived';
                    const hasRecords = propertyHasRecords(property.id);
                    return (
                    <div key={property.id} className={`relative${isArchived ? ' opacity-80' : ''}`}>
                      <StreetViewStrip propId={property.id} filename={property.listing_street_view_image} photoHeight="h-28" fallbackBg="bg-slate-50 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                      <CardContent className="p-6">
                        {(() => {
                          const listingStatus = getListingStatusMeta(property);
                          const propertyMarketRate = marketRateByProperty[property.id];
                          const canShareMarketRate = propertyMarketRate?.median_rent != null && (propertyMarketRate?.active_tenant_count || 0) > 0;
                          const marketRateShared = Boolean(propertyMarketRate?.shared_with_all_tenants);
                          return (
                        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                          <div className="flex-1">
                            {/* Archived banner */}
                            {isArchived && (
                              <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800 flex items-start gap-2">
                                <Archive className="h-4 w-4 mt-0.5 shrink-0" />
                                <span>This property has been archived and is kept for audit and record purposes. It is read-only.</span>
                              </div>
                            )}
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <Badge variant="outline" className="capitalize">{property.property_type}</Badge>
                              {isArchived ? (
                                <Badge className="bg-gray-100 text-gray-600">Archived</Badge>
                              ) : (
                                <Badge variant="outline" className={getListingStatusMeta(property).badgeClassName}>
                                  {getListingStatusMeta(property).label}
                                </Badge>
                              )}
                              {property.open_issues > 0 && (
                                <Badge className="bg-yellow-100 text-yellow-700">{property.open_issues} open issues</Badge>
                              )}
                              {!isArchived && getPropertyInterest(property.id).totalInterest > 0 && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenInterestPopup(property.id);
                                  }}
                                  className="flex items-center gap-1 px-2.5 py-1 text-xs rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors cursor-pointer"
                                >
                                  <Bell className="h-3 w-3" />
                                  {getPropertyInterest(property.id).totalInterest} interested
                                </button>
                              )}
                            </div>
                            <h3 className="font-heading font-semibold text-lg">{property.title}</h3>
                            <p className="text-sm text-muted-foreground">{property.address}</p>
                            <div className="mt-2">
                              <div className="flex items-center gap-2 flex-wrap">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-semibold text-navy-900">
                                    £{Number(property.rent || 0).toLocaleString('en-GB')} / month
                                    {!isArchived && marketRateByProperty[property.id]?.median_rent && (
                                      (() => {
                                        const percentOfMedian = (property.rent / marketRateByProperty[property.id].median_rent) * 100;
                                        const diff = Math.round(percentOfMedian - 100);
                                        const isAbove = diff > 0;
                                        return (
                                          <span className={`text-xs ml-1 ${diff > 0 ? 'text-amber-600' : diff < 0 ? 'text-green-600' : 'text-muted-foreground'}`}>
                                            ({Math.abs(diff)}% {isAbove ? 'above' : diff < 0 ? 'below' : 'at'} market)
                                          </span>
                                        );
                                      })()
                                    )}
                                  </p>

                                  {!isArchived && (
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); handleScrapeMarketData(property.id); }}
                                      disabled={marketRateLoadingByProperty[property.id]}
                                      className="inline-flex items-center justify-center p-1 rounded text-sm text-muted-foreground hover:bg-slate-100"
                                      title="Refresh market data"
                                    >
                                      {marketRateLoadingByProperty[property.id] ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <RefreshCw className="h-4 w-4" />
                                      )}
                                    </button>
                                  )}

                                  {!isArchived && canShareMarketRate && (
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="ghost"
                                      className={`h-7 px-2 text-xs gap-1 ${marketRateShared ? 'text-royal-600' : 'text-muted-foreground'}`}
                                      title={marketRateShared ? 'Market rate visible to all active tenants — click to hide' : 'Share market rate with all active tenants'}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleMarketRateShared(property.id, marketRateShared);
                                      }}
                                    >
                                      {marketRateShared
                                        ? <><Eye className="h-3.5 w-3.5" /> Shared</>
                                        : <><EyeOff className="h-3.5 w-3.5" /> Share</>}
                                    </Button>
                                  )}
                                </div>
                              </div>
                              {!isArchived && marketRateByProperty[property.id]?.median_rent != null && (
                                <>
                                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1">
                                    <BarChart2 className="h-3 w-3" />
                                    Area rents — £{Math.round(marketRateByProperty[property.id].min_rent).toLocaleString()} min · <span className="font-medium text-foreground">£{Math.round(marketRateByProperty[property.id].median_rent).toLocaleString()} median</span> · £{Math.round(marketRateByProperty[property.id].max_rent).toLocaleString()} max pcm
                                  </p>
                                  {(() => {
                                    const mr = marketRateByProperty[property.id];
                                    const requested = mr?.bedrooms_requested;
                                    const used = mr?.bedrooms;
                                    if (requested != null && used != null && requested !== used) {
                                      const usedLabel = used === 0 ? 'studio' : `${used} bed`;
                                      return (
                                        <p className="text-[11px] text-amber-600 mt-0.5">
                                          ⚠ Insufficient listings for {requested} bed — showing {usedLabel} rates as nearest available.
                                        </p>
                                      );
                                    }
                                    return null;
                                  })()}
                                </>
                              )}
                              {!isArchived && marketRateByProperty[property.id] && marketRateByProperty[property.id]?.median_rent == null && (
                                <p className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1">
                                  <BarChart2 className="h-3 w-3" />
                                  No market data found for this location and property type yet ({marketRateByProperty[property.id]?.bedrooms ?? property.bedrooms} bedrooms).
                                </p>
                              )}
                              {!isArchived && marketRateByProperty[property.id]?.last_scraped_at && (
                                <p className="text-[11px] text-muted-foreground mt-1">
                                  Updated: {new Date(marketRateByProperty[property.id].last_scraped_at).toLocaleString('en-GB', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-4 mt-2 text-sm text-navy-600">
                              <span>{property.bedrooms} bed</span>
                              <span>{property.bathrooms} bath</span>
                              <span>{property.tenant_count} tenant(s)</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {/* Write actions — hidden for archived properties */}
                            {!isArchived && listingStatus.showAction && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/landlord/listing-builder/${property.id}`);
                                }}
                              >
                                <Sparkles className="w-3 h-3 mr-1.5" />
                                {listingStatus.actionLabel}
                              </Button>
                            )}
                            {!isArchived && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenPropertyDialog(property);
                                }}
                              >
                                <Edit className="h-4 w-4 mr-1.5" />
                                Edit
                              </Button>
                            )}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                                {/* Manage tenancy — active only */}
                                {!isArchived && (
                                  <DropdownMenuItem onClick={() => {
                                    setInviteForm(createTenancyFormState({ property_id: property.id }));
                                    setTenancyMode('invite');
                                    setCreatedInvite(null);
                                    handleTabChange('tenants');
                                    setTimeout(() => scrollToElementWithOffset(tabScrollRef.current), 150);
                                  }}>
                                    <Users className="h-4 w-4 mr-2" />
                                    Manage tenancy
                                  </DropdownMenuItem>
                                )}
                                {/* Published listing links — visible even when archived (for reference) */}
                                {listingStatus.isPublished && (
                                  <>
                                    <DropdownMenuItem onClick={() => handleOpenPublishedPropertyLink(property.id)}>
                                      <Eye className="h-4 w-4 mr-2" />
                                      View Published Listing
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleCopyPublishedPropertyLink(property.id)}>
                                      <Copy className="h-4 w-4 mr-2" />
                                      Copy Published Link
                                    </DropdownMenuItem>
                                  </>
                                )}
                                <DropdownMenuItem onClick={() => { setSelectedPropertyTimeline(property); fetchTimeline(property.id); }}>
                                  <History className="h-4 w-4 mr-2" />
                                  Timeline
                                </DropdownMenuItem>
                                {/* Archive vs Delete — not shown for already-archived properties */}
                                {!isArchived && (
                                  <>
                                    <DropdownMenuSeparator />
                                    {hasRecords ? (
                                      <DropdownMenuItem
                                        className="text-amber-700 focus:text-amber-700"
                                        onClick={() => openArchivePropertyConfirm(property.id)}
                                      >
                                        <Archive className="h-4 w-4 mr-2" />
                                        Archive Property
                                      </DropdownMenuItem>
                                    ) : (
                                      <DropdownMenuItem
                                        className="text-destructive focus:text-destructive"
                                        onClick={() => openDeletePropertyConfirm(property.id)}
                                      >
                                        <Trash2 className="h-4 w-4 mr-2" />
                                        Delete Property
                                      </DropdownMenuItem>
                                    )}
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                          );
                        })()}
                      </CardContent>
                      </StreetViewStrip>
                    </div>
                    );
                  })}
                </div>
                ) : (
                <Card className="p-12 text-center">
                  <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  {propertyStatusFilter === 'archived' ? (
                    <>
                      <h3 className="font-heading font-semibold text-lg mb-2">No Archived Properties</h3>
                      <p className="text-muted-foreground mb-4">Properties you archive will appear here, preserved for audit.</p>
                    </>
                  ) : (
                    <>
                      <h3 className="font-heading font-semibold text-lg mb-2">No Properties Yet</h3>
                      <p className="text-muted-foreground mb-4">Add your first property to start managing</p>
                      <Button onClick={() => handleOpenPropertyDialog()}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Property
                      </Button>
                    </>
                  )}
                </Card>
                );
              })()}
            </TabsContent>

            <TabsContent value="tenants">
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <div className="flex rounded-md border overflow-hidden w-fit" role="group" aria-label="Tenancy status filter">
                    {['active', 'ended'].map((status) => (
                      <button
                        key={status}
                        type="button"
                        className={`px-4 py-1.5 text-sm font-medium transition-colors ${
                          tenancyStatusFilter === status
                            ? 'bg-royal-600 text-white'
                            : 'bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                        onClick={() => setTenancyStatusFilter(status)}
                        aria-pressed={tenancyStatusFilter === status}
                      >
                        {status === 'active' ? 'Active' : 'Ended'}
                      </button>
                    ))}
                  </div>
                  <Button onClick={() => setAddTenancyDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Tenancy
                  </Button>
                </div>
                <Dialog open={addTenancyDialogOpen} onOpenChange={setAddTenancyDialogOpen}>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Add tenancy</DialogTitle>
                      <DialogDescription>
                        {tenancyMode === 'invite'
                          ? 'Send a portal invite link. The tenancy activates after the tenant accepts.'
                          : 'Create an offline tenancy record for occupants not using the portal.'}
                      </DialogDescription>
                    </DialogHeader>
                    <div>
                    <div className="flex rounded-md border overflow-hidden w-fit mt-2">
                      <button
                        type="button"
                        className={`flex items-center px-4 py-1.5 text-sm font-medium transition-colors ${
                          tenancyMode === 'invite' ? 'bg-royal-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                        onClick={() => { setTenancyMode('invite'); setCreatedInvite(null); }}
                      >
                        <MailPlus className="h-3.5 w-3.5 mr-1.5" />
                        Portal invite
                      </button>
                      <button
                        type="button"
                        className={`flex items-center px-4 py-1.5 text-sm font-medium transition-colors ${
                          tenancyMode === 'manual' ? 'bg-royal-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                        onClick={() => { setTenancyMode('manual'); setCreatedInvite(null); }}
                      >
                        <Users className="h-3.5 w-3.5 mr-1.5" />
                        Manual (offline)
                      </button>
                    </div>
                    </div>
                    <div>
                    <form
                      onSubmit={tenancyMode === 'invite' ? handleCreateInvite : async (e) => {
                        e.preventDefault();
                        setTenantManagementSubmitting(true);
                        try {
                          await axios.post(`${API_URL}/api/tenancies/manual`, buildTenancyDetailsPayload(inviteForm), { withCredentials: true });
                          setInviteForm(createTenancyFormState());
                          setCreatedInvite(null);
                          setAddTenancyDialogOpen(false);
                          toast.success('Manual tenancy added');
                          await fetchData();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || err.message || 'Failed to add manual tenancy');
                        } finally {
                          setTenantManagementSubmitting(false);
                        }
                      }}
                      className="space-y-4"
                    >
                      <div>
                        <Label>Property *</Label>
                        <Select value={inviteForm.property_id} onValueChange={(val) => setInviteForm((form) => ({ ...form, property_id: val }))}>
                          <SelectTrigger data-testid="invite-property-select"><SelectValue placeholder="Select property" /></SelectTrigger>
                          <SelectContent>
                            {properties.map((property) => (
                              <SelectItem key={property.id} value={property.id}>{property.title}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-3">
                        <Label>Tenancy participants</Label>
                        {inviteForm.tenants.map((tenant, index) => (
                          <div key={`tenancy-tab-tenant-${index}`} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3">
                            <Input
                              value={tenant.name}
                              onChange={(e) => setInviteForm((form) => ({
                                ...form,
                                tenants: form.tenants.map((item, itemIndex) => itemIndex === index ? { ...item, name: e.target.value } : item),
                              }))}
                              placeholder="Full Name (e.g. John Smith)"
                              required
                              data-testid={`invite-tenant-name-${index}`}
                            />
                            <Input
                              type="email"
                              value={tenant.email}
                              onChange={(e) => setInviteForm((form) => ({
                                ...form,
                                tenants: form.tenants.map((item, itemIndex) => itemIndex === index ? { ...item, email: e.target.value } : item),
                              }))}
                              placeholder="tenant@example.com"
                              required
                              data-testid={`invite-tenant-email-${index}`}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              disabled={inviteForm.tenants.length === 1}
                              onClick={() => setInviteForm((form) => ({ ...form, tenants: form.tenants.filter((_, itemIndex) => itemIndex !== index) }))}
                            >
                              Remove
                            </Button>
                          </div>
                        ))}
                        <Button type="button" variant="outline" onClick={() => setInviteForm((form) => ({ ...form, tenants: [...form.tenants, createEmptyTenancyParticipant()] }))}>
                          Add another participant
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Start date</Label>
                          <Input type="date" value={inviteForm.start_date} onChange={(e) => setInviteForm((form) => ({ ...form, start_date: e.target.value }))} />
                        </div>
                        <div>
                          <Label>Deposit amount</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={inviteForm.deposit_amount}
                            disabled={!inviteForm.deposit_taken}
                            onChange={(e) => setInviteForm((form) => ({ ...form, deposit_amount: e.target.value }))}
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="tenancy_tab_deposit_taken"
                          checked={inviteForm.deposit_taken}
                          onCheckedChange={(checked) => setInviteForm((form) => ({ ...form, deposit_taken: Boolean(checked), deposit_amount: checked ? form.deposit_amount : '' }))}
                        />
                        <Label htmlFor="tenancy_tab_deposit_taken" className="m-0 cursor-pointer">Deposit taken for this tenancy</Label>
                      </div>
                      <div>
                        <Label>Notes</Label>
                        <Input
                          value={inviteForm.notes}
                          onChange={(e) => setInviteForm((form) => ({ ...form, notes: e.target.value }))}
                          placeholder="Optional notes"
                        />
                      </div>
                      <Button
                        type="submit"
                        disabled={inviteSubmitting || tenantManagementSubmitting || !inviteForm.property_id || inviteForm.tenants.some((t) => !t.name.trim() || !t.email.trim())}
                        data-testid={tenancyMode === 'invite' ? 'create-invite-btn' : undefined}
                      >
                        {(inviteSubmitting || tenantManagementSubmitting) ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                        {tenancyMode === 'invite' ? 'Create tenancy invite' : 'Add manual tenancy'}
                      </Button>
                    </form>

                    {tenancyMode === 'invite' && createdInvite && (
                      <div className="mt-4 space-y-4 rounded-md border p-4 bg-navy-50" data-testid="created-invite-result">
                        <div className="space-y-1">
                          <p className="text-sm font-medium">Tenancy invite created for {createdInvite.property_title}</p>
                          <p className="text-xs text-muted-foreground">
                            Recipients: {(createdInvite.invites || [createdInvite]).map((invite) => invite.tenant_name || invite.email).join(', ')}
                          </p>
                          <Badge className={createdInvite.email_status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                            {createdInvite.email_status === 'sent' ? 'Tenancy invite email sent' : 'Tenancy invite email not sent yet'}
                          </Badge>
                          {createdInvite.email_sent_at && (
                            <p className="text-xs text-muted-foreground">
                              Sent {new Date(createdInvite.email_sent_at).toLocaleString('en-GB')}
                            </p>
                          )}
                        </div>
                        <div className="space-y-2">
                          {(createdInvite.invites || [createdInvite]).map((invite, index) => (
                            <div key={invite.id || index} className="space-y-2 rounded-md border border-navy-200 bg-white/70 p-3">
                              <div className="text-xs text-muted-foreground">
                                {invite.tenant_name ? `${invite.tenant_name} - ` : ''}
                                {invite.invite_type === 'existing'
                                  ? 'Login link for an existing tenant account'
                                  : 'Registration link for a new tenant'}
                              </div>
                              <div className="flex items-center gap-2">
                                <Input value={invite.invite_url} readOnly className="text-xs" />
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  onClick={() => copyToClipboard(invite.invite_url, 'Invite link copied')}
                                  title="Copy invite link"
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                          {(createdInvite.start_date || createdInvite.deposit_amount || createdInvite.notes) && (
                            <div className="rounded-md border border-navy-200 bg-white/70 p-3 text-xs text-muted-foreground space-y-1">
                              {createdInvite.start_date && <p>Start date: {new Date(`${createdInvite.start_date}T00:00:00`).toLocaleDateString('en-GB')}</p>}
                              {createdInvite.deposit_amount ? <p>Deposit: £{Number(createdInvite.deposit_amount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p> : null}
                              {createdInvite.notes && <p>Notes: {createdInvite.notes}</p>}
                            </div>
                          )}
                          <Button type="button" variant="outline" onClick={sendInviteEmail}>
                            <MailPlus className="h-4 w-4 mr-2" />
                            Email invite link{(createdInvite.invites || []).length > 1 ? 's' : ''}
                          </Button>
                        </div>
                      </div>
                    )}
                    </div>
                  </DialogContent>
                </Dialog>

                <div className="space-y-4">
                  {properties.map((property) => {
                    const isEndedView = tenancyStatusFilter === 'ended';
                    const propertyAssignments = tenantAssignments.filter(
                      (a) => a.property_id === property.id && (isEndedView ? a.status === 'ended' : a.status !== 'ended')
                    );
                    const propertyManualTenancies = manualTenancies.filter(
                      (m) => m.property_id === property.id && (isEndedView ? m.status === 'ended' : m.status !== 'ended')
                    );
                    // In ended view, skip properties with no ended tenancies
                    if (isEndedView && propertyAssignments.length === 0 && propertyManualTenancies.length === 0) return null;
                    const activeOccupantCount =
                      tenantAssignments.filter((a) => a.property_id === property.id && a.status !== 'ended').length +
                      manualTenancies.filter((m) => m.property_id === property.id && m.status !== 'ended').length;
                    return (
                      <Card key={property.id}>
                        <CardHeader>
                          <CardTitle className="flex items-center justify-between gap-3 flex-wrap">
                            <span>{property.title}</span>
                            <span className="text-sm font-normal text-muted-foreground">{activeOccupantCount} active occupant(s)</span>
                          </CardTitle>
                          <CardDescription>{property.address}</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          {propertyAssignments.length === 0 && propertyManualTenancies.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                              {tenancyStatusFilter === 'ended'
                                ? 'No ended tenancies for this property.'
                                : 'No active tenancies for this property yet.'}
                            </p>
                          ) : (
                            <div className="space-y-3">
                              {propertyAssignments.map((assignment) => (
                                <div key={assignment.id} className="flex flex-col md:flex-row md:items-center justify-between gap-3 rounded-lg border p-4">
                                  <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <p className="font-medium">{assignment.tenant_name}</p>
                                      <Badge variant="outline">Portal tenancy</Badge>
                                      {assignment.status === 'ended' ? (
                                        <Badge className="bg-slate-100 text-slate-600 border-slate-300">Ended</Badge>
                                      ) : assignment.tenancy_id ? (
                                        <Badge variant="outline">Tenancy active</Badge>
                                      ) : (
                                        <Badge variant="secondary">Needs finalizing</Badge>
                                      )}
                                    </div>
                                    <p className="text-sm text-muted-foreground">{assignment.tenant_email}</p>
                                    <p className="text-xs text-muted-foreground mt-1">Start date: {assignment.start_date || 'Not set'}</p>
                                    {assignment.status === 'ended' && assignment.end_date && (
                                      <p className="text-xs text-muted-foreground">Ended: {new Date(assignment.end_date).toLocaleDateString('en-GB')}</p>
                                    )}
                                    <p className="text-xs text-muted-foreground">Deposit: {assignment.deposit_amount ? `£${Number(assignment.deposit_amount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'Not taken'}</p>
                                    {assignment.status === 'ended' && (
                                      <p className="text-xs text-amber-700 mt-1 italic">This tenancy has ended and is preserved for audit and legal record purposes.</p>
                                    )}
                                  </div>
                                  <div className="flex flex-col gap-2 md:items-end">
                                    {assignment.status !== 'ended' && (
                                      <div className="flex flex-wrap items-center gap-2" data-testid="tenancy-actions">
                                        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Tenancy actions</span>
                                        {!assignment.tenancy_id && (
                                          <Button variant="outline" size="sm" onClick={() => handleFinalizeAssignmentTenancy(assignment.id)} disabled={tenantManagementSubmitting}>
                                            <CheckCircle className="h-4 w-4 mr-1.5" />
                                            Finalize Tenancy
                                          </Button>
                                        )}
                                        <Button variant="outline" size="sm" onClick={() => openEditAssignmentDialog(assignment)}>
                                          <Edit className="h-4 w-4 mr-1.5" />
                                          Edit
                                        </Button>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => handleTabChange('rent')}
                                          disabled={!assignment.tenancy_id}
                                        >
                                          <PoundSterling className="h-4 w-4 mr-1.5" />
                                          Create Rent Schedule
                                        </Button>
                                        {tenancyHasRecords(assignment.tenancy_id) ? (
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setPendingConfirmAction({ type: 'end_assignment', id: assignment.id, label: assignment.tenant_name })}
                                          >
                                            <XCircle className="h-4 w-4 mr-1.5" />
                                            End tenancy
                                          </Button>
                                        ) : (
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setPendingConfirmAction({ type: 'delete_assignment', id: assignment.id, label: assignment.tenant_name })}
                                          >
                                            <XCircle className="h-4 w-4 mr-1.5" />
                                            Delete tenancy
                                          </Button>
                                        )}
                                      </div>
                                    )}
                                    {(() => {
                                      const stmt = getStatementForTenancy(assignment.tenancy_id);
                                      const hasPdf = statementHasPdf(stmt);
                                      return (
                                        <div className="flex flex-wrap items-center gap-2" data-testid="statement-actions">
                                          <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Written statement</span>
                                          <Button variant="outline" size="sm" onClick={() => {
                                            if (stmt?.status === 'draft' && hasPdf) {
                                              navigate(`/tenancy-agreement?propertyId=${assignment.property_id}&tenancyId=${assignment.tenancy_id}&id=${stmt.id}&view=1`);
                                            } else {
                                              handleOpenWrittenStatement(assignment.property_id, assignment.tenancy_id);
                                            }
                                          }} disabled={!assignment.tenancy_id}>
                                            <FileText className="h-4 w-4 mr-1.5" />
                                            {getStatementActionLabel(stmt, hasPdf)}
                                          </Button>
                                          {hasPdf && assignment.status !== 'ended' && (
                                            <Button variant="outline" size="sm" onClick={() =>
                                              stmt?.status === 'draft'
                                                ? setEditStatementConfirm({ propertyId: assignment.property_id, tenancyId: assignment.tenancy_id, statementId: stmt.id })
                                                : setAmendStatementConfirm({ propertyId: assignment.property_id, tenancyId: assignment.tenancy_id, statementId: stmt.id, status: stmt.status })
                                            }>
                                              <PenLine className="h-4 w-4 mr-1.5" />
                                              Amend
                                            </Button>
                                          )}
                                          {hasPdf && (
                                            <Button variant="outline" size="sm" onClick={() => { setStatementVersionsDialogStmt(stmt); setStatementVersionsDialogEnded(assignment.status === 'ended'); }}>
                                              <Download className="h-4 w-4 mr-1.5" />
                                              PDF &amp; Signing
                                            </Button>
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </div>
                              ))}
                              {propertyManualTenancies.map((manualTenancy) => (
                                <div key={manualTenancy.id} className="flex flex-col md:flex-row md:items-center justify-between gap-3 rounded-lg border p-4 bg-slate-50">
                                  <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <p className="font-medium">{getTenancyLabel(manualTenancy)}</p>
                                      <Badge variant="outline">Manual tenancy</Badge>
                                      {manualTenancy.status === 'ended' && (
                                        <Badge className="bg-slate-100 text-slate-600 border-slate-300">Ended</Badge>
                                      )}
                                    </div>
                                    <p className="text-sm text-muted-foreground">{getTenancyTenantEmails(manualTenancy).join(', ') || 'No email recorded'}</p>
                                    <p className="text-xs text-muted-foreground mt-1">Start date: {manualTenancy.start_date || 'Not set'}</p>
                                    {manualTenancy.status === 'ended' && manualTenancy.ended_at && (
                                      <p className="text-xs text-muted-foreground">Ended: {new Date(manualTenancy.ended_at).toLocaleDateString('en-GB')}</p>
                                    )}
                                    <p className="text-xs text-muted-foreground">Deposit: {manualTenancy.deposit_amount ? `£${Number(manualTenancy.deposit_amount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'Not taken'}</p>
                                    {manualTenancy.status === 'ended' && (
                                      <p className="text-xs text-amber-700 mt-1 italic">This tenancy has ended and is preserved for audit and legal record purposes.</p>
                                    )}
                                  </div>
                                  <div className="flex flex-col gap-2 md:items-end">
                                    {manualTenancy.status !== 'ended' && (
                                      <div className="flex flex-wrap items-center gap-2" data-testid="tenancy-actions">
                                        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Tenancy actions</span>
                                        <Button variant="outline" size="sm" onClick={() => openEditManualTenancyDialog(manualTenancy)}>
                                          <Edit className="h-4 w-4 mr-1.5" />
                                          Edit
                                        </Button>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => handleTabChange('rent')}
                                        >
                                          <PoundSterling className="h-4 w-4 mr-1.5" />
                                          Create Rent Schedule
                                        </Button>
                                        {tenancyHasRecords(manualTenancy.id) ? (
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setPendingConfirmAction({ type: 'end_manual_tenancy', id: manualTenancy.id, label: getTenancyLabel(manualTenancy) })}
                                          >
                                            <XCircle className="h-4 w-4 mr-1.5" />
                                            End tenancy
                                          </Button>
                                        ) : (
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setPendingConfirmAction({ type: 'delete_manual_tenancy', id: manualTenancy.id, label: getTenancyLabel(manualTenancy) })}
                                          >
                                            <XCircle className="h-4 w-4 mr-1.5" />
                                            Delete tenancy
                                          </Button>
                                        )}
                                      </div>
                                    )}
                                    {(() => {
                                      const stmt = getStatementForTenancy(manualTenancy.id);
                                      const hasPdf = statementHasPdf(stmt);
                                      return (
                                        <div className="flex flex-wrap items-center gap-2" data-testid="statement-actions">
                                          <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Written statement</span>
                                          <Button variant="outline" size="sm" onClick={() => {
                                            if (stmt?.status === 'draft' && hasPdf) {
                                              navigate(`/tenancy-agreement?propertyId=${manualTenancy.property_id}&tenancyId=${manualTenancy.id}&id=${stmt.id}&view=1`);
                                            } else {
                                              handleOpenWrittenStatement(manualTenancy.property_id, manualTenancy.id);
                                            }
                                          }}>
                                            <FileText className="h-4 w-4 mr-1.5" />
                                            {getStatementActionLabel(stmt, hasPdf)}
                                          </Button>
                                          {hasPdf && manualTenancy.status !== 'ended' && (
                                            <Button variant="outline" size="sm" onClick={() =>
                                              stmt?.status === 'draft'
                                                ? setEditStatementConfirm({ propertyId: manualTenancy.property_id, tenancyId: manualTenancy.id, statementId: stmt.id })
                                                : setAmendStatementConfirm({ propertyId: manualTenancy.property_id, tenancyId: manualTenancy.id, statementId: stmt.id, status: stmt.status })
                                            }>
                                              <PenLine className="h-4 w-4 mr-1.5" />
                                              Amend
                                            </Button>
                                          )}
                                          {hasPdf && (
                                            <Button variant="outline" size="sm" onClick={() => { setStatementVersionsDialogStmt(stmt); setStatementVersionsDialogEnded(manualTenancy.status === 'ended'); }}>
                                              <Download className="h-4 w-4 mr-1.5" />
                                              PDF &amp; Signing
                                            </Button>
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </TabsContent>

            {/* Issues Tab */}
            <TabsContent value="issues">
              {/* Search and Filters */}
              <Card className="mb-8">
                <CardContent className="p-4">
                  <div className="flex flex-col lg:flex-row gap-4">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search issues..."
                        value={issueSearch}
                        onChange={(e) => setIssueSearch(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearchIssues()}
                        className="pl-10"
                        data-testid="issue-search-input"
                      />
                    </div>
                    <Select value={issueStatusFilter} onValueChange={setIssueStatusFilter}>
                      <SelectTrigger className="w-[150px]">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="reported">Reported</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={issuePriorityFilter} onValueChange={setIssuePriorityFilter}>
                      <SelectTrigger className="w-[150px]">
                        <SelectValue placeholder="Priority" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Priority</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={issuePropertyFilter} onValueChange={setIssuePropertyFilter}>
                      <SelectTrigger className="w-[200px]">
                        <SelectValue placeholder="Property" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Properties</SelectItem>
                        {properties.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button onClick={handleSearchIssues}>
                      <Filter className="h-4 w-4 mr-2" />
                      Apply
                    </Button>
                    <Button onClick={() => setLandlordIssueDialogOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Report Issue
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Issues List */}
              {issues.length > 0 ? (
                <div className="space-y-4">
                  {issues.map(issue => (
                    <Card key={issue.id} className={issue.priority === 'urgent' ? 'border-red-200' : ''}>
                      <CardContent className="p-6">
                        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              {getStatusBadge(issue.status)}
                              {getPriorityBadge(issue.priority)}
                            </div>
                            <h3 className="font-heading font-semibold text-lg">{issue.title}</h3>
                            <p className="text-sm text-muted-foreground mt-1">
                              {issue.property_title || 'General Issue'} • {issue.property_address}
                            </p>
                            <p className="text-sm text-navy-600 mt-1">
                              Reported by: {issue.tenant_name}
                            </p>
                            <p className="text-sm mt-2 text-muted-foreground line-clamp-2">{issue.description}</p>
                            <p className="text-xs text-muted-foreground mt-2">
                              <Calendar className="h-3 w-3 inline mr-1" />
                              {new Date(issue.created_at).toLocaleDateString('en-GB')}
                            </p>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedIssue(issue);
                                setIssueNotes(issue.admin_notes || '');
                                setIssueCost(issue.cost != null ? String(issue.cost) : '');
                                setIssueCostCategory(issue.cost_category || '');
                              }}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View Details
                            </Button>
                            {issue.status !== 'resolved' && (
                              <Select 
                                value={issue.status}
                                onValueChange={(val) => handleUpdateIssue(issue.id, val)}
                              >
                                <SelectTrigger className="w-[140px]">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="reported">Reported</SelectItem>
                                  <SelectItem value="in_progress">In Progress</SelectItem>
                                  <SelectItem value="resolved">Resolved</SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="p-12 text-center">
                  <ClipboardList className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-heading font-semibold text-lg mb-2">No Issues Found</h3>
                  <p className="text-muted-foreground">
                    {dashboardEmptyState
                      ? 'No data yet — this will populate as you start using the platform.'
                      : 'No issues match your search criteria'}
                  </p>
                </Card>
              )}
            </TabsContent>

            {/* Applications Tab */}
            <TabsContent value="applications">
              <div className="space-y-8" id="landlord-applications-section">
                {/* Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
                  <Card>
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex items-center justify-between">
                        <div className="min-w-0">
                          <p className="text-xs sm:text-sm text-gray-600 font-medium">Total</p>
                          <p className="text-xl sm:text-3xl font-bold mt-1">{applications.length}</p>
                        </div>
                        <ClipboardList className="w-8 h-8 sm:w-12 sm:h-12 text-blue-100 flex-shrink-0" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex items-center justify-between">
                        <div className="min-w-0">
                          <p className="text-xs sm:text-sm text-gray-600 font-medium">Pending</p>
                          <p className="text-xl sm:text-3xl font-bold mt-1 text-amber-600">
                            {applications.filter(a => getApplicationDecisionStatus(a) === 'pending').length}
                          </p>
                        </div>
                        <Clock className="w-8 h-8 sm:w-12 sm:h-12 text-amber-100 flex-shrink-0" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex items-center justify-between">
                        <div className="min-w-0">
                          <p className="text-xs sm:text-sm text-gray-600 font-medium">Approved</p>
                          <p className="text-xl sm:text-3xl font-bold mt-1 text-green-600">
                            {applications.filter(a => getApplicationDecisionStatus(a) === 'approved').length}
                          </p>
                        </div>
                        <CheckCircle className="w-8 h-8 sm:w-12 sm:h-12 text-green-100 flex-shrink-0" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex items-center justify-between">
                        <div className="min-w-0">
                          <p className="text-xs sm:text-sm text-gray-600 font-medium">Rejected</p>
                          <p className="text-xl sm:text-3xl font-bold mt-1 text-red-600">
                            {applications.filter(a => getApplicationDecisionStatus(a) === 'rejected').length}
                          </p>
                        </div>
                        <XCircle className="w-8 h-8 sm:w-12 sm:h-12 text-red-100 flex-shrink-0" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Application List */}
                {applications.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center text-muted-foreground">
                      <ClipboardList className="h-10 w-10 mx-auto mb-3 opacity-30" />
                      <p>No applications yet.</p>
                      <p className="text-sm mt-1">When tenants apply for your properties, they will appear here.</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {applications.map((application) => {
                      const applicationDecisionStatus = getApplicationDecisionStatus(application);
                      return (
                        <Card key={application.id}>
                          <CardContent className="p-6">
                            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                  <span className="font-semibold">{application.property_title || application.property_id}</span>
                                  {getApplicationStatusBadge(applicationDecisionStatus)}
                                  {getScreeningStatusBadge(application)}
                                </div>
                                <div className="text-sm text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 mb-2">
                                  {application.tenant_name && (
                                    <span className="flex items-center gap-1">
                                      <Users className="h-3 w-3" /> {application.tenant_name}
                                    </span>
                                  )}
                                  {application.tenant_email && (
                                    <span>{application.tenant_email}</span>
                                  )}
                                  <span className="flex items-center gap-1">
                                    <Calendar className="h-3 w-3" /> {new Date(application.created_at).toLocaleDateString('en-GB')}
                                  </span>
                                </div>
                                {application.message && (
                                  <div className="bg-gray-50 rounded p-4 text-sm text-gray-700 whitespace-pre-wrap">
                                    {application.message}
                                  </div>
                                )}
                                {Array.isArray(application.application_messages) && application.application_messages.length > 0 && (
                                  <div className="mt-2 space-y-2 rounded border bg-slate-50 p-3">
                                    <span className="font-semibold text-xs block text-slate-600">Application conversation</span>
                                    {application.application_messages.map((message) => (
                                      <div key={message.id} className={`rounded p-3 text-sm whitespace-pre-wrap ${message.sender_id === user?.id ? 'bg-blue-50 text-blue-900' : 'bg-white text-slate-700 border'}`}>
                                        <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                          {message.sender_name || (message.sender_id === user?.id ? 'You' : application.tenant_name || 'Applicant')}
                                        </span>
                                        {message.body}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {application.landlord_reply && (!Array.isArray(application.application_messages) || application.application_messages.length === 0) && (
                                  <div className="bg-blue-50 rounded p-4 text-sm text-blue-800 mt-2 whitespace-pre-wrap">
                                    <span className="font-semibold text-xs block mb-1">Your reply:</span>
                                    {application.landlord_reply}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleOpenReplyDialog(application)}
                                >
                                  <Send className="h-4 w-4 mr-1" />
                                  {applicationDecisionStatus === 'pending' ? 'Reply' : 'Reply Again'}
                                </Button>
                                {!application.screening_status && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-purple-700 border-purple-300 hover:bg-purple-50"
                                    onClick={() => handleOpenScreeningDialog(application)}
                                  >
                                    <ClipboardCheck className="h-4 w-4 mr-1" />
                                    Send Pre-Screening
                                  </Button>
                                )}
                                {application.screening_status === 'completed' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-green-700 border-green-300 hover:bg-green-50"
                                    onClick={() => handleOpenScreeningAnswers(application)}
                                  >
                                    <Eye className="h-4 w-4 mr-1" />
                                    View Screening Answers
                                  </Button>
                                )}
                                {(applicationDecisionStatus === 'pending' || applicationDecisionStatus === 'responded') && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-green-700 border-green-300 hover:bg-green-50"
                                      onClick={() => handleApplicationDecision(application.id, 'approved')}
                                    >
                                      <CheckCircle className="h-4 w-4 mr-1" />
                                      Approve
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-red-700 border-red-300 hover:bg-red-50"
                                      onClick={() => handleApplicationDecision(application.id, 'rejected')}
                                    >
                                      <XCircle className="h-4 w-4 mr-1" />
                                      Reject
                                    </Button>
                                  </>
                                )}
                                {applicationDecisionStatus === 'approved' && (
                                  <div className="flex w-full flex-col gap-2 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-900 sm:flex-row sm:items-center sm:justify-between">
                                    <span>Approval recorded. Create a portal tenancy by inviting this applicant, or add them as a manual tenancy for offline occupants.</span>
                                    <div className="flex flex-col gap-2 sm:flex-row">
                                      <Button
                                        size="sm"
                                        className="bg-green-700 hover:bg-green-800"
                                        onClick={() => handleOpenInviteDialog(
                                          properties.find((property) => property.id === application.property_id),
                                          application
                                        )}
                                      >
                                        <MailPlus className="h-4 w-4 mr-1" />
                                        Create Tenancy / Invite Tenant
                                      </Button>
                                    </div>
                                  </div>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-slate-700 border-slate-300 hover:bg-slate-50"
                                  onClick={() => handlePromptApplicationRemoval(application)}
                                >
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  Remove
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Messages Tab */}
            <TabsContent value="messages">
              <MessageCenterContent
                embedded
                initialThreadId={selectedMessageThreadId}
                onUnreadCountChange={setMessageUnreadCount}
              />
            </TabsContent>

            {/* Compliance Tab */}
            <TabsContent value="compliance">
              <div className="space-y-8">
                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-xs sm:text-sm text-gray-600 font-medium">
                            Total Properties
                          </p>
                          <p className="text-2xl sm:text-3xl font-bold mt-1">{complianceStats.total}</p>
                        </div>
                        <Building2 className="w-8 h-8 sm:w-12 sm:h-12 text-blue-100 flex-shrink-0" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-xs sm:text-sm text-gray-600 font-medium">MOT Pass</p>
                          <p className="text-2xl sm:text-3xl font-bold mt-1 text-green-600">
                            {complianceStats.compliant}
                          </p>
                        </div>
                        <CheckCircle className="w-8 h-8 sm:w-12 sm:h-12 text-green-100 flex-shrink-0" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-xs sm:text-sm text-gray-600 font-medium">Warning</p>
                          <p className="text-2xl sm:text-3xl font-bold mt-1 text-yellow-600">
                            {complianceStats.warning}
                          </p>
                        </div>
                        <AlertTriangle className="w-8 h-8 sm:w-12 sm:h-12 text-yellow-100 flex-shrink-0" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-xs sm:text-sm text-gray-600 font-medium">
                            MOT Due
                          </p>
                          <p className="text-2xl sm:text-3xl font-bold mt-1 text-red-600">
                            {complianceStats.nonCompliant}
                          </p>
                        </div>
                        <AlertTriangle className="w-8 h-8 sm:w-12 sm:h-12 text-red-100 flex-shrink-0" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-xs sm:text-sm text-gray-600 font-medium">MOT Disabled</p>
                          <p className="text-2xl sm:text-3xl font-bold mt-1 text-slate-600">
                            {complianceStats.bypassed || 0}
                          </p>
                        </div>
                        <EyeOff className="w-8 h-8 sm:w-12 sm:h-12 text-slate-200 flex-shrink-0" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Alerts */}
                {complianceStats.nonCompliant > 0 && (
                  <div className="flex gap-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0" />
                    <div className="text-sm text-red-800">
                      <strong>{complianceStats.nonCompliant} property/properties</strong> are
                      missing required documents and need immediate attention.
                    </div>
                  </div>
                )}

                {complianceStats.warning > 0 && (
                  <div className="flex gap-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0" />
                    <div className="text-sm text-yellow-800">
                      <strong>{complianceStats.warning} property/properties</strong> have expired
                      MOT documents.
                    </div>
                  </div>
                )}

                {(complianceStats.bypassed || 0) > 0 && (
                  <div className="flex gap-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                    <EyeOff className="w-6 h-6 text-slate-600 flex-shrink-0" />
                    <div className="text-sm text-slate-800">
                      <strong>{complianceStats.bypassed} property/properties</strong> have SIYAK Property MOT disabled and are using document storage only.
                    </div>
                  </div>
                )}

                {complianceStats.compliant === (complianceStats.total - (complianceStats.bypassed || 0)) && (complianceStats.total - (complianceStats.bypassed || 0)) > 0 && (
                  <div className="flex gap-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                    <div className="text-sm text-green-800">
                      <strong>All properties are MOT Pass!</strong> Keep documents
                      updated to maintain MOT status.
                    </div>
                  </div>
                )}

                {/* Properties List */}
                <div className="space-y-4">
                  <h2 className="text-xl font-bold">Properties</h2>

                  {(complianceData.properties || []).length === 0 ? (
                    <Card>
                      <CardContent className="flex flex-col items-center justify-center py-12">
                        <Building2 className="w-12 h-12 text-gray-400 mb-4" />
                        <p className="text-gray-600">
                          No properties found. Add properties to track MOT status.
                        </p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="grid gap-4">
                      {(complianceData.properties || []).map((propertyData) => {
                        const property = properties.find(
                          (p) => p.id === propertyData.property_id
                        );

                        if (!property) return null;

                        const missingCount =
                          propertyData.missing_documents?.length ||
                          propertyData.missing_required_documents?.length ||
                          0;
                        const checklistMissingCount = Array.isArray(propertyData.failed_rules)
                          ? propertyData.failed_rules.filter((rule) => rule?.requires_checkbox_declaration).length
                          : 0;
                        const totalMissingItems = Math.max(missingCount, missingCount + checklistMissingCount);
                        const expiredCount =
                          propertyData.expired_documents?.length ||
                          propertyData.expired_compliance_documents?.length ||
                          0;
                        const motStatus = (propertyData.mot_status || '').toLowerCase();
                        const isMotBypassed = motStatus === 'bypassed' || Boolean(propertyData.mot_bypassed);

                        const status = motStatus || (
                          propertyData.regulatory_compliant
                            ? 'compliant'
                            : expiredCount > 0 && missingCount === 0
                              ? 'warning'
                              : 'non-compliant'
                        );

                        return (
                          <Card
                            key={propertyData.property_id}
                            className="hover:shadow-md transition-shadow cursor-pointer"
                            onClick={() =>
                              navigate(
                                `/property/${propertyData.property_id}/documents`
                              )
                            }
                          >
                            <CardContent className="pt-4 sm:pt-6">
                              {/* Header: Title and Badge/Button */}
                              <div className="flex items-start justify-between gap-2 mb-3 sm:mb-4">
                                <div className="flex-1 min-w-0">
                                  <h3 className="text-base sm:text-lg font-semibold line-clamp-2">
                                    {property.title}
                                  </h3>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                  <ComplianceBadge
                                    status={status}
                                    missingCount={totalMissingItems}
                                    expiredCount={expiredCount}
                                    expiringSoonCount={
                                      propertyData.expiring_documents?.length || 0
                                    }
                                  />
                                </div>
                              </div>

                              {/* Address */}
                              <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4 line-clamp-2">
                                {property.address}
                              </p>

                              {/* Issues Summary - Responsive Grid */}
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mb-3 sm:mb-4">
                                {isMotBypassed && (
                                  <div className="flex items-center gap-2 text-xs sm:text-sm p-2 bg-slate-50 rounded border border-slate-200 sm:col-span-2">
                                    <EyeOff className="w-4 h-4 text-slate-600 flex-shrink-0" />
                                    <span className="text-slate-700 font-semibold">
                                      SIYAK MOT disabled — using document storage only
                                    </span>
                                  </div>
                                )}
                                {missingCount > 0 && (
                                  <div className="flex items-center gap-2 text-xs sm:text-sm p-2 bg-red-50 rounded border border-red-100">
                                    <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
                                    <span className="text-red-700">
                                      <span className="font-semibold">
                                        {getMissingBreakdownText(propertyData, missingCount)}
                                      </span>
                                    </span>
                                  </div>
                                )}

                                {expiredCount > 0 && (
                                  <div className="flex items-center gap-2 text-xs sm:text-sm p-2 bg-yellow-50 rounded border border-yellow-100">
                                    <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0" />
                                    <span className="text-yellow-700">
                                      <span className="font-semibold">
                                        {expiredCount}
                                      </span>
                                      {' '}expired
                                    </span>
                                  </div>
                                )}

                                {(propertyData.expiring_documents
                                  ?.length || 0) > 0 && (
                                  <div className="flex items-center gap-2 text-xs sm:text-sm p-2 bg-orange-50 rounded border border-orange-100">
                                    <Clock className="w-4 h-4 text-orange-500 flex-shrink-0" />
                                    <span className="text-orange-600">
                                      <span className="font-semibold">
                                        {propertyData.expiring_documents.length}
                                      </span>
                                      {' '}expiring
                                    </span>
                                  </div>
                                )}

                                {!isMotBypassed && propertyData.regulatory_compliant && (
                                  <div className="flex items-center gap-2 text-xs sm:text-sm p-2 bg-green-50 rounded border border-green-100">
                                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                                    <span className="text-green-700 font-semibold">
                                      MOT Pass
                                    </span>
                                  </div>
                                )}


                              </div>

                              {/* Tenancy Start Dates */}
                              {!isMotBypassed && propertyData.tenancies?.length > 0 && (
                                <div
                                  className="mt-3 sm:mt-4 p-3 sm:p-4 rounded-lg border border-gray-200 bg-gray-50"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <h4 className="text-xs sm:text-sm font-semibold text-gray-900 mb-2 sm:mb-3">Tenancies</h4>
                                  <div className="space-y-2 sm:space-y-3">
                                    {propertyData.tenancies.map((tenancy) => (
                                      <div key={tenancy.tenancy_id} className="flex items-center justify-between text-xs sm:text-sm gap-2">
                                        <span className="font-medium text-gray-800 min-w-0 truncate">{getTenancyLabel(tenancy)}</span>
                                        {tenancy.start_date_known ? (
                                          <span className="text-gray-500 text-xs flex-shrink-0">
                                              {formatDateForDisplay(tenancy.start_date) || 'Start date unavailable'}
                                          </span>
                                        ) : (
                                          <input
                                            type="date"
                                            placeholder="Set start date"
                                            className="text-xs border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 flex-shrink-0"
                                            onChange={(e) => {
                                              if (e.target.value) {
                                                handleMotTenancyStartDate(tenancy.tenancy_id, e.target.value);
                                              }
                                            }}
                                          />
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Action Button - Full Width on Mobile */}
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full mt-3 sm:mt-4"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(
                                    `/property/${propertyData.property_id}/documents`
                                  );
                                }}
                              >
                                View Docs
                                <ArrowRight className="w-4 h-4 ml-2" />
                              </Button>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Rent Tab */}
            <TabsContent value="rent">
              <div className="space-y-8">
                {rentUpgradeHints.length > 0 && (
                  <div className="space-y-3">
                    {rentUpgradeHints.map((hint) => (
                      <UpgradeHintCard
                        key={`${hint.id}-${dismissedHintVersion}`}
                        message={hint.message}
                        ctaLabel={hint.ctaLabel}
                        compact
                        onDismiss={() => handleDismissHint(hint.id)}
                      />
                    ))}
                  </div>
                )}

                {/* Overview Stats */}
                {rentOverview && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card>
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm text-gray-600 font-medium">Due This Month</p>
                            <p className="text-2xl font-bold mt-2">{formatCurrencyStat(rentOverview.pending_amount)}</p>
                            <p className="text-xs text-muted-foreground mt-1">{rentOverview.pending_count ?? 0} schedule{(rentOverview.pending_count ?? 0) !== 1 ? 's' : ''}</p>
                          </div>
                          <Receipt className="w-10 h-10 text-blue-100 shrink-0" />
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm text-gray-600 font-medium">Due</p>
                            <p className="text-2xl font-bold mt-2 text-blue-600">{rentOverview.due}</p>
                          </div>
                          <Clock className="w-10 h-10 text-blue-100 shrink-0" />
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm text-gray-600 font-medium">Overdue</p>
                            <p className="text-2xl font-bold mt-2 text-red-600">{rentOverview.overdue}</p>
                          </div>
                          <AlertTriangle className="w-10 h-10 text-red-100 shrink-0" />
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm text-gray-600 font-medium">Monthly Revenue</p>
                            <p className="text-2xl font-bold mt-2">{formatCurrencyStat(rentOverview.monthly_revenue)}</p>
                          </div>
                          <PoundSterling className="w-10 h-10 text-green-100 shrink-0" />
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Action Bar */}
                <div className="flex flex-wrap items-center justify-between gap-2 py-4">
                  <Button onClick={() => handleAddRentSchedule()} size="sm" disabled={submitting}>
                    <Plus className="h-4 w-4 mr-1" /> Add Rent Schedule
                  </Button>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-muted-foreground"
                      onClick={() => setReminderRulesOpen(true)}
                    >
                      <Info className="h-4 w-4 mr-1" /> Reminder rules
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSendReminders}
                      disabled={sendingReminders}
                    >
                      {sendingReminders ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-1" />
                      ) : (
                        <Bell className="h-4 w-4 mr-1" />
                      )}
                      Send Reminders
                    </Button>
                  </div>
                </div>

                {/* Rent Schedules */}
                {rentSchedules.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center text-muted-foreground">
                      <Receipt className="h-10 w-10 mx-auto mb-3 opacity-30" />
                      <p>No rent schedules yet.</p>
                      <p className="text-sm mt-1">Add a schedule from the Properties tab or the button above.</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {rentSchedules.map((rent) => (
                      <Card key={rent.id}>
                        <CardContent className="p-6">
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold truncate">{rent.property_title || rent.property_id}</span>
                                {getRentStatusBadge(rent.status, rent)}
                              </div>
                              <div className="text-sm text-muted-foreground mt-1 flex flex-wrap gap-x-4 gap-y-1">
                                {rent.tenant_name && (
                                  <span className="flex items-center gap-1">
                                    <Users className="h-3 w-3" /> {rent.tenant_name}
                                    {rent.tenant_source === 'manual' && (
                                      <span className="ml-1 text-xs text-muted-foreground">(Manual)</span>
                                    )}
                                  </span>
                                )}
                                <span className="flex items-center gap-1">
                                  <PoundSterling className="h-3 w-3" /> {Number(rent.rent_amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-3 w-3" /> Due day {rent.due_day_of_month}
                                </span>
                                {rent.next_due_date && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" /> Next: {new Date(rent.next_due_date).toLocaleDateString('en-GB')}
                                  </span>
                                )}
                                {rent.paid_date && (
                                  <span className="flex items-center gap-1">
                                    <CheckCircle className="h-3 w-3 text-green-500" /> Paid: {new Date(rent.paid_date).toLocaleDateString('en-GB')}
                                  </span>
                                )}
                                {rent.status === 'partially_paid' && rent.balance_remaining > 0 && (
                                  <span className="flex items-center gap-1 text-amber-600 font-medium">
                                    £{rent.balance_remaining.toFixed(2)} remaining
                                  </span>
                                )}
                                {rent.status !== 'paid' && rent.status !== 'partially_paid' && rent.planned_payment_amount != null && rent.planned_payment_amount > 0 && (
                                  <span className="flex items-center gap-1 text-amber-600 font-medium">
                                    Planned partial payment: £{rent.planned_payment_amount.toFixed(2)}
                                  </span>
                                )}
                                {rent.payment_method && (
                                  <span className="flex items-center gap-1 text-muted-foreground">
                                    💳 {getPaymentMethodLabel(rent.payment_method)}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              {rent.status !== 'paid' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="w-full sm:w-auto"
                                  onClick={() => handleOpenReceiptPreview(rent)}
                                  title="Preview the receipt before sending"
                                >
                                  <Eye className="h-4 w-4 mr-1" /> Preview Draft
                                </Button>
                              )}
                              {rent.status !== 'paid' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="w-full sm:w-auto"
                                  onClick={() => handleSendRentReminder(rent)}
                                  disabled={sendingReminderIds.includes(rent.id)}
                                >
                                  {sendingReminderIds.includes(rent.id) ? (
                                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                  ) : (
                                    <Bell className="h-4 w-4 mr-1" />
                                  )}
                                  Send Reminder
                                </Button>
                              )}
                              {/* Edit button - hidden once fully paid; still available while partially
                                  paid so the next installment amount/date preset can be adjusted */}
                              {rent.status !== 'paid' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openEditRentDialog(rent)}
                                >
                                  <Edit className="h-4 w-4 mr-1" /> Edit
                                </Button>
                              )}

                              {/* Receipt actions (Download/Resend/Log to Finance) — available for
                                  paid AND partially_paid, since a partially-paid schedule already has
                                  a receipt for the payment(s) recorded so far. */}
                              {(rent.status === 'paid' || rent.status === 'partially_paid') && (
                                <>
                                  {Array.isArray(rent.receipts) && rent.receipts.length > 1 ? (
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button size="sm" variant="outline">
                                          <FileText className="h-4 w-4 mr-1" /> Receipts ({rent.receipts.length})
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        <DropdownMenuLabel>Payment receipts</DropdownMenuLabel>
                                        <DropdownMenuSeparator />
                                        {rent.receipts.map((receipt) => (
                                          <DropdownMenuItem
                                            key={receipt.history_id || receipt.receipt_reference}
                                            onClick={() => handleViewReceipt(receipt.receipt_url)}
                                          >
                                            <FileText className="h-4 w-4 mr-2" />
                                            {receipt.receipt_reference}
                                            {receipt.paid_date ? ` · ${new Date(receipt.paid_date).toLocaleDateString('en-GB')}` : ''}
                                            {typeof receipt.amount === 'number' ? ` · £${receipt.amount.toFixed(2)}` : ''}
                                          </DropdownMenuItem>
                                        ))}
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  ) : (
                                    rent.receipt_url && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleViewReceipt(rent.receipt_url)}
                                      >
                                        <FileText className="h-4 w-4 mr-1" /> Download Receipt
                                      </Button>
                                    )
                                  )}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleForwardReceipt(rent.id, rent.tenant_name, rent.tenant_source)}
                                  >
                                    <Send className="h-4 w-4 mr-1" />
                                    Resend Receipt
                                  </Button>
                                  {/* Unmark button - only for admins, only when fully paid */}
                                  {user?.role === 'admin' && rent.status === 'paid' && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => openUnmarkPaidConfirm(rent.id)}
                                    >
                                      <X className="h-4 w-4 mr-1" /> Unmark
                                    </Button>
                                  )}
                                  {/* Log to Finance */}
                                  {!financeLoggedRentIds.has(rent.id) && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      disabled={loggingFinanceRentId === rent.id}
                                      onClick={() => handleLogToFinance(rent)}
                                      title="Create a Finance entry for this rent payment"
                                    >
                                      {loggingFinanceRentId === rent.id
                                        ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                        : <BarChart2 className="h-4 w-4 mr-1" />}
                                      Log to Finance
                                    </Button>
                                  )}
                                </>
                              )}

                              {rent.status !== 'paid' ? (
                                (skipMarkPaidConfirmation || markPaidCountdownArmedRentId === rent.id) ? (
                                <CountdownButton
                                  onConfirm={async () => {
                                    if (payingIds.includes(rent.id)) return;
                                    const selectedPaidDate = paymentDate || new Date().toISOString().slice(0, 10);
                                    const previousStatus = rent.status;
                                    const previousAmountPaid = rent.amount_paid || 0;
                                    
                                    setPayingIds(prev => [...prev, rent.id]);

                                    // Build payload: explicit paymentAmount (used only for additional
                                    // installments on already partially-paid schedules) takes priority,
                                    // then the preset planned amount (from Edit Rent Schedule), else the
                                    // full remaining balance.
                                    const payload = { paid_date: selectedPaidDate };
                                    if (paymentAmount && parseFloat(paymentAmount) > 0) {
                                      payload.amount = parseFloat(paymentAmount);
                                    } else if (rent.planned_payment_amount > 0) {
                                      payload.amount = rent.planned_payment_amount;
                                    }

                                    // Optimistic UI update
                                    setRentSchedules(prev => prev.map(r => r.id === rent.id ? { ...r, status: 'paid', paid_date: selectedPaidDate } : r));

                                    try {
                                      const response = await axios.post(`${API_URL}/api/rent/${rent.id}/pay`, payload);
                                      // Store all response data
                                      if (response.data) {
                                        setRentSchedules(prev => prev.map(r => 
                                          r.id === rent.id 
                                            ? { ...r, ...response.data }
                                            : r
                                        ));
                                      }
                                      const successMsg = response.data?.status === 'partially_paid'
                                        ? `Payment of £${(response.data.amount_paid - previousAmountPaid).toFixed(2)} recorded — £${response.data.balance_remaining?.toFixed(2)} remaining`
                                        : 'Rent marked as paid — receipt sent to tenant';
                                      toast.success(successMsg);
                                      // Refresh to pick up newly created next-month schedule
                                      fetchData();
                                    } catch (error) {
                                      // Revert optimistic update on failure
                                      setRentSchedules(prev => prev.map(r => r.id === rent.id ? { ...r, status: previousStatus, paid_date: null, receipt_url: null, receipt_reference: null, receipts: [], amount_paid: rent.amount_paid || 0, balance_remaining: rent.balance_remaining || rent.rent_amount || 0 } : r));
                                      toast.error(error.response?.data?.detail || 'Failed to record payment');
                                    } finally {
                                      setPayingIds(prev => prev.filter(id => id !== rent.id));
                                      setMarkPaidCountdownArmedRentId(prev => (prev === rent.id ? null : prev));
                                      setPaymentAmount('');
                                    }
                                  }}
                                  autoStart={markPaidCountdownArmedRentId === rent.id}
                                  disabled={payingIds.includes(rent.id)}
                                  countdownSeconds={20}
                                  cancelText="Cancel"
                                  confirmText="Marking Paid..."
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  <span className="flex flex-col items-start leading-tight">
                                    <span>{rent.status === 'partially_paid' ? 'Record Payment' : 'Mark Paid'}</span>
                                    <span className="text-[10px] font-normal opacity-80">{rentPaymentDatePresets[rent.id] || 'Today'}</span>
                                  </span>
                                </CountdownButton>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="default"
                                    onClick={() => handleRequestMarkPaid(rent.id)}
                                    disabled={payingIds.includes(rent.id)}
                                  >
                                    <CheckCircle className="h-4 w-4 mr-1" />
                                    {rent.status === 'partially_paid' ? 'Record Payment' : 'Mark Paid'}
                                  </Button>
                                )
                              ) : null}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-500 hover:text-red-700"
                                onClick={() => openDeleteRentConfirm(rent.id)}
                                disabled={rent.status === 'paid' || rent.status === 'partially_paid'}
                                title={rent.status === 'paid' || rent.status === 'partially_paid' ? 'Rent schedules with payments cannot be deleted' : 'Delete rent schedule'}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Export Tab */}
            <TabsContent value="export">
              <div className="grid gap-6 md:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ClipboardList className="h-5 w-5" />
                      Export Issues
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Download all your issues as a CSV or JSON file for backup or reporting.
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <Button
                        variant="outline"
                        onClick={() => window.open(`${API_URL}/api/export/issues?format=csv`, '_blank')}
                        data-testid="export-issues-csv"
                      >
                        Download CSV
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => window.open(`${API_URL}/api/export/issues?format=json`, '_blank')}
                        data-testid="export-issues-json"
                      >
                        Download JSON
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <History className="h-5 w-5" />
                      Maintenance History
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Export full maintenance history for a specific property.
                    </p>
                    <Select 
                      value={selectedPropertyTimeline || ''} 
                      onValueChange={(val) => setSelectedPropertyTimeline(val)}
                    >
                      <SelectTrigger data-testid="export-property-select">
                        <SelectValue placeholder="Select a property" />
                      </SelectTrigger>
                      <SelectContent>
                        {properties.map(prop => (
                          <SelectItem key={prop.id} value={prop.id}>{prop.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex flex-wrap gap-3">
                      <Button
                        variant="outline"
                        disabled={!selectedPropertyTimeline}
                        onClick={() => selectedPropertyTimeline && window.open(`${API_URL}/api/export/maintenance-history/${selectedPropertyTimeline}?format=csv`, '_blank')}
                        data-testid="export-history-csv"
                      >
                        Download CSV
                      </Button>
                      <Button 
                        variant="outline"
                        disabled={!selectedPropertyTimeline}
                        onClick={() => selectedPropertyTimeline && window.open(`${API_URL}/api/export/maintenance-history/${selectedPropertyTimeline}?format=json`, '_blank')}
                        data-testid="export-history-json"
                      >
                        Download JSON
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <PoundSterling className="h-5 w-5" />
                      Export Rent Records
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Download rent schedules and payment history for your records.
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <Button
                        variant="outline"
                        onClick={() => window.open(`${API_URL}/api/export/rent?format=csv`, '_blank')}
                        data-testid="export-rent-csv"
                      >
                        Download CSV
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => window.open(`${API_URL}/api/export/rent?format=json`, '_blank')}
                        data-testid="export-rent-json"
                      >
                        Download JSON
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Download className="h-5 w-5" />
                      Finance Export Data ({isCompany ? 'Accounting Period' : 'Tax Year'} {currentFinanceYear})
                    </CardTitle>
                    <CardDescription>
                      {isCompany
                        ? 'Download cashflow transactions for your company accounting period.'
                        : 'Download cashflow transactions or SA105 tax summary for the current tax year.'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Cashflow Transactions</Label>
                      <p className="text-xs text-muted-foreground">
                        All transactions grouped by quarter for {isCompany ? 'accounting period' : 'tax year'} {currentFinanceYear}.
                      </p>
                      <Button variant="outline" onClick={() => {
                        const params = new URLSearchParams({ year: currentFinanceYear, grouping: 'quarterly' });
                        window.open(`${API_URL}/api/cashflow/export?${params.toString()}`, '_blank');
                      }}>
                        <Download className="mr-2 h-4 w-4" />Download Cashflow CSV
                      </Button>
                    </div>
                    {!isCompany && (
                      <>
                        <div className="border-t pt-2" />
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">SA105 Tax Summary</Label>
                          <p className="text-xs text-muted-foreground">Income and expenses by SA105 categories for tax year {currentFinanceYear}.</p>
                          <Button variant="outline" onClick={() => {
                            const params = new URLSearchParams({ year: currentFinanceYear, grouping: 'quarterly' });
                            window.open(`${API_URL}/api/cashflow/sa105-summary/csv?${params.toString()}`, '_blank');
                          }}>
                            <FileSpreadsheet className="mr-2 h-4 w-4" />Download SA105 CSV
                          </Button>
                        </div>
                      </>
                    )}
                    {isCompany && (
                      <>
                        <div className="border-t pt-2" />
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">CT600 Tax Summary</Label>
                          <p className="text-xs text-muted-foreground">Income and expenses by CT600 categories for accounting period {currentFinanceYear}.</p>
                          <Button variant="outline" onClick={() => {
                            const params = new URLSearchParams({ year: currentFinanceYear, grouping: 'quarterly' });
                            window.open(`${API_URL}/api/cashflow/tax-summary/csv?${params.toString()}`, '_blank');
                          }}>
                            <FileSpreadsheet className="mr-2 h-4 w-4" />Download CT600 CSV
                          </Button>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Export Document Delivery Records
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      Download your document delivery history, including recipients and acknowledgement status.
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <Button
                        variant="outline"
                        onClick={() => window.open(`${API_URL}/api/landlord/document-delivery/export?format=csv`, '_blank')}
                        data-testid="export-document-delivery-csv"
                      >
                        Download CSV
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => window.open(`${API_URL}/api/landlord/document-delivery/export?format=json`, '_blank')}
                        data-testid="export-document-delivery-json"
                      >
                        Download JSON
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="h-5 w-5" />
                      Your Data is Safe
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      All your property and issue data is securely stored and can be exported at any time.
                      Regular exports are recommended for your records.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Finances Tab */}
            <TabsContent value="finances">
              <FinancePanel properties={properties} isActive={activeTab === 'finances'} />
            </TabsContent>

            {/* Document Deliveries Tab */}
            <TabsContent value="document-deliveries">
              <LandlordDocumentDeliveryTab />
            </TabsContent>

          </Tabs>
          </div>
        </div>

        {/* Issue Detail Modal */}
        <Dialog open={!!selectedIssue} onOpenChange={() => setSelectedIssue(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedIssue?.title}</DialogTitle>
              <DialogDescription>
                Reported by {selectedIssue?.tenant_name} ({selectedIssue?.tenant_email})
              </DialogDescription>
            </DialogHeader>
            
            {selectedIssue && (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {getStatusBadge(selectedIssue.status)}
                  {getPriorityBadge(selectedIssue.priority)}
                </div>
                
                {selectedIssue.property_title && (
                  <div className="text-sm">
                    <span className="text-muted-foreground">Property:</span>{' '}
                    <span className="font-medium">{selectedIssue.property_title}</span>
                    <span className="text-muted-foreground"> - {selectedIssue.property_address}</span>
                  </div>
                )}
                
                <div className="p-4 bg-navy-50 rounded-lg">
                  <p className="text-sm whitespace-pre-wrap">{selectedIssue.description}</p>
                </div>
                
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Update Status</label>
                    <Select 
                      value={selectedIssue.status}
                      onValueChange={(val) => handleUpdateIssue(selectedIssue.id, val)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="reported">Reported</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Update Priority</label>
                    <Select 
                      value={selectedIssue.priority}
                      onValueChange={(val) => handleUpdateIssue(selectedIssue.id, selectedIssue.status, null)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium">Notes (visible to tenant)</label>
                  <Textarea
                    placeholder="Add notes about this issue..."
                    value={issueNotes}
                    onChange={(e) => setIssueNotes(e.target.value)}
                    rows={3}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleUpdateIssue(selectedIssue.id, selectedIssue.status, issueNotes)}
                    disabled={updatingIssue}
                  >
                    {updatingIssue ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                    Save Notes
                  </Button>
                </div>

                {/* Cost tracking (landlord only — not visible to tenants) */}
                <div className="space-y-2 border-t pt-3">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <PoundSterling className="h-4 w-4 text-muted-foreground" /> Cost Tracking (Finances)
                  </label>
                  <p className="text-xs text-muted-foreground">Record the cost of this issue. It will appear as an expense in your Finances tab.</p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="space-y-1">
                      <label className="text-xs font-medium">Amount (£)</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">£</span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          className="pl-7"
                          value={issueCost}
                          onChange={e => setIssueCost(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-medium">Expense Category</label>
                      <Select value={issueCostCategory} onValueChange={setIssueCostCategory}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {(isCompany ? CT600_EXPENSE_CATEGORIES : SA105_EXPENSE_CATEGORIES).map(c => (
                            <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleSaveIssueCost}
                    disabled={savingIssueCost}
                  >
                    {savingIssueCost ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PoundSterling className="h-4 w-4 mr-2" />}
                    Save Cost
                  </Button>
                  {selectedIssue.cost != null && (
                    <p className="text-xs text-green-700 font-medium">
                      Current cost: £{Number(selectedIssue.cost).toFixed(2)} · {(isCompany ? CT600_CATEGORY_LABEL_MAP : SA105_CATEGORY_LABEL_MAP)[selectedIssue.cost_category] || selectedIssue.cost_category || 'No category'}
                    </p>
                  )}
                </div>

                <div className="text-xs text-muted-foreground">
                  Created: {new Date(selectedIssue.created_at).toLocaleString('en-GB')}
                  {selectedIssue.updated_at !== selectedIssue.created_at && (
                    <> • Updated: {new Date(selectedIssue.updated_at).toLocaleString('en-GB')}</>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Property Timeline Modal */}
        <Dialog open={!!selectedPropertyTimeline} onOpenChange={() => setSelectedPropertyTimeline(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Property Timeline</DialogTitle>
              <DialogDescription>
                {selectedPropertyTimeline?.title} - {selectedPropertyTimeline?.address}
              </DialogDescription>
            </DialogHeader>
            
            {loadingTimeline ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-royal-600" />
              </div>
            ) : timeline.length > 0 ? (
              <div className="space-y-4">
                {timeline.map(event => (
                  <div key={event.id} className="flex gap-4">
                    <div className="flex-shrink-0 mt-1">
                      {getTimelineIcon(event.event_type)}
                    </div>
                    <div className="flex-1 pb-4 border-b last:border-0">
                      <h4 className="font-medium text-sm">{event.title}</h4>
                      <p className="text-sm text-muted-foreground">{event.description}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(event.created_at).toLocaleString('en-GB')}
                        {event.user_name && <> • by {event.user_name}</>}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <History className="h-8 w-8 mx-auto mb-2" />
                No events recorded yet
              </div>
            )}
          </DialogContent>
        </Dialog>



        {/* Tenant Interest Popup */}
        <Dialog open={interestPopupOpen} onOpenChange={setInterestPopupOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Tenant Interest</DialogTitle>
              <DialogDescription>
                {selectedPropertyInterest?.property?.title}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Saved Count */}
              {selectedPropertyInterest?.saves?.length > 0 && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-medium text-sm flex items-center gap-2 mb-3">
                    <Heart className="h-4 w-4 text-red-500" />
                    Saved by {selectedPropertyInterest.saves.length} {selectedPropertyInterest.saves.length === 1 ? 'person' : 'people'}
                  </h4>
                  <div className="space-y-2">
                    {selectedPropertyInterest.saves.map(save => (
                      <div key={save.id} className="flex items-start justify-between p-2 bg-white rounded border">
                        <div className="flex-1">
                          <p className="font-sm font-medium">{save.tenant_name}</p>
                          <p className="text-xs text-muted-foreground">{save.tenant_email}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(save.created_at).toLocaleDateString('en-GB')}
                          </p>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="ml-3"
                          onClick={() => handlePromptInterestRemoval(save)}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Transfer Requests */}
              {selectedPropertyInterest?.transfers?.length > 0 && (
                <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <h4 className="font-medium text-sm flex items-center gap-2 mb-3">
                    <MapPin className="h-4 w-4 text-orange-500" />
                    Move Intent Requests: {selectedPropertyInterest.transfers.length}
                  </h4>
                  <div className="space-y-2">
                    {selectedPropertyInterest.transfers.map(req => (
                      <div key={req.id} className="flex items-start justify-between p-2 bg-white rounded border">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-semibold px-2 py-0.5 rounded bg-orange-200 text-orange-800 capitalize">
                              {req.status}
                            </span>
                          </div>
                          <p className="font-sm font-medium">{req.tenant_name}</p>
                          <p className="text-xs text-muted-foreground">{req.tenant_email}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(req.created_at).toLocaleDateString('en-GB')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedPropertyInterest?.totalInterest === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No tenant interest signals yet
                </p>
              )}
            </div>
          </DialogContent>
        </Dialog>

        <AlertDialog
          open={Boolean(pendingInterestRemoval)}
          onOpenChange={(open) => {
            if (!open && !removingInterest) {
              setPendingInterestRemoval(null);
            }
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove tenant interest?</AlertDialogTitle>
              <AlertDialogDescription>
                Remove this tenant from your interested list? This does not affect their saved property.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={removingInterest}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmInterestRemoval} disabled={removingInterest}>
                {removingInterest ? 'Removing...' : 'Remove'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog
          open={Boolean(pendingApplicationRemoval)}
          onOpenChange={(open) => {
            if (!open && !withdrawingApplication) {
              setPendingApplicationRemoval(null);
            }
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove application?</AlertDialogTitle>
              <AlertDialogDescription>
                Remove this application from your list?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={withdrawingApplication}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmApplicationRemoval} disabled={withdrawingApplication}>
                {withdrawingApplication ? 'Removing...' : 'Remove'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Reply Dialog */}
        <Dialog open={replyOpen} onOpenChange={setReplyOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Reply to Application</DialogTitle>
              <DialogDescription>
                {replyNotification?.tenant_name && (
                  <div className="mt-2">
                    <p className="text-sm font-semibold">{replyNotification.tenant_name}</p>
                    <p className="text-xs text-muted-foreground">{replyNotification.tenant_email}</p>
                  </div>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              {/* Tenant's Application Message */}
              {replyNotification?.message && (
                <div className="bg-green-50 border border-green-200 rounded-md p-4">
                  <p className="text-xs font-semibold text-green-900 mb-2">Tenant's Application Message</p>
                  <p className="text-sm text-green-900 whitespace-pre-wrap">{replyNotification.message}</p>
                </div>
              )}
              {Array.isArray(replyNotification?.application_messages) && replyNotification.application_messages.length > 0 && (
                <div className="space-y-2 rounded-md border p-4">
                  <p className="text-xs font-semibold text-slate-700">Application conversation</p>
                  {replyNotification.application_messages.map((message) => (
                    <div key={message.id} className={`rounded-md p-3 text-sm whitespace-pre-wrap ${message.sender_id === user?.id ? 'bg-blue-50 text-blue-900' : 'bg-slate-50 text-slate-700'}`}>
                      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        {message.sender_name || (message.sender_id === user?.id ? 'You' : replyNotification.tenant_name || 'Applicant')}
                      </p>
                      <p>{message.body}</p>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Landlord's Reply */}
              <div>
                <Label htmlFor="reply-message">Your Reply *</Label>
                <Textarea
                  id="reply-message"
                  placeholder="Type your message..."
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  className="min-h-[120px]"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  💡 Your reply has been pre-filled with your contact information. Feel free to customize it.
                </p>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setReplyOpen(false)} disabled={replySending}>
                  Cancel
                </Button>
                <Button onClick={handleSendReply} disabled={replySending || !replyMessage.trim()}>
                  {replySending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send Reply
                    </>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Screening Question Selection Dialog */}
        <Dialog open={screeningDialogOpen} onOpenChange={(open) => { if (!screeningSending) { setScreeningDialogOpen(open); if (!open) { setScreeningApplication(null); setSelectedQuestionIds(new Set()); } } }}>
          <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ClipboardCheck className="h-5 w-5 text-purple-600" />
                Send Pre-Screening Questionnaire
              </DialogTitle>
              <DialogDescription>
                {screeningApplication && (
                  <span>Select questions to send to <strong>{screeningApplication.tenant_name || 'the tenant'}</strong> for <strong>{screeningApplication.property_title || 'this property'}</strong>.</span>
                )}
              </DialogDescription>
            </DialogHeader>
            {screeningQuestionsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between pb-2 border-b">
                  <span className="text-sm font-medium text-muted-foreground">
                    {selectedQuestionIds.size} of {screeningQuestions.length} selected
                  </span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSelectedQuestionIds(new Set(screeningQuestions.map(q => q.id)))}
                    >
                      Select All
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSelectedQuestionIds(new Set())}
                    >
                      Clear All
                    </Button>
                  </div>
                </div>
                <div className="overflow-y-auto flex-1 space-y-2 py-2 pr-1">
                  {screeningQuestions.map((q, idx) => {
                    const isHidden = q.conditional_property_field === 'furnishing' &&
                      !screeningApplication?.furnishing;
                    if (isHidden) return null;
                    const checked = selectedQuestionIds.has(q.id);
                    return (
                      <label
                        key={q.id}
                        className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${checked ? 'bg-purple-50 border-purple-300' : 'border-gray-200 hover:bg-gray-50'}`}
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5 accent-purple-600"
                          checked={checked}
                          onChange={(e) => {
                            setSelectedQuestionIds(prev => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(q.id);
                              else next.delete(q.id);
                              return next;
                            });
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <span className="text-xs text-muted-foreground font-mono mr-2">{idx + 1}.</span>
                          <span className="text-sm">{q.text}</span>
                          {q.follow_up && (
                            <p className="text-xs text-muted-foreground mt-1 italic">Follow-up if applicable: {q.follow_up}</p>
                          )}
                          <Badge variant="outline" className="mt-1 text-xs capitalize">{q.answer_type.replace('_', '/')}</Badge>
                        </div>
                      </label>
                    );
                  })}
                </div>
                <div className="flex justify-end gap-2 pt-3 border-t">
                  <Button variant="outline" onClick={() => setScreeningDialogOpen(false)} disabled={screeningSending}>
                    Cancel
                  </Button>
                  <Button
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    onClick={handleSendScreening}
                    disabled={selectedQuestionIds.size === 0 || screeningSending}
                  >
                    {screeningSending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <ClipboardCheck className="h-4 w-4 mr-2" />
                        Send Questionnaire ({selectedQuestionIds.size})
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Screening Answers Dialog */}
        <Dialog open={screeningAnswersDialogOpen} onOpenChange={setScreeningAnswersDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ClipboardCheck className="h-5 w-5 text-green-600" />
                Screening Answers
              </DialogTitle>
              <DialogDescription>
                {screeningAnswersApplication && (
                  <span>Pre-screening responses from <strong>{screeningAnswersApplication.tenant_name || 'the tenant'}</strong></span>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="overflow-y-auto flex-1 space-y-4 py-2">
              {(screeningAnswersApplication?.screening_questions || []).map((q) => {
                const answerObj = (screeningAnswersApplication?.screening_answers || []).find(a => a.question_id === q.question_id);
                const answer = answerObj?.answer;
                const followUpAnswer = answerObj?.follow_up_answer;

                let answerDisplay;
                if (q.answer_type === 'yes_no') {
                  const isYes = answer === 'yes' || answer === 'Yes' || answer === true;
                  const isNo = answer === 'no' || answer === 'No' || answer === false;
                  answerDisplay = (
                    <Badge className={isYes ? 'bg-green-100 text-green-700' : isNo ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}>
                      {isYes ? 'Yes' : isNo ? 'No' : answer || '—'}
                    </Badge>
                  );
                } else if (q.answer_type === 'date') {
                  let formatted = answer || '—';
                  if (answer) {
                    try {
                      const d = new Date(answer);
                      formatted = d.toLocaleDateString('en-GB');
                    } catch { /* keep raw */ }
                  }
                  answerDisplay = <span className="text-sm">{formatted}</span>;
                } else {
                  answerDisplay = <span className="text-sm whitespace-pre-wrap">{answer || '—'}</span>;
                }

                return (
                  <div key={q.question_id} className="border rounded-lg p-4">
                    <p className="text-sm font-medium mb-2">{q.question_text}</p>
                    <div>{answerDisplay}</div>
                    {followUpAnswer && (
                      <div className="mt-2 pl-4 border-l-2 border-gray-200">
                        <p className="text-xs text-muted-foreground mb-1">{q.follow_up}</p>
                        <p className="text-sm">{followUpAnswer}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end pt-3 border-t">
              <Button variant="outline" onClick={() => setScreeningAnswersDialogOpen(false)}>
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={reminderRulesOpen} onOpenChange={setReminderRulesOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Rent reminder rules</DialogTitle>
              <DialogDescription>
                Reminder sends follow the same rules for manual sends and the daily automatic cycle.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 text-sm text-muted-foreground">
              <div>
                <p className="font-medium text-foreground">Upcoming</p>
                <p>Sent when a rent schedule is due in 3 days and the status is still due.</p>
              </div>
              <div>
                <p className="font-medium text-foreground">Due today</p>
                <p>Sent on the due date when the schedule remains marked as due.</p>
              </div>
              <div>
                <p className="font-medium text-foreground">Overdue</p>
                <p>Schedules past the due date move to overdue automatically. Overdue reminders can send at most once every 7 days for the same rent schedule.</p>
              </div>
              <div>
                <p className="font-medium text-foreground">Duplicate protection</p>
                <p>The same reminder type is not sent twice for the same rent schedule on the same day.</p>
              </div>
              <div>
                <p className="font-medium text-foreground">Historical schedules</p>
                <p>Paid schedules are treated as historical records and are not edited.</p>
              </div>
              <div className="flex justify-end pt-2">
                <Button type="button" variant="outline" onClick={() => setReminderRulesOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Create Rent Schedule Dialog */}
        <Dialog open={rentDialogOpen} onOpenChange={setRentDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Rent Schedule</DialogTitle>
              <DialogDescription>
                Set up a recurring rent schedule for a tenant.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateRent} className="space-y-4">
              <div>
                <Label>Property</Label>
                <Select
                  value={rentForm.property_id}
                  onValueChange={(val) => {
                    setRentForm((f) => ({ ...f, property_id: val, tenancy_id: '', tenant_id: '', manual_tenancy_id: '', rent_amount: getDefaultRentForProperty(val) }));
                    fetchTenants(val);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select property" />
                  </SelectTrigger>
                  <SelectContent>
                    {properties.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tenant</Label>
                {tenants.length > 0 ? (
                  <Select
                    value={rentForm.manual_tenancy_id ? `${MANUAL_TENANT_PREFIX}${rentForm.manual_tenancy_id}` : rentForm.tenant_id}
                    onValueChange={(val) => {
                      const selected = tenants.find((tenant) => getTenantOptionValue(tenant) === val);
                      const manualTenancyId = getManualTenancyIdFromValue(val);
                      setRentForm((f) => ({
                        ...f,
                        tenancy_id: selected?.tenancy_id || selected?.manual_tenancy_id || manualTenancyId || '',
                        tenant_id: isManualTenantValue(val) ? '' : val,
                        manual_tenancy_id: manualTenancyId,
                      }));
                    }}
                    disabled={!rentForm.property_id}
                  >
                    <SelectTrigger><SelectValue placeholder="Select tenant" /></SelectTrigger>
                    <SelectContent>
                      {tenants.map((t) => (
                        <SelectItem key={getTenantOptionValue(t)} value={getTenantOptionValue(t)}>
                          {(t.name || t.email)}{t.is_manual ? ' (Manual)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-sm text-muted-foreground py-2">
                    No tenants found. <button type="button" className="underline text-foreground" onClick={() => { setRentDialogOpen(false); handleTabChange('tenants'); }}>Manage tenants first.</button>
                  </p>
                )}
              </div>
              <div>
                <Label>Rent Amount (£)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={rentForm.rent_amount}
                  onChange={(e) => setRentForm((f) => ({ ...f, rent_amount: e.target.value }))}
                  placeholder="e.g. 1200"
                  required
                />
              </div>
              <div>
                <Label>Due Day of Month (1–28)</Label>
                <Input
                  type="number"
                  min="1"
                  max="28"
                  value={rentForm.due_day_of_month}
                  onChange={(e) => setRentForm((f) => ({ ...f, due_day_of_month: e.target.value }))}
                  required
                />
              </div>
              <div>
                <Label>Payment Method *</Label>
                <Select value={rentForm.payment_method} onValueChange={(val) => setRentForm((f) => ({ ...f, payment_method: val }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="standing_order">Standing Order</SelectItem>
                    <SelectItem value="direct_debit">Direct Debit</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Textarea
                  value={rentForm.notes}
                  onChange={(e) => setRentForm((f) => ({ ...f, notes: e.target.value }))}
                  rows={2}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setRentDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting || !rentForm.tenancy_id || (!rentForm.tenant_id && !rentForm.manual_tenancy_id) || !rentForm.rent_amount}>
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                  Create Schedule
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Rent Schedule Dialog */}
        <Dialog open={editRentDialogOpen} onOpenChange={(open) => {
          setEditRentDialogOpen(open);
          if (!open) {
            setEditingRent(null);
            setEditRentForm({
              rent_amount: '',
              due_day_of_month: '',
              paid_date_preset: '',
              payment_method: 'bank_transfer',
              notes: ''
            });
          }
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Rent Schedule</DialogTitle>
              <DialogDescription>{editingRent?.property_title || editingRent?.property_id} - {editingRent?.tenant_name || 'Tenant'}</DialogDescription>
            </DialogHeader>

            {editingRent?.status === 'partially_paid' && (
              <p className="text-xs text-amber-600 -mt-2">
                A payment has already been recorded for this schedule, so the rent amount, due day, and payment method are locked. You can still adjust the next installment amount, its planned payment date, and notes.
              </p>
            )}

            <div className="space-y-4">
              <div>
                <Label>Rent Amount (£)</Label>
                <Input
                  type="number"
                  min="0"
                  max={editingRent?.status === 'partially_paid' ? editingRent.rent_amount : undefined}
                  step="0.01"
                  value={editRentForm.rent_amount}
                  onChange={(e) => setEditRentForm((f) => ({ ...f, rent_amount: e.target.value }))}
                  placeholder="e.g. 1200"
                  required
                  data-testid="edit-rent-amount"
                />
                {editingRent && parseFloat(editRentForm.rent_amount) > 0 && parseFloat(editRentForm.rent_amount) < parseFloat(editingRent.rent_amount) ? (
                  <p className="text-xs text-amber-600 mt-1">
                    This is lower than the current rent of £{parseFloat(editingRent.rent_amount).toFixed(2)}, so it will be recorded as a partial payment for this cycle when marked paid — the rent amount itself won't change, and the receipt preview will reflect this partial amount.
                  </p>
                ) : editingRent?.status === 'partially_paid' ? (
                  <p className="text-xs text-muted-foreground mt-1">This sets the amount for the next "Record Payment" action on this schedule only (not future rent schedules). It cannot exceed the current rent of £{parseFloat(editingRent.rent_amount).toFixed(2)}.</p>
                ) : (
                  <p className="text-xs text-muted-foreground mt-1">Lowering this below the current rent amount records it as a partial payment for this cycle instead of changing the rent itself.</p>
                )}
              </div>
              <div>
                <Label>Due Day of Month (1–28)</Label>
                <Input
                  type="number"
                  min="1"
                  max="28"
                  value={editRentForm.due_day_of_month}
                  onChange={(e) => setEditRentForm((f) => ({ ...f, due_day_of_month: e.target.value }))}
                  required
                  disabled={editingRent?.status === 'partially_paid'}
                  data-testid="edit-rent-due-day"
                />
              </div>
              <div>
                <Label>Payment date (for Mark Paid)</Label>
                <Input
                  type="date"
                  value={editRentForm.paid_date_preset}
                  onChange={(e) => setEditRentForm((f) => ({ ...f, paid_date_preset: e.target.value }))}
                  data-testid="edit-rent-paid-date-preset"
                />
                <p className="text-xs text-muted-foreground mt-1">Pre-set the date that will be used when you click Mark Paid. Leave blank to use today's date.</p>
              </div>
              <div>
                <Label>Payment Method *</Label>
                <Select value={editRentForm.payment_method} onValueChange={(val) => setEditRentForm((f) => ({ ...f, payment_method: val }))} disabled={editingRent?.status === 'partially_paid'}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="standing_order">Standing Order</SelectItem>
                    <SelectItem value="direct_debit">Direct Debit</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Textarea
                  value={editRentForm.notes}
                  onChange={(e) => setEditRentForm((f) => ({ ...f, notes: e.target.value }))}
                  rows={2}
                  data-testid="edit-rent-notes"
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditRentDialogOpen(false)} disabled={editRentSubmitting}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleEditRent}
                  disabled={
                    editRentSubmitting
                    || !editRentForm.rent_amount
                    || !editRentForm.due_day_of_month
                  }
                >
                  {editRentSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                  Save Changes
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Report Issue on Behalf of Tenant Dialog */}
        <Dialog open={landlordIssueDialogOpen} onOpenChange={(open) => { setLandlordIssueDialogOpen(open); if (!open) { setLandlordIssueForm({ property_id: '', tenant_id: '', description: '', category: DEFAULT_ISSUE_CATEGORY, sub_category: '', custom_title: '', priority: 'medium' }); setLandlordIssueTenants([]); } }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Report Issue on Behalf of Tenant</DialogTitle>
              <DialogDescription>Log a maintenance or property issue for one of your tenants.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleLandlordCreateIssue} className="space-y-4">
              <div>
                <Label>Property</Label>
                <Select
                  value={landlordIssueForm.property_id}
                  onValueChange={async (val) => {
                    setLandlordIssueForm(f => ({ ...f, property_id: val, tenant_id: '' }));
                    try {
                      const res = await axios.get(`${API_URL}/api/properties/${val}/tenants`);
                      setLandlordIssueTenants(res.data);
                    } catch {
                      setLandlordIssueTenants([]);
                    }
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Select property" /></SelectTrigger>
                  <SelectContent>
                    {properties.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tenant</Label>
                {landlordIssueTenants.length > 0 ? (
                  <Select value={landlordIssueForm.tenant_id} onValueChange={(val) => setLandlordIssueForm(f => ({ ...f, tenant_id: val }))} disabled={!landlordIssueForm.property_id}>
                    <SelectTrigger><SelectValue placeholder="Select tenant" /></SelectTrigger>
                    <SelectContent>
                      {landlordIssueTenants.map(t => (
                        <SelectItem key={getTenantOptionValue(t)} value={getTenantOptionValue(t)}>{(t.name || t.email)}{t.is_manual ? ' (Manual)' : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-sm text-muted-foreground py-2">
                    No tenants found. <button type="button" className="underline text-foreground" onClick={() => { setLandlordIssueDialogOpen(false); handleTabChange('tenants'); }}>Manage tenants first.</button>
                  </p>
                )}
              </div>
              <div>
                <Label>Category</Label>
                <Select
                  value={landlordIssueForm.category}
                  onValueChange={(val) => updateLandlordIssueDetails({ category: val, sub_category: '', custom_title: '' })}
                >
                  <SelectTrigger>
                    <SelectValue asChild>
                      <div className="flex items-center gap-2">
                        {SelectedLandlordCategoryIcon ? <SelectedLandlordCategoryIcon size={16} /> : null}
                        <span>{landlordIssueForm.category}</span>
                      </div>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {ISSUE_CATEGORY_OPTIONS.map((category) => {
                      const Icon = CATEGORY_ICONS[category];
                      return (
                        <SelectItem key={category} value={category}>
                          <div className="flex items-center gap-2">
                            {Icon ? <Icon size={16} /> : null}
                            <span>{category}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              {landlordIssueSubCategoryOptions.length > 0 && (
                <div>
                  <Label>Subcategory (Optional)</Label>
                  <Select
                    value={landlordIssueForm.sub_category || '__none__'}
                    onValueChange={(val) => updateLandlordIssueDetails({ sub_category: val === '__none__' ? '' : val })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {landlordIssueSubCategoryOptions.map((subCategory) => (
                        <SelectItem key={subCategory} value={subCategory}>{subCategory}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {landlordIssueForm.category === 'Other' && (
                <div>
                  <Label>Custom Title</Label>
                  <Input
                    value={landlordIssueForm.custom_title}
                    onChange={(e) => updateLandlordIssueDetails({ custom_title: e.target.value })}
                    placeholder="Enter a short custom issue title"
                    required
                  />
                </div>
              )}
              <div>
                <Label>Description</Label>
                <Textarea
                  value={landlordIssueForm.description}
                  onChange={(e) => setLandlordIssueForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Describe the issue in detail..."
                  rows={6}
                  className="min-h-[130px]"
                  required
                />
              </div>
              <div>
                <Label>Priority</Label>
                <Select
                  value={landlordIssueForm.priority}
                  onValueChange={(val) => setLandlordIssueForm(f => ({ ...f, priority: val }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setLandlordIssueDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={landlordIssueSubmitting || !landlordIssueForm.tenant_id}>
                  {landlordIssueSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                  Report Issue
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Amend Statement Confirm Dialog */}
        <AlertDialog open={!!amendStatementConfirm} onOpenChange={(open) => { if (!open) setAmendStatementConfirm(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Amend this statement?</AlertDialogTitle>
              <AlertDialogDescription>
                {amendStatementConfirm?.status === 'sent' || amendStatementConfirm?.status === 'active_signing'
                  ? 'This statement has been sent for signing. Amending it will create a new draft and cancel any in-flight signing requests. Signatories will need to sign the new version.'
                  : 'Amending will create a new draft. You will need to regenerate the PDF before it can be signed or shared.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setAmendStatementConfirm(null)}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  const { propertyId, tenancyId, statementId } = amendStatementConfirm;
                  setAmendStatementConfirm(null);
                  navigate(`/tenancy-agreement?propertyId=${propertyId}&tenancyId=${tenancyId}&id=${statementId}&amend=1`);
                }}
              >
                Continue amending
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Edit Statement Confirm Dialog */}
        <AlertDialog open={!!editStatementConfirm} onOpenChange={(open) => { if (!open) setEditStatementConfirm(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Edit this statement?</AlertDialogTitle>
              <AlertDialogDescription>
                A PDF has already been generated. Editing this statement will require you to regenerate the PDF.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setEditStatementConfirm(null)}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  const { propertyId, tenancyId, statementId } = editStatementConfirm;
                  setEditStatementConfirm(null);
                  navigate(`/tenancy-agreement?propertyId=${propertyId}&tenancyId=${tenancyId}&id=${statementId}`);
                }}
              >
                Continue editing
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Forward Receipt Confirm Dialog */}
        <AlertDialog open={Boolean(pendingForwardReceipt)} onOpenChange={(open) => { if (!open) setPendingForwardReceipt(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Resend Receipt?
              </AlertDialogTitle>
              <AlertDialogDescription>
                This will resend the rent receipt email to {pendingForwardReceipt?.tenantName || 'the tenant'} using SIYAK email delivery.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={forwardingReceipt}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmForwardReceipt} disabled={forwardingReceipt}>
                {forwardingReceipt ? <Loader2 className="h-4 w-4 animate-spin mr-1 inline" /> : null}
                Resend Receipt
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Dialog
          open={editTenancyDialogOpen}
          onOpenChange={(open) => {
            if (!open && !editTenancySubmitting) {
              resetEditTenancyState();
              return;
            }
            setEditTenancyDialogOpen(open);
          }}
        >
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{editTenancyType === 'manual' ? 'Edit manual tenancy' : 'Edit tenant assignment'}</DialogTitle>
              <DialogDescription>
                {editTenancyType === 'manual'
                  ? 'Update the manual tenancy participants recorded for this property.'
                  : 'Update the linked tenant, property, start date, or notes for this tenancy record.'}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleEditTenancyRecord} className="space-y-4">
              {editTenancyType === 'assignment' ? (
                <>
                  <div>
                    <Label>Property</Label>
                    <Select value={editTenancyForm.property_id} onValueChange={(val) => setEditTenancyForm((form) => ({ ...form, property_id: val }))}>
                      <SelectTrigger><SelectValue placeholder="Select property" /></SelectTrigger>
                      <SelectContent>
                        {properties.map((property) => (
                          <SelectItem key={property.id} value={property.id}>{property.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Tenant account</Label>
                    <Select value={editTenancyForm.tenant_id} onValueChange={(val) => setEditTenancyForm((form) => ({ ...form, tenant_id: val }))}>
                      <SelectTrigger><SelectValue placeholder="Select tenant" /></SelectTrigger>
                      <SelectContent>
                        {availableEditTenantOptions.map((tenant) => (
                          <SelectItem key={tenant.id} value={tenant.id}>{tenant.name || tenant.email}{tenant.email ? ` · ${tenant.email}` : ''}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-3 md:col-span-2">
                    <Label>Tenancy participants</Label>
                    {editTenancyForm.tenants.map((tenant, index) => (
                      <div key={`edit-manual-tenant-${index}`} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3">
                        <Input
                          value={tenant.name}
                          onChange={(e) => setEditTenancyForm((form) => ({
                            ...form,
                            tenants: form.tenants.map((item, itemIndex) => itemIndex === index ? { ...item, name: e.target.value } : item),
                          }))}
                          placeholder="Full Name (e.g. John Smith)"
                          required
                        />
                        <Input
                          type="email"
                          value={tenant.email}
                          onChange={(e) => setEditTenancyForm((form) => ({
                            ...form,
                            tenants: form.tenants.map((item, itemIndex) => itemIndex === index ? { ...item, email: e.target.value } : item),
                          }))}
                          placeholder="Tenant email"
                          required
                        />
                        <Button
                          type="button"
                          variant="outline"
                          disabled={editTenancyForm.tenants.length === 1}
                          onClick={() => setEditTenancyForm((form) => ({ ...form, tenants: form.tenants.filter((_, itemIndex) => itemIndex !== index) }))}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                    <Button type="button" variant="outline" onClick={() => setEditTenancyForm((form) => ({ ...form, tenants: [...form.tenants, { name: '', email: '' }] }))}>
                      Add another participant
                    </Button>
                  </div>
                </>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Start date</Label>
                  <Input
                    type="date"
                    value={editTenancyForm.start_date}
                    onChange={(e) => setEditTenancyForm((form) => ({ ...form, start_date: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Deposit amount</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editTenancyForm.deposit_amount}
                    disabled={!editTenancyForm.deposit_taken}
                    onChange={(e) => setEditTenancyForm((form) => ({ ...form, deposit_amount: e.target.value }))}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="edit_tenancy_deposit_taken"
                  checked={editTenancyForm.deposit_taken}
                  onCheckedChange={(checked) => setEditTenancyForm((form) => ({ ...form, deposit_taken: Boolean(checked), deposit_amount: checked ? form.deposit_amount : '' }))}
                />
                <Label htmlFor="edit_tenancy_deposit_taken" className="m-0 cursor-pointer">Deposit taken for this tenancy</Label>
              </div>

              <div>
                <Label>Notes</Label>
                <Input
                  value={editTenancyForm.notes}
                  onChange={(e) => setEditTenancyForm((form) => ({ ...form, notes: e.target.value }))}
                  placeholder="Optional notes"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={resetEditTenancyState} disabled={editTenancySubmitting}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={editTenancySubmitting || (editTenancyType === 'assignment'
                    ? !editTenancyForm.property_id || !editTenancyForm.tenant_id
                    : editTenancyForm.tenants.some((tenant) => !tenant.name.trim() || !tenant.email.trim()))}
                >
                  {editTenancySubmitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Edit className="h-4 w-4 mr-2" />}
                  Save changes
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        <AlertDialog
          open={markPaidConfirmOpen}
          onOpenChange={(open) => {
            setMarkPaidConfirmOpen(open);
            if (!open) {
              setPendingMarkPaidRentId(null);
              setMarkPaidDontShowAgainChecked(false);
              setPaymentAmount('');
            }
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm mark as paid?</AlertDialogTitle>
              <AlertDialogDescription>
                This action generates and sends the rent receipt to the tenant using your current account details and cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-4 text-sm text-amber-900">
              <div className="flex items-start gap-3">
                <Receipt className="mt-0.5 h-5 w-5 text-amber-700" />
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium">Receipt details check</p>
                    <Badge variant="outline" className="border-amber-300 bg-white text-amber-900">Recommended</Badge>
                  </div>
                  <p className="mt-1 text-xs text-amber-800">
                    Have you updated your information in Account Settings for this rent receipt?
                  </p>
                  <ul className="mt-3 list-disc space-y-1 pl-4 text-xs text-amber-800">
                    <li>Landlord name or company name, depending on your landlord type</li>
                    <li>Address, contact details, and logo or brand mark</li>
                  </ul>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-amber-300 bg-white text-amber-900 hover:bg-amber-100"
                      onClick={handleOpenReceiptSettings}
                    >
                      Open Account Settings
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                    <p className="text-[11px] text-amber-800">
                      Review now if you need to update the receipt issuer details before sending.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 py-1">
              <Checkbox
                id="mark-paid-dont-show-again"
                checked={markPaidDontShowAgainChecked}
                onCheckedChange={(checked) => setMarkPaidDontShowAgainChecked(Boolean(checked))}
              />
              <Label htmlFor="mark-paid-dont-show-again" className="text-sm font-normal">
                Don't show this again
              </Label>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleMarkPaidDialogConfirm}>Continue</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog
          open={Boolean(pendingConfirmAction)}
          onOpenChange={(open) => {
            if (!open && !confirmActionLoading) {
              setPendingConfirmAction(null);
            }
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{getConfirmDialogContent().title}</AlertDialogTitle>
              <AlertDialogDescription>{getConfirmDialogContent().description}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={confirmActionLoading}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={confirmActionLoading}
                onClick={handleConfirmAction}
              >
                {confirmActionLoading ? 'Processing...' : getConfirmDialogContent().actionLabel}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <StatementVersionsDialog
          open={Boolean(statementVersionsDialogStmt)}
          onOpenChange={(open) => { if (!open) { setStatementVersionsDialogStmt(null); setStatementVersionsDialogEnded(false); } }}
          statement={statementVersionsDialogStmt}
          tenancyEnded={statementVersionsDialogEnded}
          onSent={fetchData}
        />

        <PdfPreviewDialog
          open={Boolean(receiptPreview)}
          onOpenChange={(open) => { if (!open) closeReceiptPreview(); }}
          title={(
            <span className="flex min-w-0 items-center gap-2">
              <FileText className="h-5 w-5 shrink-0 text-royal-600" />
              <span className="truncate">{receiptPreview?.isDraft ? 'Receipt Draft Preview' : 'Receipt Preview'}</span>
            </span>
          )}
          description={(
            <div className="space-y-1">
              <div className="truncate">
                {receiptPreview?.propertyTitle} • {receiptPreview?.tenantName}
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span>Ref: {receiptPreview?.reference}</span>
                {receiptPreview?.paidDate && (
                  <span>{receiptPreview?.isDraft ? 'Preview date' : 'Paid'}: {formatReceiptDate(receiptPreview.paidDate)}</span>
                )}
                {receiptPreview?.isDraft && <span>Generated from this schedule and your current Account Settings</span>}
              </div>
            </div>
          )}
          actions={!receiptPreview?.isDraft && receiptPreview?.fallbackUrl ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-10 w-full sm:w-auto"
              onClick={handleDownloadReceiptPreview}
            >
              <Download className="mr-2 h-4 w-4" /> Download receipt
            </Button>
          ) : null}
          pdfData={receiptPreview?.pdfData}
          sourceKey={receiptPreview?.sourceKey}
          loading={receiptPreviewLoading}
          loadingTitle="Generating PDF preview"
          loadingDescription="This preview is rendered on the server and is not saved or sent."
          errorMessage={receiptPreview?.errorMessage}
          unavailableMessage="Receipt preview unavailable."
          fallbackActionLabel={!receiptPreview?.isDraft && receiptPreview?.fallbackUrl ? 'Download existing receipt' : ''}
          onFallbackAction={!receiptPreview?.isDraft && receiptPreview?.fallbackUrl ? handleDownloadReceiptPreview : undefined}
        />
      </div>
    </Layout>
  );
};
